import { createClient } from '@supabase/supabase-js';
import { buildDeletePayload, buildPausePayload, buildRestorePayload, restoredShapeFor, applicantPriorState, priorStateColumnMissing, stateFilterSpec, MISSING_PRIOR_STATE_HINT, verifyDeletedRow, verifyPausedRow, verifyRestoredRow, isMissingColumnError, isNoRowMatchedError, lifecycleBlockReason, restoreMode, restoreBlockMessage } from './accountLifecycle';
import { latestRemovalByInvoice, removalRefusal, isMissingTableError } from './orderRemovals';
import { applyInProgressByFields, parseItems as parseAdjItems, extrasFromInvoice, totalFormulaConsistent, recomputeTotal, validateProposal, hasAdjustmentChange, sameItems, round2, approvalBlockReason, requestOwnershipProblem, rowBaselineMismatch, rowAlreadyApplied, expectedNewTotal, dedupeOrderRows, orderRowsNewestFirst, statusClass, buildProposedItems, itemsSum as adjItemsSum } from './invoiceAdjustments';
import { rowDiscountPct, samePct, normalizePct, isMissingDiscountColumnError, DISCOUNT_MIGRATION_MSG } from './invoiceDiscount';
import { resolveRepIdentity, isRepCreatedInvoice } from './adminAdjustPure';
import { orderContentDifferences } from './adminInvoicePure';
import { computeCorrection } from './commissionCorrections';
import { preflightCorrectionState } from './commissionPreflight';
import { validateRemittanceInput, remittanceEligibility, remittanceAmount, activeRemittanceFor, REMIT_ADJUST_BLOCK_MSG, remittanceReadFailedMsg, REP_REMIT_COLUMNS } from './repRemittances';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://placeholder.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'placeholder';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

export async function signUp({ email, password, fullName, role, company, phone, ein, address, tier, refCode }) {
  const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: fullName, role, company, phone, ein: ein||"", address: address||"", tier: tier||"dealer", ref_code: refCode||"" } } });
  if (error) throw error; return data;
}

export async function signIn({ email, password }) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error; return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

// Change the currently signed-in user's login email. Supabase sends a
// confirmation link to the NEW email; clicking it completes the change.
// If the project has "Secure email change" enabled, a link also goes to
// the OLD email and BOTH must be confirmed. This does NOT update
// profiles.email — that syncs on next login via getProfile.
export async function changeLoginEmail(newEmail) {
  const trimmed = String(newEmail||"").trim().toLowerCase();
  if (!trimmed) throw new Error("Email cannot be empty.");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) throw new Error("That doesn't look like a valid email address.");
  const { data, error } = await supabase.auth.updateUser({ email: trimmed });
  if (error) throw error;
  return data;
}

// Kick off the "forgot password" flow. Supabase emails a one-time magic link
// to the address; clicking it lands on the portal with a recovery session,
// and the app surfaces a "set new password" form (see handleRecoveryEvent).
// redirectTo must point at the live portal so the link is clickable from
// the user's inbox on any device.
export async function sendPasswordReset(email) {
  const trimmed = String(email||"").trim().toLowerCase();
  if (!trimmed) throw new Error("Enter your account email first.");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) throw new Error("That doesn't look like a valid email address.");
  const redirectTo = (typeof window !== 'undefined' && window.location)
    ? `${window.location.origin}/?reset=1`
    : undefined;
  const { data, error } = await supabase.auth.resetPasswordForEmail(trimmed, redirectTo ? { redirectTo } : undefined);
  if (error) throw error;
  return data;
}

// Called after the user clicks the recovery email link. The session is
// already active (Supabase parses the magic link token on mount), we just
// push a new password.
export async function updatePassword(newPassword) {
  if (!newPassword || newPassword.length < 6) throw new Error("Password must be at least 6 characters.");
  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
  return data;
}

export async function getSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error; return data.session;
}

export async function getUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error; return data.user;
}

export function onAuthStateChange(callback) {
  return supabase.auth.onAuthStateChange((_event, session) => { callback(session); });
}

export async function getProfile(userId) {
  const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();
  if (error) throw error; return data;
}

export async function updateProfile(userId, updates) {
  // First attempt: full update
  const { data, error } = await supabase.from('profiles').update(updates).eq('id', userId).select().single();
  if (!error) return data;
  // Some Supabase deployments don't have rep/terms/credit_limit/discount or the
  // suspend/delete audit columns (status, deletion_reason, etc.) on profiles.
  // Retry without the extension fields so the rest of the profile still persists;
  // approved/rejected alone are enough for the login gate below to block access.
  const {
    rep, terms, credit_limit, discount,
    status, deletion_reason, deleted_at, paused_reason, paused_at, suspended_by,
    ...safeUpdates
  } = updates;
  if(Object.keys(safeUpdates).length === 0) throw error;
  const { data: data2, error: error2 } = await supabase.from('profiles').update(safeUpdates).eq('id', userId).select().single();
  if (!error2) return data2;
  throw error;
}

/** Save customer assignment (rep, approved terms, credit limit, discount) to a dedicated table.
 * Falls back to a shared localStorage cache so same-browser admin/dealer testing still works
 * even when the Supabase customer_settings table is missing. */
const LS_CUSTOMER_SETTINGS = "mpcare_customer_settings_cache";
function readCustSettingsCache() {
  try { return JSON.parse(localStorage.getItem(LS_CUSTOMER_SETTINGS) || "{}"); } catch(e) { return {}; }
}
function writeCustSettingsCache(cache) {
  try { localStorage.setItem(LS_CUSTOMER_SETTINGS, JSON.stringify(cache)); } catch(e) {}
}
export async function saveCustomerAssignment(userId, { rep, terms, creditLimit, discount, refCode }) {
  if(!userId) return null;
  const cache = readCustSettingsCache();
  const prev = cache[userId] || {};
  // Merge so callers can update just one field (e.g. refCode only at signup) without wiping others.
  cache[userId] = {
    ...prev,
    rep: rep !== undefined ? rep : prev.rep,
    terms: terms !== undefined ? terms : prev.terms,
    creditLimit: creditLimit !== undefined ? creditLimit : prev.creditLimit,
    discount: discount !== undefined ? discount : prev.discount,
    refCode: refCode !== undefined ? refCode : prev.refCode,
    updatedAt: new Date().toISOString()
  };
  writeCustSettingsCache(cache);
  // Mirror to Supabase best-effort
  try {
    const payload = { user_id: userId, updated_at: new Date().toISOString() };
    if(rep !== undefined) payload.rep = rep||"";
    if(terms !== undefined) payload.approved_terms = JSON.stringify(terms||[]);
    if(creditLimit !== undefined) payload.credit_limit = creditLimit||0;
    if(discount !== undefined) payload.discount = discount||0;
    if(refCode !== undefined) payload.ref_code = refCode||"";
    const { data, error } = await supabase.from('customer_settings').upsert(payload, { onConflict: 'user_id' }).select().single();
    if(!error) return data;
    // A RETURNED {error} (RLS, missing column, network) used to be dropped here with no log at all,
    // so a discount / rep / credit-limit save that never reached Supabase was invisible.
    console.warn("[saveCustomerAssignment] customer_settings upsert returned an error for user", userId, ":", error.message, "code:", error.code, "details:", error.details, "hint:", error.hint, "fields:", Object.keys(payload).filter(k => k !== "user_id" && k !== "updated_at").join(","), "- localStorage cache still has the data");
    // Retry without ref_code column if it's missing so the other fields still save
    if(refCode !== undefined) {
      const { ref_code, ...safe } = payload;
      const { data: d2, error: e2 } = await supabase.from('customer_settings').upsert(safe, { onConflict: 'user_id' }).select().single();
      if(!e2) return d2;
      console.warn("[saveCustomerAssignment] customer_settings retry without ref_code also failed for user", userId, ":", e2.message, "code:", e2.code);
    }
  } catch(e) { console.warn("[saveCustomerAssignment] Supabase upsert failed (RLS? table missing?):", e?.message||e, "— localStorage cache still has the data"); }
  return null;
}
export async function getCustomerAssignment(userId) {
  if(!userId) return null;
  // Try Supabase first (cross-device truth). maybeSingle so a brand-new
  // user with no row yet returns null instead of a console-noisy 406.
  try {
    const { data, error } = await supabase.from('customer_settings').select('*').eq('user_id', userId).maybeSingle();
    if(error) {
      console.warn("[getCustomerAssignment] Supabase query error (RLS? network?):", error.message, "— falling back to localStorage");
    } else if(data) {
      let terms = [];
      try { terms = typeof data.approved_terms==="string" ? JSON.parse(data.approved_terms) : (data.approved_terms || []); } catch(e) { terms = []; }
      return { rep: data.rep||"", terms, creditLimit: data.credit_limit||0, discount: data.discount||0, refCode: data.ref_code||"" };
    } else {
      console.info("[getCustomerAssignment] No customer_settings row found for user", userId, "— checking localStorage");
    }
  } catch(e) { console.warn("[getCustomerAssignment] Supabase exception:", e?.message||e, "— falling back to localStorage"); }
  // Fallback to localStorage cache (same-browser)
  const cache = readCustSettingsCache();
  const hit = cache[userId];
  if(hit) return { rep: hit.rep||"", terms: hit.terms||[], creditLimit: hit.creditLimit||0, discount: hit.discount||0, refCode: hit.refCode||"" };
  return null;
}
/**
 * STRICT read of ONE dealer account's discount rate (customer_settings.discount),
 * used by the rep invoice builder, where the number becomes money. Unlike
 * getCustomerAssignment / getAllCustomerAssignments it NEVER falls back to the
 * browser's localStorage cache and NEVER turns a failed read into 0%:
 *   { ok:true,  pct, found }  the read worked. found:true is a real answer (a NULL discount, or 0, is a
 *                             genuine 0%). found:false means NO customer_settings row was visible:
 *                             that is UNKNOWN, not 0% (the admin's assignment always writes the row,
 *                             so for a dealer account a missing row means the write failed or RLS hides
 *                             it from this rep). The rep invoice form makes the rep acknowledge it.
 *   { ok:false, error }       the read failed (network, RLS error, a non-numeric value, or a rate
 *                             outside 0..99.99: 100% would make a free invoice that several readers
 *                             treat as "no total").
 * The caller must fail closed on ok:false. Every failure is logged.
 */
export async function getCustomerDiscountStrict(userId) {
  const id = String(userId || "").trim();
  if (!id) return { ok: false, error: "No dealer account id to look the discount up with." };
  try {
    const { data, error } = await supabase.from('customer_settings').select('user_id,discount').eq('user_id', id).maybeSingle();
    if (error) {
      console.warn("[getCustomerDiscountStrict] customer_settings read failed for user", id, ":", error.message, "code:", error.code);
      return { ok: false, error: error.message || "The customer's discount could not be read." };
    }
    if (!data) {
      console.warn("[getCustomerDiscountStrict] NO customer_settings row visible for user", id, "- reported as found:false (UNKNOWN, not a confirmed 0%). Either the admin's assignment never wrote the row, or RLS hides it from this rep. The rep invoice form requires an explicit acknowledgement before it creates an invoice without a discount.");
      return { ok: true, pct: 0, found: false };
    }
    if (data.discount == null || data.discount === "") return { ok: true, pct: 0, found: true };
    const n = Number(data.discount);
    if (!Number.isFinite(n) || n < 0 || n >= 100 || normalizePct(n) >= 100) {   // the last test catches 99.99996, which rounds up to 100
      console.warn("[getCustomerDiscountStrict] customer_settings.discount for user", id, "is not a usable rate (needs 0 up to, but not including, 100; 100% would make a free invoice that several screens would show at full price):", data.discount);
      return { ok: false, error: "The stored discount for this customer is not a valid percentage (" + String(data.discount) + "). Ask admin to correct it." };
    }
    return { ok: true, pct: normalizePct(n), found: true };
  } catch (e) {
    console.warn("[getCustomerDiscountStrict] customer_settings read threw for user", id, ":", e?.message || e);
    return { ok: false, error: e?.message || "The customer's discount could not be read." };
  }
}

/**
 * STRICT read of ONE dealer account's whole customer_settings row for the ADMIN "Create invoice" form
 * (rep text, approved terms, credit limit, discount). Unlike getCustomerAssignment it NEVER falls back to
 * the browser's localStorage cache and NEVER turns a failed read into "no settings":
 *   { ok:true, found, settings:{ rep, terms (raw approved_terms), creditLimit, discount (RAW value or null), refCode } }
 *   { ok:false, error }   the read failed (network, RLS). Every failure is logged.
 * `discount` is returned raw so the caller can refuse an invalid stored rate instead of silently clamping it.
 */
export async function getCustomerSettingsStrict(userId) {
  const id = String(userId || "").trim();
  if (!id) return { ok: false, error: "No dealer account id to read the settings with." };
  try {
    const { data, error } = await supabase.from('customer_settings').select('*').eq('user_id', id).maybeSingle();
    if (error) {
      console.warn("[getCustomerSettingsStrict] customer_settings read failed for user", id, ":", error.message, "code:", error.code);
      return { ok: false, error: error.message || "The account settings could not be read." };
    }
    if (!data) return { ok: true, found: false, settings: { rep: "", terms: null, creditLimit: 0, discount: null, refCode: "" } };
    return { ok: true, found: true, settings: { rep: data.rep || "", terms: data.approved_terms == null ? null : data.approved_terms, creditLimit: Number(data.credit_limit) || 0, discount: data.discount == null ? null : data.discount, refCode: data.ref_code || "" } };
  } catch (e) {
    console.warn("[getCustomerSettingsStrict] customer_settings read threw for user", id, ":", e?.message || e);
    return { ok: false, error: e?.message || "The account settings could not be read." };
  }
}

/** Admin-side bulk fetch so we can merge assignments into the accounts list in one query. */
export async function getAllCustomerAssignments() {
  // Try Supabase first
  try {
    const { data, error } = await supabase.from('customer_settings').select('*');
    if(!error && data) {
      const map = {};
      data.forEach(row=>{
        let terms = [];
        try { terms = typeof row.approved_terms==="string" ? JSON.parse(row.approved_terms) : (row.approved_terms || []); } catch(e) { terms = []; }
        map[row.user_id] = { rep: row.rep||"", terms, creditLimit: row.credit_limit||0, discount: row.discount||0, refCode: row.ref_code||"" };
      });
      return map;
    }
  } catch(e) { /* fall through */ }
  // Fallback to localStorage cache
  return readCustSettingsCache();
}

/** Save signup metadata to profiles table so admin can see phone, EIN, address, company etc.
 * Older Supabase deployments may be missing extension columns (ref_code, rep, tier, ein,
 * approved, rejected, etc.). The fallback chain progressively strips them so at least
 * the core identity (id + full_name + email) lands even on a stale schema.
 *
 * Returns { ok, savedFields, missingFields, lastError } so callers (like the signup
 * flow) can surface a "run migration 0007" warning if the customer's business info
 * silently got dropped. */
export async function saveProfileMetadata(userId, metadata) {
  const sent = { id: userId, ...metadata };
  // 1) Full payload — happy path when migration 0007 has been run.
  const { data, error } = await supabase.from('profiles').upsert(sent, { onConflict: 'id' }).select().single();
  if (!error) return { ok: true, savedFields: Object.keys(metadata), missingFields: [], data };

  // 2) Strip fields frequently missing on older schemas and retry.
  const { ref_code, rep, tier, ein, approved, rejected, status, ...safeMeta } = metadata;
  const stripped1 = Object.keys(metadata).filter(k => !(k in safeMeta));
  if (Object.keys(safeMeta).length > 0) {
    const { data: d2, error: e2 } = await supabase.from('profiles').upsert({ id: userId, ...safeMeta }, { onConflict: 'id' }).select().single();
    if (!e2) {
      console.warn(`[saveProfileMetadata] columns missing (${stripped1.join(', ')}). Saved core fields only. Run migration 0007_profiles_columns.sql to restore the full profile schema.`);
      return { ok: true, savedFields: Object.keys(safeMeta), missingFields: stripped1, data: d2 };
    }
    // 3) Final fallback — minimum viable profile row.
    const minimal = { full_name: safeMeta.full_name, email: safeMeta.email };
    const stripped2 = Object.keys(metadata).filter(k => !(k in minimal));
    const { data: d3, error: e3 } = await supabase.from('profiles').upsert({ id: userId, ...minimal }, { onConflict: 'id' }).select().single();
    if (!e3) {
      console.error(`[saveProfileMetadata] heavy schema drift — saved only full_name + email. Dropped: ${stripped2.join(', ')}. Run migration 0007_profiles_columns.sql.`, e2.message);
      return { ok: true, savedFields: Object.keys(minimal), missingFields: stripped2, data: d3, lastError: e2.message };
    }
    console.error("[saveProfileMetadata] all fallbacks failed:", e3.message);
    return { ok: false, savedFields: [], missingFields: Object.keys(metadata), lastError: e3.message };
  }
  console.warn("[saveProfileMetadata] initial upsert failed, nothing to retry:", error.message);
  return { ok: false, savedFields: [], missingFields: Object.keys(metadata), lastError: error.message };
}

export async function getPendingApprovals() {
  const { data, error } = await supabase.from('profiles').select('id, role, approved, created_at').eq('approved', false);
  if (error) throw error; return data;
}

export async function getAllProfiles() {
  const { data, error } = await supabase.from('profiles').select('*');
  if (error) throw error; return data || [];
}

/** Switch an account's role — used when a user signed up as the wrong
 *  type (e.g. picked "dealer" but meant "sales rep"). Keeps their email
 *  / password / phone / address / history intact so they can sign in
 *  with the same credentials and land in the correct portal.
 *
 *  Only admin can call this — the protect_profile_fields trigger from
 *  migration 0003 blocks role/approved/rejected changes from non-admins.
 *  The admin's JWT passes is_admin() inside the trigger, so the update
 *  goes through. Also clears any prior suspend/delete flags so the
 *  flipped account is immediately usable.
 *
 *  @param {string} userId  auth.uid() of the target user (profiles.id)
 *  @param {"customer"|"rep"} newRole
 *  @param {string} [newTier]  For customers: "dealer" | "distributor" |
 *                             "enterprise". For reps: forced to "rep".
 *  @returns {Promise<object>}  Updated profile row. */
export async function changeAccountRole(userId, newRole, newTier) {
  if (!userId) throw new Error("No user id.");
  if (newRole !== "customer" && newRole !== "rep") throw new Error("Role must be 'customer' or 'rep'.");
  const payload = {
    role: newRole,
    tier: newRole === "rep" ? "rep" : (newTier || "dealer"),
    approved: true,
    rejected: false,
    status: null,
    paused_reason: null,
    paused_at: null,
    deletion_reason: null,
    deleted_at: null,
    suspended_by: null,
  };
  const { data, error } = await supabase.from('profiles').update(payload).eq('id', userId).select().single();
  if (error) throw error;
  return data;
}

export async function approveUser(userId) {
  const { data, error } = await supabase.from('profiles')
    .update({ approved: true, rejected: false, status: 'approved' })
    .eq('id', userId).select().single();
  if (error) throw error; return data;
}

export async function rejectUser(userId) {
  const { data, error } = await supabase.from('profiles')
    .update({ approved: false, rejected: true, status: 'rejected' })
    .eq('id', userId).select().single();
  if (error) throw error; return data;
}

const MISSING_COLS_HINT =
  "The profiles table is missing a lifecycle column (status / deleted_at / deletion_reason / paused_* / suspended_by). " +
  "Run supabase/migrations/0029_profiles_delete_columns.sql in the Supabase SQL editor, then try again. Nothing was changed.";

const CHANGED_MSG = "The account changed while you were working, so nothing was written. Reload the page and check its current state.";

/** Applies a stateFilterSpec to an UPDATE query: null-safe (IS NULL for null values), exact match
 *  on every pinned column, so a row that changed since it was read matches nothing. */
function applyStateFilter(q, spec) {
  let out = q;
  spec.forEach(([col, val]) => { out = val === null ? out.is(col, null) : out.eq(col, val); });
  return out;
}

/** Fresh single-row read used by every lifecycle helper (never trusts cached admin state). */
async function readFreshProfile(fn, userId) {
  const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();
  if (error) {
    console.warn("[" + fn + "] fresh read failed for", userId, error.message);
    throw new Error("Could not load the account: " + error.message);
  }
  return data;
}

/** Fresh single-row read of one profile for callers outside the lifecycle helpers (e.g. the Edit
 *  Account save guard). Throws on any read error; never writes. */
export async function fetchFreshProfile(userId) {
  if (!userId) throw new Error("No user id.");
  return readFreshProfile("fetchFreshProfile", userId);
}

/** Shared body of softDeleteProfile / softPauseProfile. STRICT: no column-stripping fallback,
 *  throws on any error, re-reads the row first and refuses when the fresh row is not allowed,
 *  makes the UPDATE itself conditional on the fresh state (so a concurrent change becomes a
 *  no-op that errors) and verifies the returned row. */
async function strictLifecycleWrite(fn, action, userId, { reason, actorEmail, actorId } = {}) {
  if (!userId) throw new Error("No user id.");
  if (!String(reason || "").trim()) throw new Error("A reason is required.");
  const fresh = await readFreshProfile(fn, userId);
  const blocked = lifecycleBlockReason(fresh, { action, actorId, userId });
  if (blocked) {
    console.warn("[" + fn + "] refused for", userId, blocked);
    throw new Error(blocked);
  }
  const nowIso = new Date().toISOString();
  // Applicant (pending / rejected) delete records the prior state so Restore can put it back.
  // Approved / paused deletes and every pause never write that column unless the fresh row proves
  // it exists (then a leftover value is nulled).
  const priorState = action === "delete" ? applicantPriorState(fresh) : null;
  const hasPriorCol = !priorStateColumnMissing(fresh);
  if (priorState && !hasPriorCol) {
    console.warn("[" + fn + "] refused for", userId, "deleted_prior_state column missing (migration 0030 not run); nothing written");
    throw new Error(MISSING_PRIOR_STATE_HINT);
  }
  const payload = action === "delete"
    ? buildDeletePayload({ reason, actorEmail, nowIso, priorState, clearPriorState: !priorState && hasPriorCol })
    : buildPausePayload({ reason, actorEmail, nowIso });
  // Conditional on the EXACT state we just read (approved, rejected, status and, when the column
  // exists, deleted_prior_state), each NULL-safe: a concurrent change makes this match no row.
  const pinCols = hasPriorCol ? ["approved", "rejected", "status", "deleted_prior_state"] : ["approved", "rejected", "status"];
  const { data, error } = await applyStateFilter(
    supabase.from('profiles').update(payload).eq('id', userId), stateFilterSpec(fresh, pinCols)
  ).select().single();
  if (error) {
    console.warn("[" + fn + "] update failed for", userId, error.message);
    if (isMissingColumnError(error)) throw new Error(Object.prototype.hasOwnProperty.call(payload, "deleted_prior_state") ? MISSING_PRIOR_STATE_HINT : MISSING_COLS_HINT);
    if (isNoRowMatchedError(error)) throw new Error(CHANGED_MSG);
    throw new Error("Could not " + action + " account: " + error.message);
  }
  const problem = action === "delete" ? verifyDeletedRow(data) : verifyPausedRow(data);
  if (problem) {
    console.warn("[" + fn + "] verification failed for", userId, problem);
    throw new Error((action === "delete" ? "Delete" : "Pause") + " was not recorded by the database (" + problem + "). Most likely cause: " + MISSING_COLS_HINT);
  }
  return data;
}

/** STRICT soft-delete of ONE approved, paused, pending or rejected profile row (admin only,
 *  enforced by RLS). Never hard-deletes and never touches auth.users, orders, invoices,
 *  commissions, inventory or assignments. Refuses admin rows and the acting admin's own row.
 *  Pending/rejected applicants record deleted_prior_state (migration 0030). */
export async function softDeleteProfile(userId, opts) {
  return strictLifecycleWrite("softDeleteProfile", "delete", userId, opts);
}

/** STRICT soft-pause of ONE approved profile row. Same guarantees as softDeleteProfile;
 *  a paused account is restorable exactly like a deleted one. */
export async function softPauseProfile(userId, opts) {
  return strictLifecycleWrite("softPauseProfile", "pause", userId, opts);
}

/** STRICT restore of ONE deleted/paused profile row. Re-fetches the row fresh first, refuses a
 *  row that is not deleted/paused (a rejected applicant can never be approved by accident;
 *  the legacy stripped shape is accepted only with proof: `fromSnapshot` or an audit value),
 *  then updates ONLY the lifecycle columns, conditional on that same state, and verifies. */
export async function restoreProfile(userId, { fromSnapshot } = {}) {
  if (!userId) throw new Error("No user id.");
  const fresh = await readFreshProfile("restoreProfile", userId);
  const mode = restoreMode(fresh, { fromSnapshot });
  if (!mode) {
    const msg = restoreBlockMessage(fresh);
    console.warn("[restoreProfile] refused for", userId, msg);
    throw new Error(msg);
  }
  // Restore target comes from the FRESH row: an applicant deleted as pending/rejected goes back to
  // exactly that state and is never approved; everything else is the existing approved restore.
  const payload = buildRestorePayload(fresh);
  const expected = restoredShapeFor(fresh);
  const pinCols = priorStateColumnMissing(fresh) ? ["approved", "rejected", "status"] : ["approved", "rejected", "status", "deleted_prior_state"];
  const { data, error } = await applyStateFilter(
    supabase.from('profiles').update(payload).eq('id', userId), stateFilterSpec(fresh, pinCols)
  ).select().single();
  if (error) {
    console.warn("[restoreProfile] update failed for", userId, error.message);
    // Pick the hint from what the payload really contains: deleted_prior_state present means 0030
    // (or a stale schema cache after 0030); otherwise the original 0029 lifecycle columns.
    if (isMissingColumnError(error)) throw new Error(Object.prototype.hasOwnProperty.call(payload, "deleted_prior_state") ? MISSING_PRIOR_STATE_HINT : MISSING_COLS_HINT);
    if (isNoRowMatchedError(error)) throw new Error("The account changed while you were working, so nothing was written. Reload the page and try again.");
    throw new Error("Could not restore account: " + error.message);
  }
  const problem = verifyRestoredRow(data, expected);
  if (problem) {
    console.warn("[restoreProfile] verification failed for", userId, problem);
    throw new Error("Restore was not recorded by the database (" + problem + ").");
  }
  return data;
}

export async function getOrders(userId, role) {
  let query = supabase.from('orders').select('id, total, status, created_at');
  if (role !== 'admin') query = query.eq('user_id', userId);
  const { data, error } = await query;
  if (error) throw error; return data;
}

export async function createOrder(orderData) {
  const { data, error } = await supabase.from('orders').insert(orderData).select().single();
  if (error) throw error; return data;
}

/** Save a portal order to Supabase — tries full details, falls back to basic fields */
//
// Account discount (migration 0034). A rep invoice for a customer with a discount passes
// `discount` ({pct, amount}) and `subtotal`; the three columns subtotal / discount_pct /
// discount_amount are added to the INSERT payload ONLY then. Every other invoice keeps the exact
// payload it always had, so a database where 0034 has not been run still saves every
// non-discounted invoice. There is deliberately NO retry that strips the new columns: that would
// store a discounted total with no discount metadata. When the INSERT fails because the columns
// do not exist, the failure is logged and reported through the optional `onError({kind,message})`
// callback ("Database update 0034 has not been applied yet"); the function still returns null so
// the caller's existing sync-pending banner and retry loop apply.
export async function savePortalOrder({ orderId, invoiceId, userId, customer, customerEmail, total, status, paymentTerms, rep, items, source, subtotal, discount, onError }) {
  // Commission source-of-truth guard: every order row that hits the database
  // must carry the rep who earns the commission. The dealer portal's checkout
  // already passes rep=profileData.rep, but that value only exists after
  // customer_settings has been hydrated into profileData — on a first-load or
  // slow-network submit it can be empty. Fall back to a direct
  // customer_settings lookup so an order placed by a dealer with an assigned
  // rep never lands with a blank rep field. (Dealers re-assigned to a different
  // rep later stay credited to the original rep, per the product decision on
  // Q3 — rep is stamped ONCE at order write time and not rewritten.)
  let resolvedRep = (rep || "").trim();
  if (!resolvedRep && userId) {
    try {
      const assignment = await getCustomerAssignment(userId);
      if (assignment && assignment.rep) resolvedRep = String(assignment.rep).trim();
    } catch (e) {
      console.warn("[savePortalOrder] customer_settings rep fallback failed:", e?.message || e);
    }
  }

  // Try full insert with all details
  const fullPayload = {
    user_id: userId || null, total, status,
    order_ref: orderId, invoice_ref: invoiceId,
    customer, customer_email: customerEmail,
    payment_terms: paymentTerms, rep: resolvedRep,
    items: JSON.stringify(items || []),
    source: source || "portal_order"
  };
  const discountPct = normalizePct(discount && discount.pct);
  const carriesDiscount = discountPct > 0;
  if (carriesDiscount) {
    const amount = round2(discount.amount);
    const sub = subtotal != null && subtotal !== "" && Number.isFinite(Number(subtotal)) ? round2(subtotal) : round2((Number(total) || 0) + amount);
    fullPayload.subtotal = sub;
    fullPayload.discount_pct = discountPct;
    fullPayload.discount_amount = amount;
  }
  const reportError = (info) => {
    if (typeof onError !== "function") return;
    try { onError(info); } catch (cbErr) { console.warn("[savePortalOrder] onError callback threw:", cbErr?.message || cbErr); }
  };

  // Idempotency guard — return existing row instead of inserting a dupe.
  //
  // Why: the rep portal's sync-retry loop fires savePortalOrder when an
  // earlier attempt didn't return a row (network timeout, lost response,
  // brief RLS hiccup). The underlying INSERT can still have succeeded
  // server-side, so the retry creates a SECOND row with the same
  // invoice_ref. Concrete production case: INV-F290 ended up as 4 rows
  // in portal_orders (ids 70/71/72/73), all identical, because 3 retries
  // fired after the first INSERT already landed. portal_orders has no
  // UNIQUE constraint on invoice_ref (and we can't add one without
  // deleting the existing dupes, which we won't do per CLAUDE.md §0).
  //
  // The guard below pre-checks for an existing row with the same
  // invoice_ref. If one exists, return it AS-IS — do NOT overwrite any
  // of its fields. This preserves admin / customer downstream actions
  // (status changes, shipping fields, paid date, paymentId) without
  // touching them. A retry becomes a no-op return of the original row.
  //
  // Behavior contract: each call still RESOLVES to a row (the inserted
  // one OR the pre-existing one), so the caller sees `supabaseRow !==
  // null` and skips the sync-pending banner. Callers that needed a new
  // row id are unchanged: the returned row's id is just the original
  // INSERT's id, not a brand-new one.
  if (invoiceId) {
    try {
      const { data: existing, error: lookupErr } = await supabase
        .from('portal_orders')
        .select('*')
        .eq('invoice_ref', invoiceId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (lookupErr) {
        console.warn("[savePortalOrder] dedupe lookup for invoice_ref", invoiceId, "failed:", lookupErr.message, "— proceeding to insert");
      } else if (existing) {
        console.info("[savePortalOrder] invoice_ref", invoiceId, "already exists as portal_orders.id", existing.id, "— returning existing row, NOT overwriting");
        return existing;
      }
    } catch (e) {
      console.warn("[savePortalOrder] dedupe check threw for invoice_ref", invoiceId, ":", e?.message || e, "— proceeding to insert");
    }
  }

  // Single attempt against portal_orders — the ONE table every reader (admin
  // Orders tab, dealer Orders, getAllPortalOrders, realtime subscribers) ever
  // looks at. Every failure is logged with the real error, never swallowed:
  // CLAUDE.md §4/§7 — a returned {error} that isn't logged here is exactly
  // the "silent catch" pattern that turned one bad insert into a five-round
  // guessing game during the rep_commission_payments saga.
  //
  // There used to be two more fallback attempts here, into a legacy `orders`
  // table that no migration in this repo creates (undocumented schema,
  // unknown constraints) and that no reader in the app ever consults for a
  // row placed there (getAllPortalOrders only reads `orders` if the
  // portal_orders SELECT itself throws — not merely "doesn't contain this
  // invoice"). Removed: a stub insert of just {user_id,total,status} — no
  // invoice_ref, no customer, no items — could succeed there and return a
  // truthy row, which callers (createInvoice, the RepPortal backfill retry
  // loop) correctly treat as "saved" and stop retrying / stop warning. The
  // invoice then exists nowhere any human or report can find it: not in
  // portal_orders (so admin, the dealer and every other device never see
  // it), and unidentifiable in `orders` (no invoice_ref to match it back
  // to). The rep's own device still shows it fine (the localStorage mirror
  // in RepPortal.js always runs regardless of this function's outcome), so
  // the SAME sale looks present on the rep's phone and permanently absent
  // everywhere else — with nothing in the console explaining why. Returning
  // null on any portal_orders failure keeps the full invoice safe in the
  // rep's local cache and lets the existing, correctly-designed retry loop
  // (RepPortal.js, "Only mark pushed on a VERIFIED non-null return") keep
  // retrying the ONE table that actually matters, with the real reason now
  // visible in the console every time.
  try {
    const { data, error } = await supabase.from('portal_orders').insert(fullPayload).select().single();
    if (!error) return data;
    console.warn("[savePortalOrder] portal_orders INSERT failed for invoice_ref", invoiceId, ":", error.message, "code:", error.code, "details:", error.details, "hint:", error.hint, "payload:", fullPayload);
    if (carriesDiscount && isMissingDiscountColumnError(error)) {
      console.warn("[savePortalOrder] portal_orders has no discount columns: migration 0034_portal_orders_discount.sql has not been run. The discounted invoice", invoiceId, "stays on this device (sync pending) and is retried automatically; it is NOT saved without its discount.");
      reportError({ kind: "discount_columns_missing", message: DISCOUNT_MIGRATION_MSG, error });
    } else {
      reportError({ kind: "insert_failed", message: error.message || "The invoice could not be saved to the database.", error });
    }
  } catch (e) {
    console.warn("[savePortalOrder] portal_orders INSERT threw for invoice_ref", invoiceId, ":", e?.message || e, "payload:", fullPayload);
    reportError({ kind: "insert_threw", message: e?.message || "The invoice could not be saved to the database.", error: e });
  }
  return null;
}

/** Fetch all portal orders (admin) — reads portal_orders only (no fallback
 *  table; see the inline comment in the function body for why that was
 *  removed). Deduplicates by invoice_ref at source so every caller (customer Orders
 *  hydrate, admin Orders tab, rep field-invoice list) sees ONE row per
 *  invoice regardless of historical duplicate INSERTs.
 *
 *  Defensive guard for pre-existing duplicate rows in portal_orders
 *  (concrete case: INV-F290 ids 70/71/72/73 from sync-retry firings
 *  before savePortalOrder gained its idempotency guard above). Reads
 *  every row from Supabase, then collapses by invoice_ref keeping the
 *  most recent by created_at — preserves any admin / customer fields
 *  that landed on the latest row. Does NOT touch the DB; older
 *  duplicate rows remain in storage untouched per CLAUDE.md §0 (no
 *  DELETE on live data without explicit per-row authorization).
 */
export async function getAllPortalOrders() {
  // Shared, unchanged dedupe rule (see dedupeOrderRows in invoiceAdjustments.js):
  // newest created_at wins, first-seen wins on ties. Approve uses the same rule.
  const dedupeByInvoiceRef = dedupeOrderRows;

  // This is what the sync-retry loop's "is this invoice already saved?" check
  // (existingRefs in RepPortal.js) is built from, along with every order list
  // in the app (admin Orders, dealer Orders, the Dashboard, commission math).
  // A silently-swallowed failure here used to fall back to reading the same
  // undocumented, unmigrated legacy `orders` table savePortalOrder no longer
  // writes to — returning stale or unrelated rows with no indication anything
  // was wrong, or an empty list that every screen would show as "no orders"
  // with zero explanation. Removed: the failure is now always logged, and no
  // misleading fallback data is returned.
  try {
    const { data, error } = await supabase.from('portal_orders').select('*').order('created_at', { ascending: false });
    if (!error && data) return dedupeByInvoiceRef(data);
    if (error) console.warn("[getAllPortalOrders] portal_orders SELECT failed:", error.message, "code:", error.code, "details:", error.details);
  } catch (e) {
    console.warn("[getAllPortalOrders] portal_orders SELECT threw:", e?.message || e);
  }
  return [];
}

// ── ADMIN-CREATED INVOICE: strict portal_orders insert ──────────────────────────
// Used ONLY by the admin "Create invoice" feature (adminCreateInvoice.js). savePortalOrder is
// deliberately NOT used for it: that function re-fills a blank rep from the dealer's assignment,
// returns an existing row unchanged without comparing it, and returns null with no reason on a failure.
//
// Contract (never throws):
//   { ok:true,  row, deduped }   the row now exists in portal_orders. deduped:true means an IDENTICAL row
//                                (same invoice_ref, user, customer, email, rep, terms, total, subtotal,
//                                discount, items) was already there, e.g. a retry after a lost response: that
//                                is the same invoice, not a second one. `row` is the STORED row.
//   { ok:false, code, message, uncertain, error }
//        uncertain:true means the INSERT may have reached the database (dropped connection / timeout /
//        gateway error) or may still be running: the caller must tell the admin to check Orders for the
//        invoice number BEFORE trying again, and must not offer another attempt until they confirm.
// Steps: (0) refuse while an earlier insert for the same invoice_ref from THIS page is still in flight;
// (1) after an uncertain earlier result (opts.afterUncertain) wait (15 s when it was a gateway / 5xx / dropped
// connection, opts.afterGateway; 4 s after our own timeout-and-abort), then look the invoice_ref up; a read
// failure stops here (nothing is inserted); an existing row is VERIFIED against the payload (a different row
// with the same number is a hard error, never "success"); (2) ONE insert .select().single(), abortable. No
// column stripping, no fallback table, no second attempt. Every failure is logged with the full error.
//
// KNOWN LIMIT (documented, not hidden): portal_orders has NO unique index on invoice_ref (production already
// holds duplicate invoice_ref rows from older sync retries, which is why dedupeOrderRows exists), so the
// database itself cannot refuse a second row with the same number. The protections are therefore: the
// lookup, the in-flight guard above, aborting the request when it times out, the delayed re-lookup, and the
// window refusing a new attempt until the admin confirms they checked Orders.
const ADMIN_ORDER_LOOKUP_TIMEOUT_MS = 15000;
const ADMIN_ORDER_INSERT_TIMEOUT_MS = 20000;
export const ADMIN_ORDER_RETRY_DELAY_MS = 4000;
// After a gateway / 5xx / dropped-connection result (NOT after our own timeout-and-abort) the upstream request may
// still be running for tens of seconds, so the retry waits this much longer before it looks the number up again.
export const ADMIN_ORDER_RETRY_DELAY_AFTER_GATEWAY_MS = 15000;
// invoice_ref -> true while the underlying INSERT request has not settled (even after the caller gave up waiting).
const inflightAdminInserts = new Map();
const defaultDelay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
function raceTimeout(promise, ms, what, onTimeout) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      try { if (typeof onTimeout === "function") onTimeout(); }
      catch (e) { console.warn("[createAdminPortalOrder] aborting the timed-out request failed:", e?.message || e); }
      const err = new Error(what + " timed out after " + Math.round(ms / 1000) + " seconds");
      err.ownTimeout = true;   // OUR timer fired (and the request was aborted): the retry may use the short wait
      reject(err);
    }, ms);
  });
  return Promise.race([Promise.resolve(promise), timeout]).finally(() => clearTimeout(timer));
}
// Gives a postgrest builder an AbortSignal (supabase-js / postgrest-js 2.x: .abortSignal()). A builder
// without it (an old client, a test double) simply runs without the abort; the in-flight guard still holds.
function abortable(builder) {
  const ctl = typeof AbortController === "function" ? new AbortController() : null;
  let q = builder;
  if (ctl && builder && typeof builder.abortSignal === "function") q = builder.abortSignal(ctl.signal);
  return { q, abort: () => { if (ctl) ctl.abort(); } };
}
const NETWORKISH_RE = /failed to fetch|fetch failed|networkerror|network request failed|network error|load failed|time[\s-]?out|connection|econn|etimedout|abort|socket|offline|gateway|service unavailable|unexpected end of json/i;
const HTMLISH_RE = /<\s*(!doctype|html|head|body|title|h1|center)\b/i;
// Did the insert possibly reach the database although we got an error back? The REAL postgrest-js puts the
// HTTP status on the RESPONSE ({ data, error, status }), not on the error, so the caller passes it in.
function insertErrorIsUncertain(error, status) {
  if (!error) return false;
  const st = typeof status === "number" ? status : (typeof error.status === "number" ? error.status : NaN);
  if (Number.isFinite(st) && (st >= 500 || st === 0)) return true;   // a gateway / server failure, or no response at all
  const code = String(error.code || "");
  if (code) return false;   // a database / PostgREST error code means the server answered: the insert did not happen
  const text = [error.message, error.details].filter(Boolean).join(" ");
  return HTMLISH_RE.test(text) || NETWORKISH_RE.test(text);
}
const uncertainMessage = (ref, detail) => "The connection dropped or timed out while saving. Invoice " + ref + " MAY have been created: open Orders and look for " + ref + " before trying again." + (detail ? " (" + detail + ")" : "");
const shortDetail = (s) => String(s || "network error").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 140) || "network error";
export async function createAdminPortalOrder(payload, opts) {
  const o = opts || {};
  const lookupMs = Number(o.lookupTimeoutMs) > 0 ? Number(o.lookupTimeoutMs) : ADMIN_ORDER_LOOKUP_TIMEOUT_MS;
  const insertMs = Number(o.insertTimeoutMs) > 0 ? Number(o.insertTimeoutMs) : ADMIN_ORDER_INSERT_TIMEOUT_MS;
  const ref = payload && payload.invoice_ref ? String(payload.invoice_ref) : "";
  if (!ref || !payload.user_id || !payload.source || !(Number(payload.total) > 0)) {
    console.warn("[createAdminPortalOrder] refused: the payload is incomplete", { invoice_ref: ref, user_id: payload && payload.user_id, source: payload && payload.source, total: payload && payload.total });
    return { ok: false, code: "invalid", uncertain: false, message: "The invoice could not be saved: the invoice details were incomplete. Nothing was saved." };
  }
  const inFlight = () => {
    console.warn("[createAdminPortalOrder] invoice_ref", ref, "is still being saved by an earlier attempt from this page: refusing to start a second insert");
    return { ok: false, code: "in_flight", uncertain: true, message: "Invoice " + ref + " is still being saved by your earlier attempt. Wait a minute, then open Orders and look for " + ref + " before trying again." };
  };
  if (inflightAdminInserts.has(ref)) return inFlight();
  // 0. After an uncertain result the first request may still be committing: give it time, then look again.
  //    After a gateway / 5xx / dropped connection (o.afterGateway) the original request may still be running on the
  //    server for tens of seconds, so the wait is much longer (15 s) than after our own timeout-and-abort (4 s).
  if (o.afterUncertain) {
    const wait = typeof o.delay === "function" ? o.delay : defaultDelay;
    const ms = o.afterGateway
      ? (o.retryDelayAfterGatewayMs != null ? Number(o.retryDelayAfterGatewayMs) : ADMIN_ORDER_RETRY_DELAY_AFTER_GATEWAY_MS)
      : (o.retryDelayMs != null ? Number(o.retryDelayMs) : ADMIN_ORDER_RETRY_DELAY_MS);
    if (typeof o.onWait === "function") {
      try { o.onWait(ms); }
      catch (e) { console.warn("[createAdminPortalOrder] the onWait callback for", ref, "threw (continuing):", e?.message || e); }
    }
    try { await wait(ms); }
    catch (e) { console.warn("[createAdminPortalOrder] the pre-retry wait for", ref, "failed (continuing to the lookup):", e?.message || e); }
    if (inflightAdminInserts.has(ref)) return inFlight();
  }
  // 1. Is there already a row with this invoice number?
  let existing = null;
  try {
    const look = abortable(supabase.from('portal_orders').select('*').eq('invoice_ref', ref).order('created_at', { ascending: false }).limit(1).maybeSingle());
    const { data, error } = await raceTimeout(look.q, lookupMs, "Checking for an existing invoice " + ref, look.abort);
    if (error) {
      console.warn("[createAdminPortalOrder] lookup of invoice_ref", ref, "FAILED, so nothing was inserted:", error.message, "code:", error.code, "details:", error.details, "hint:", error.hint);
      return { ok: false, code: "lookup_failed", uncertain: false, error, message: "Could not check whether invoice " + ref + " already exists (" + (error.message || "database error") + "). Nothing was saved by this attempt. If an earlier attempt for this invoice was not confirmed, check Orders for " + ref + " first." };
    }
    existing = data || null;
  } catch (e) {
    console.warn("[createAdminPortalOrder] lookup of invoice_ref", ref, "THREW, so nothing was inserted:", e?.message || e);
    return { ok: false, code: "lookup_failed", uncertain: false, error: e, message: "Could not check whether invoice " + ref + " already exists (" + (e?.message || "connection problem") + "). Nothing was saved by this attempt. If an earlier attempt for this invoice was not confirmed, check Orders for " + ref + " first." };
  }
  if (existing) {
    const diffs = orderContentDifferences(existing, payload);
    if (diffs.length === 0) {
      console.info("[createAdminPortalOrder] invoice_ref", ref, "already exists as portal_orders.id", existing.id, "with identical content: treated as the same invoice (idempotent), nothing inserted");
      return { ok: true, row: existing, deduped: true };
    }
    console.warn("[createAdminPortalOrder] invoice_ref", ref, "already exists as portal_orders.id", existing.id, "with DIFFERENT content (" + diffs.join("; ") + "): refusing. existing:", { total: existing.total, customer: existing.customer, rep: existing.rep, user_id: existing.user_id, payment_terms: existing.payment_terms }, "payload:", { total: payload.total, customer: payload.customer, rep: payload.rep, user_id: payload.user_id, payment_terms: payload.payment_terms });
    return { ok: false, code: "duplicate_mismatch", uncertain: false, differences: diffs, message: "An invoice numbered " + ref + " already exists in the database with different contents (" + diffs.join("; ") + "), so nothing was saved. Open Orders and look at " + ref + ": if it is the invoice you meant, it is already created and you are done; if not, void it there and create the invoice again." };
  }
  // The check and the registration below happen in the same tick, so two overlapping calls cannot both pass.
  if (inflightAdminInserts.has(ref)) return inFlight();
  // 2. The one insert.
  const carriesDiscount = payload.discount_pct != null;
  try {
    const ins = abortable(supabase.from('portal_orders').insert(payload).select().single());
    const underlying = Promise.resolve(ins.q);
    inflightAdminInserts.set(ref, true);
    const release = () => { inflightAdminInserts.delete(ref); };
    underlying.then(release, release);
    const { data, error, status } = await raceTimeout(underlying, insertMs, "Saving invoice " + ref, ins.abort);
    if (!error) {
      if (!data || String(data.invoice_ref) !== ref) {
        console.warn("[createAdminPortalOrder] insert returned no usable row for invoice_ref", ref, ":", data);
        return { ok: false, code: "unconfirmed", uncertain: true, message: "The database did not confirm that invoice " + ref + " was saved. It MAY exist: open Orders and look for " + ref + " before trying again." };
      }
      return { ok: true, row: data, deduped: false };
    }
    console.warn("[createAdminPortalOrder] portal_orders INSERT failed for invoice_ref", ref, ":", error.message, "code:", error.code, "details:", error.details, "hint:", error.hint, "http status:", status, "payload:", payload);
    const code = String(error.code || "");
    const text = [error.message, error.details, error.hint].filter(Boolean).join(" ");
    if (code === "42501" || /row-level security|violates row-level|permission denied/i.test(text)) {
      return { ok: false, code: "rls", uncertain: false, error, message: "The database refused to save this invoice (a database permission policy blocked it). Nothing was saved. Check that this admin account is allowed to create orders." };
    }
    if (carriesDiscount && isMissingDiscountColumnError(error)) {
      return { ok: false, code: "migration_0034", uncertain: false, error, message: DISCOUNT_MIGRATION_MSG + ". Nothing was saved. Run migration 0034 in Supabase, then try again." };
    }
    if (insertErrorIsUncertain(error, status)) {
      return { ok: false, code: "network", uncertain: true, error, message: uncertainMessage(ref, shortDetail(error.message)) };
    }
    return { ok: false, code: "insert_failed", uncertain: false, error, message: "The invoice could not be saved: " + (error.message || "database error") + ". Nothing was saved." };
  } catch (e) {
    console.warn("[createAdminPortalOrder] portal_orders INSERT THREW for invoice_ref", ref, ":", e?.message || e, "payload:", payload);
    return { ok: false, code: "network", uncertain: true, ownAbort: !!(e && e.ownTimeout), error: e, message: uncertainMessage(ref, shortDetail(e?.message)) };
  }
}

/** True while an insert for this invoice number, started by this page, has not settled yet. */
export function isAdminInsertInFlight(invoiceId) {
  return inflightAdminInserts.has(String(invoiceId == null ? "" : invoiceId));
}

/**
 * STRICT lookup of one portal_orders row by invoice_ref, straight from the database: no cache, no realtime, no
 * fallback. Used by the admin "Check the database now" button after a save whose outcome was not confirmed, so
 * the answer does not depend on the (possibly out of date) Orders list.
 * Never throws. -> { ok:true, found:false, row:null } | { ok:true, found:true, row } | { ok:false, found:false, row:null, error }
 * An ok:false answer means "could not check", NEVER "not there".
 */
export async function findPortalOrderByRefStrict(invoiceId, opts) {
  const o = opts || {};
  const ms = Number(o.timeoutMs) > 0 ? Number(o.timeoutMs) : ADMIN_ORDER_LOOKUP_TIMEOUT_MS;
  const ref = String(invoiceId == null ? "" : invoiceId).trim();
  if (!ref) {
    console.warn("[findPortalOrderByRefStrict] refused: no invoice number");
    return { ok: false, found: false, row: null, error: "No invoice number to look up." };
  }
  try {
    const look = abortable(supabase.from('portal_orders').select('*').eq('invoice_ref', ref).order('created_at', { ascending: false }).limit(1).maybeSingle());
    const { data, error } = await raceTimeout(look.q, ms, "Looking up invoice " + ref, look.abort);
    if (error) {
      console.warn("[findPortalOrderByRefStrict] lookup of invoice_ref", ref, "FAILED (could not check):", error.message, "code:", error.code, "details:", error.details, "hint:", error.hint);
      return { ok: false, found: false, row: null, error: error.message || "database error" };
    }
    return data ? { ok: true, found: true, row: data } : { ok: true, found: false, row: null };
  } catch (e) {
    console.warn("[findPortalOrderByRefStrict] lookup of invoice_ref", ref, "THREW (could not check):", e?.message || e);
    return { ok: false, found: false, row: null, error: e?.message || "connection problem" };
  }
}


/**
 * Subscribe to realtime INSERT / UPDATE / DELETE events on portal_orders.
 * Enabled by 0011_order_delivery_method.sql — that migration adds the table
 * to the supabase_realtime publication. Without the migration the channel
 * opens but never fires; admin banner still works on on-mount fetch.
 *
 * @param {(payload:{eventType:string,new:object,old:object})=>void} onChange
 * @returns {()=>void} unsubscribe function
 */
export function subscribePortalOrders(onChange) {
  if (typeof onChange !== 'function') return () => {};
  let channel;
  try {
    channel = supabase
      .channel('portal_orders_realtime')
      .on('postgres_changes',
          { event: '*', schema: 'public', table: 'portal_orders' },
          payload => {
            try { onChange(payload); }
            catch (e) { console.warn("[subscribePortalOrders] handler threw:", e?.message || e); }
          })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribePortalOrders] channel status:", status,
            "— realtime unavailable; admin banner will still update on refresh.");
        }
      });
  } catch (e) {
    console.warn("[subscribePortalOrders] setup failed:", e?.message || e);
    return () => {};
  }
  return () => {
    try { if (channel) supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribePortalOrders] teardown:", e?.message || e); }
  };
}

/**
 * Patch shipping fields (tracking, carrier, shippedDate, shippingStatus,
 * deliveredDate, notes) on a portal_orders row. Match key is the invoice
 * ref (INV-#####) or order ref (ORD-####); we try both because the admin
 * may have either one. Used by admin "Add tracking" and "Mark delivered"
 * so the update propagates cross-device — otherwise the customer's own
 * browser (which only has its own localStorage SHARED_INVOICES) would
 * never see the admin's tracking edit.
 *
 * Returns { ok:true } on success, { ok:false, reason } otherwise. Failures
 * are logged but not thrown — the caller has already updated local state
 * so UX is unaffected if the DB write fails; the next mount/poll will
 * reconcile.
 */
export async function updatePortalOrderShipping(ids, fields) {
  const { invoiceId, orderId } = ids || {};
  const payload = {};
  if (fields?.tracking != null)        payload.tracking         = fields.tracking;
  if (fields?.carrier != null)         payload.carrier          = fields.carrier;
  if (fields?.shippedDate != null)     payload.shipped_date     = fields.shippedDate;
  if (fields?.deliveredDate != null)   payload.delivered_date   = fields.deliveredDate;
  if (fields?.shippingStatus != null)  payload.shipping_status  = fields.shippingStatus;
  if (fields?.notes != null)           payload.shipping_notes   = fields.notes;
  // Ship Now modal (0011 migration) — one of 'carrier' / 'rep' / 'notes_only'.
  // deliveredByRep is written only when deliveryMethod === 'rep'.
  if (fields?.deliveryMethod != null)  payload.delivery_method  = fields.deliveryMethod;
  if (fields?.deliveredByRep != null)  payload.delivered_by_rep = fields.deliveredByRep;
  if (Object.keys(payload).length === 0) return { ok:false, reason:"no fields" };

  // Try match by invoice_ref first, then order_ref. Both columns exist on
  // portal_orders per savePortalOrder above.
  const tryUpdate = async (column, value) => {
    if (!value) return null;
    try {
      const { data, error } = await supabase.from('portal_orders').update(payload).eq(column, value).select();
      if (error) return { error };
      return { data };
    } catch (e) { return { error:e }; }
  };

  let res = await tryUpdate('invoice_ref', invoiceId);
  if (!res || res.error || !res.data || res.data.length === 0) {
    res = await tryUpdate('order_ref', orderId);
  }
  if (!res || res.error) {
    const msg = res?.error?.message || "unknown";
    console.warn("updatePortalOrderShipping: DB write failed —", msg);
    return { ok:false, reason: msg };
  }
  if (!res.data || res.data.length === 0) {
    console.warn("updatePortalOrderShipping: no portal_orders row matched", ids);
    return { ok:false, reason: "no row matched" };
  }
  return { ok:true, data: res.data };
}

/**
 * Admin VOID persistence. Surgical, Supabase-first UPDATE of portal_orders:
 * ONLY status / shipping_status / cancel_reason / cancelled_date are written.
 * items, total, user_id, rep, customer, tracking and every other column are
 * never touched. NEVER deletes. Keyed by invoice_ref so every historical
 * duplicate row for the invoice is updated (fallback: order_ref). Guarded with
 * status IS NULL OR status != 'cancelled' (NULL rows included);
 * a 0-row UPDATE is re-selected and only reported ok if every row is cancelled.
 * Formerly guarded with
 * .neq('status','cancelled') so an already-cancelled row is not rewritten.
 *
 * Fresh check first (no stale in-memory state). Returns
 *   { ok:true, rows:N, alreadyCancelled? }  or  { ok:false, reason }.
 * Every failure is console.warn'd with context.
 */
export async function cancelPortalOrder({ invoiceRef, orderRef, reason, note } = {}) {
  if (!invoiceRef && !orderRef) {
    console.warn("[cancelPortalOrder] no invoiceRef/orderRef supplied");
    return { ok:false, reason:"No invoice reference supplied." };
  }
  // cancel_reason is copied verbatim to rep and dealer devices, so it carries
  // ONLY the reason (+ note). Actor attribution lives in the admin-only
  // order_removals audit, never here.
  const cancelReason = (note && String(note).trim())
    ? `${reason} — ${String(note).trim()}`
    : String(reason || "");
  const patch = {
    status:         'cancelled',
    shipping_status:'cancelled',
    cancel_reason:  cancelReason,
    cancelled_date: new Date().toISOString(),
  };
  const attempt = async (column, value) => {
    if (!value) return { skipped:true };
    try {
      // Fresh check of what the DB currently holds for this invoice.
      const { data: existing, error: selErr } = await supabase
        .from('portal_orders').select('id,status').eq(column, value);
      if (selErr) return { error: selErr };
      if (!existing || existing.length === 0) return { none:true };
      if (existing.every(r => r.status === 'cancelled')) return { rows:0, alreadyCancelled:true };
      // NULL-status rows must be included: .neq alone skips them.
      const { data, error } = await supabase
        .from('portal_orders').update(patch).eq(column, value)
        .or('status.is.null,status.neq.cancelled').select('id');
      if (error) return { error };
      const n = (data || []).length;
      if (n > 0) return { rows: n };
      // 0 rows updated without an error: RLS filtering or a concurrent void.
      // Only report success if a re-select proves every row is cancelled now.
      const { data: after, error: afterErr } = await supabase
        .from('portal_orders').select('id,status').eq(column, value);
      if (afterErr) return { error: afterErr };
      if (!after || after.length === 0) return { none:true };
      if (after.every(r => r.status === 'cancelled')) return { rows:0, alreadyCancelled:true };
      return { notApplied:true };
    } catch (e) { return { error: e }; }
  };
  let res = await attempt('invoice_ref', invoiceRef);
  if (res.skipped || res.none) res = await attempt('order_ref', orderRef);
  if (res.error) {
    console.warn("[cancelPortalOrder] DB write failed for", invoiceRef || orderRef, ":", res.error.message || res.error);
    return { ok:false, reason: res.error.message || String(res.error) };
  }
  if (res.notApplied) {
    console.warn("[cancelPortalOrder] UPDATE matched 0 rows and rows are not cancelled (RLS or concurrent change)", { invoiceRef, orderRef });
    return { ok:false, reason:"The database did not accept the void (no rows were changed, possibly a permission or concurrent-change issue). Nothing was changed." };
  }
  if (res.skipped || res.none) {
    // Cache-only invoice that never reached the database: not a failure, but
    // the caller must tell the admin the void is saved on this device only.
    console.warn("[cancelPortalOrder] no portal_orders row exists for", { invoiceRef, orderRef }, "- void will be local to this device.");
    return { ok:true, rows:0, localOnly:true };
  }
  return { ok:true, rows: res.rows, alreadyCancelled: !!res.alreadyCancelled };
}

// ─── Order removals (migration 0027) — admin-only soft hide event log ───────
const ORDER_REMOVALS_CACHE_KEY = 'mpcare_order_removals_cache';
function readOrderRemovalsCache() {
  try {
    const raw = localStorage.getItem(ORDER_REMOVALS_CACHE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    console.warn("[readOrderRemovalsCache] parse failed:", e?.message || e);
    return [];
  }
}
function writeOrderRemovalsCache(rows) {
  try { localStorage.setItem(ORDER_REMOVALS_CACHE_KEY, JSON.stringify(rows || [])); }
  catch (e) { console.warn("[writeOrderRemovalsCache] failed:", e?.message || e); }
}
export function readCachedOrderRemovals() { return readOrderRemovalsCache(); }
/** Mirror the admin's in-memory removal events into the localStorage cache (cache only; the
 *  database stays the source of truth). */
export function writeCachedOrderRemovals(rows) { writeOrderRemovalsCache(rows); }

const ORDER_REMOVALS_MISSING_MSG = "The removal log table does not exist yet. Ask the developer to run migration 0027_order_removals.sql in Supabase, then try again.";

/** Admin-only. Returns { events, missingTable, fromCache }. When the table
 *  does not exist yet (migration 0027 not run) it warns and returns no
 *  removals so nothing is hidden. */
export async function listOrderRemovals() {
  try {
    const { data, error } = await supabase
      .from('order_removals').select('*').order('created_at', { ascending: true });
    if (error) {
      if (isMissingTableError(error)) {
        console.warn("[listOrderRemovals] order_removals table missing (run migration 0027) — treating as no removals.");
        return { events: [], missingTable: true, fromCache: false };
      }
      throw error;
    }
    const rows = data || [];
    writeOrderRemovalsCache(rows);
    return { events: rows, missingTable: false, fromCache: false };
  } catch (e) {
    console.warn("[listOrderRemovals] Supabase SELECT failed:", e?.message || e, "— using localStorage cache.");
    return { events: readOrderRemovalsCache(), missingTable: false, fromCache: true };
  }
}

/** Append one remove/restore event. Fresh-checks current state first and
 *  refuses remove on a non-cancelled or already-removed order, and restore on
 *  a non-removed one. Never updates or deletes anything. */
export async function insertOrderRemoval({ invoiceRef, orderRef, action, reason, actor, snapshot, prevStatus, cachedStatus } = {}) {
  if (!invoiceRef) return { ok:false, reason:"No invoice reference supplied." };
  if (!String(reason || "").trim()) return { ok:false, reason:"A reason is required." };
  try {
    const { data: evs, error: evErr } = await supabase
      .from('order_removals').select('*').eq('invoice_ref', invoiceRef).order('created_at', { ascending:false }).limit(50);
    if (evErr) {
      if (isMissingTableError(evErr)) {
        console.warn("[insertOrderRemoval] order_removals table missing (run migration 0027).");
        return { ok:false, missingTable:true, reason: ORDER_REMOVALS_MISSING_MSG };
      }
      console.warn("[insertOrderRemoval] fresh state check failed:", evErr.message);
      return { ok:false, reason: evErr.message };
    }
    const current = latestRemovalByInvoice(evs || [])[invoiceRef] || null;
    // Fresh invoice status from the DB; fall back to the cache only when the
    // invoice has no portal_orders row at all.
    let invoiceStatus = cachedStatus;
    const { data: rows, error: rowErr } = await supabase
      .from('portal_orders').select('id,status').eq('invoice_ref', invoiceRef);
    if (rowErr) {
      console.warn("[insertOrderRemoval] portal_orders status check failed:", rowErr.message);
      return { ok:false, reason: rowErr.message };
    }
    if (rows && rows.length > 0) invoiceStatus = rows.some(r => r.status === 'cancelled') ? 'cancelled' : rows[0].status;
    const refusal = removalRefusal(action, current, invoiceStatus);
    if (refusal) return { ok:false, reason: refusal };

    let actorId = null;
    try { const { data: s } = await supabase.auth.getSession(); actorId = s?.session?.user?.id || null; }
    catch (e) { console.warn("[insertOrderRemoval] getSession failed:", e?.message || e); }
    const row = {
      invoice_ref: invoiceRef,
      order_ref: orderRef || null,
      action,
      reason: String(reason).trim(),
      actor: actor || null,
      actor_id: actorId,
      invoice_snapshot: snapshot || null,
      prev_status: prevStatus || invoiceStatus || null,
      client_id: `orm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`,
    };
    const { data: inserted, error } = await supabase.from('order_removals').insert(row).select().single();
    if (error) {
      console.warn("[insertOrderRemoval] INSERT failed:", error.message);
      if (isMissingTableError(error)) return { ok:false, missingTable:true, reason: ORDER_REMOVALS_MISSING_MSG };
      return { ok:false, reason: error.message };
    }
    const cache = readOrderRemovalsCache();
    cache.push(inserted);
    writeOrderRemovalsCache(cache);
    return { ok:true, event: inserted };
  } catch (e) {
    console.warn("[insertOrderRemoval] unexpected error:", e?.message || e);
    return { ok:false, reason: e?.message || "Unexpected error" };
  }
}

/** Realtime on order_removals (RLS: only admins receive events). Per-call
 *  random channel name (PR #85 lesson). Returns an unsubscribe fn. */
export function subscribeOrderRemovals(callback) {
  if (typeof callback !== 'function') throw new Error("subscribeOrderRemovals requires a callback function.");
  const channelName = 'order-removals-live-' + Math.random().toString(36).slice(2, 10);
  let channel;
  try {
    channel = supabase.channel(channelName)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'order_removals' }, payload => {
        try { callback({ event: payload.eventType, row: payload.new || payload.old || null }); }
        catch (e) { console.warn("[subscribeOrderRemovals] callback threw:", e?.message || e); }
      })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribeOrderRemovals] channel status:", status, "— realtime may be unavailable; removals sync on reload.");
        }
      });
  } catch (e) {
    console.warn("[subscribeOrderRemovals] failed to open channel:", e?.message || e);
    return () => {};
  }
  return () => {
    try { supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribeOrderRemovals] removeChannel failed:", e?.message || e); }
  };
}

export async function getInvoices(userId, role) {
  let query = supabase.from('invoices').select('id, total, status');
  if (role !== 'admin') query = query.eq('user_id', userId);
  const { data, error } = await query;
  if (error) throw error; return data;
}

export async function getCommissions(repId) {
  const { data, error } = await supabase.from('commissions').select('id, amount, status').eq('rep_id', repId);
  if (error) throw error; return data;
}

export async function getAdminStats() {
  const [profiles, orders, invoices, commissions] = await Promise.all([supabase.from('profiles').select('id, role, approved, created_at'), supabase.from('orders').select('id, total, status, created_at'), supabase.from('invoices').select('id, total, status'), supabase.from('commissions').select('id, amount, status')]);
  return { totalAccounts: profiles.data?.length || 0, pendingApprovals: profiles.data?.filter(p => !p.approved && p.role === 'customer').length || 0, totalOrders: orders.data?.length || 0, totalRevenue: invoices.data?.filter(i => i.status === 'paid').reduce((s, i) => s + i.total, 0) || 0, outstandingBalance: invoices.data?.filter(i => i.status === 'outstanding').reduce((s, i) => s + i.total, 0) || 0, pendingCommissions: commissions.data?.filter(c => c.status === 'pending').reduce((s, c) => s + c.amount, 0) || 0 };
}

/** Cross-device product + kit catalog. Admin edits persist to the `portal_products`
 * singleton row (id=1) and fall back to a localStorage cache if the table is missing
 * or RLS blocks writes. Customer and rep portals read it on mount so updates made
 * on one browser/device show up everywhere. */
const LS_PORTAL_PRODUCTS = "mpcare_portal_products_cache";
function readPortalProductsCache() {
  try { return JSON.parse(localStorage.getItem(LS_PORTAL_PRODUCTS) || "null"); } catch(e) { return null; }
}
function writePortalProductsCache(data) {
  try { localStorage.setItem(LS_PORTAL_PRODUCTS, JSON.stringify(data)); } catch(e) {}
}
export async function savePortalProducts({ products, kits }) {
  // Partial update by design: only the columns the caller actually passed are
  // written. This is the rule from CLAUDE.md "Live data protection" — a code
  // path that meant to save a product edit must NOT also overwrite the kits
  // column with whatever happened to be in some component's in-memory ref
  // (which can be stale relative to another tab / device / realtime update).
  //
  // Pass `{ products: arr }` to update only the products column.
  // Pass `{ kits: arr }`     to update only the kits column.
  // Pass both to update both (only legitimate use: the explicit "Seed catalog
  // from code" admin action that intentionally rewrites everything).
  const hasProducts = products !== undefined;
  const hasKits     = kits     !== undefined;
  if (!hasProducts && !hasKits) {
    console.warn("[savePortalProducts] called with neither products nor kits — refusing no-op write");
    return null;
  }

  // Update the localStorage cache for whichever fields were provided. Read the
  // existing cache first so a partial call does not wipe the other side from
  // the local cache.
  const prevCache = readPortalProductsCache() || {};
  const cache = {
    products: hasProducts ? (products || []) : (prevCache.products || []),
    kits:     hasKits     ? (kits     || []) : (prevCache.kits     || []),
    updatedAt: new Date().toISOString()
  };
  writePortalProductsCache(cache);

  try {
    // Build the UPDATE payload with ONLY the fields the caller passed. If
    // products is undefined we don't touch the products column; same for kits.
    const payload = { updated_at: new Date().toISOString() };
    if (hasProducts) payload.products = JSON.stringify(products || []);
    if (hasKits)     payload.kits     = JSON.stringify(kits     || []);

    const { data, error } = await supabase.from('portal_products')
      .update(payload).eq('id', 1).select().maybeSingle();
    if (!error && data) return data;
    if (error) console.warn("[savePortalProducts] update failed:", error.message);
    // Row missing — bootstrap insert. For an INSERT we always provide both
    // columns so the row schema is well-formed. The "missing" side gets [] —
    // safe because this branch only fires when no row exists yet.
    const { data: inserted, error: insertErr } = await supabase.from('portal_products')
      .insert({
        id: 1,
        products:   JSON.stringify(hasProducts ? (products || []) : []),
        kits:       JSON.stringify(hasKits     ? (kits     || []) : []),
        updated_at: payload.updated_at,
        settings:   {}
      })
      .select().single();
    if (!insertErr) return inserted;
    console.warn("[savePortalProducts] insert failed:", insertErr?.message);
  } catch(e) {
    console.warn("[savePortalProducts] threw:", e?.message||e);
    /* localStorage cache still has it for next-load fallback */
  }
  return null;
}
export async function getPortalProducts() {
  // Try Supabase first (cross-device truth). maybeSingle so the empty-table
  // case (before the first admin save) returns null instead of a 406.
  try {
    const { data, error } = await supabase.from('portal_products').select('*').eq('id', 1).maybeSingle();
    if (error) {
      console.warn("[getPortalProducts] Supabase error (RLS? network?):", error.message, "— falling back to localStorage cache");
    } else if (data) {
      let products = [], kits = [];
      try { products = typeof data.products==="string" ? JSON.parse(data.products) : (data.products || []); } catch(e) {}
      try { kits = typeof data.kits==="string" ? JSON.parse(data.kits) : (data.kits || []); } catch(e) {}
      const settings = (data.settings && typeof data.settings === 'object') ? data.settings : {};
      return { products, kits, settings, updatedAt: data.updated_at };
    } else {
      console.info("[getPortalProducts] Supabase returned no row (table empty — admin hasn't seeded yet)");
    }
  } catch(e) {
    console.warn("[getPortalProducts] Supabase fetch threw:", e?.message || e, "— falling back to localStorage cache");
  }
  // Fallback to localStorage cache (same browser)
  const cache = readPortalProductsCache();
  if(cache) {
    console.info("[getPortalProducts] Using localStorage cache (", (cache.products||[]).length, "products,", (cache.kits||[]).length, "kits)");
    return { products: cache.products || [], kits: cache.kits || [], updatedAt: cache.updatedAt };
  }
  console.warn("[getPortalProducts] No Supabase data AND no localStorage cache — catalog will be empty until admin seeds");
  return null;
}

/**
 * STRICT read of the live product catalog (portal_products id=1) for the ADMIN "Create invoice" submit
 * check. Unlike getPortalProducts it NEVER falls back to the localStorage cache or to seed data, because
 * the prices it returns are compared against what the admin saw before money is committed. READ ONLY.
 *   { ok:true, products, kits }  |  { ok:false, error }
 */
export async function getPortalProductsStrict() {
  try {
    const { data, error } = await supabase.from('portal_products').select('products,kits').eq('id', 1).maybeSingle();
    if (error) {
      console.warn("[getPortalProductsStrict] portal_products read failed:", error.message, "code:", error.code);
      return { ok: false, error: error.message || "The product catalog could not be read." };
    }
    if (!data) {
      console.warn("[getPortalProductsStrict] portal_products has no row (id=1)");
      return { ok: false, error: "The product catalog has not been saved in the database yet." };
    }
    const parse = (v) => (typeof v === "string" ? JSON.parse(v) : (v || []));
    const products = parse(data.products);
    const kits = parse(data.kits);
    if (!Array.isArray(products) || !Array.isArray(kits)) {
      console.warn("[getPortalProductsStrict] catalog columns are not arrays");
      return { ok: false, error: "The product catalog in the database is not readable." };
    }
    return { ok: true, products, kits };
  } catch (e) {
    console.warn("[getPortalProductsStrict] catalog read threw:", e?.message || e);
    return { ok: false, error: e?.message || "The product catalog could not be read." };
  }
}

/** Real-time subscription on the portal_products singleton (id=1). Fires
 *  `onChange()` whenever any UPDATE/INSERT lands on the row — i.e. whenever
 *  admin saves a price change, adds/removes a product, edits kits, or
 *  updates portal settings. The caller is expected to call getPortalProducts()
 *  inside the callback and push the fresh data into SHARED_PRODUCTS so all
 *  open admin/rep/customer tabs across all devices see the update without
 *  anyone having to refresh.
 *
 *  RLS on portal_products: SELECT is open to everyone (admin, rep, customer,
 *  anon signup visitor). INSERT/UPDATE/DELETE are admin-only. Realtime
 *  events follow SELECT, so every authenticated session receives admin's
 *  changes. No write path here.
 *
 *  Channel name uses a per-call random suffix (same pattern as
 *  subscribeMaterialOrders, subscribeShopVisits, subscribeInventoryRequests,
 *  subscribeRepCommissionPayments) so multiple subscribers across the app
 *  don't collide on a fixed channel name.
 *
 *  @param {() => void} onChange  invoked on each event; takes no args
 *  @returns {() => void}         unsubscribe function
 */
export function subscribePortalProducts(onChange) {
  if (typeof onChange !== 'function') return () => {};
  const channelName = 'portal_products_realtime_' + Math.random().toString(36).slice(2, 10);
  let channel;
  try {
    channel = supabase
      .channel(channelName)
      .on('postgres_changes',
          { event: '*', schema: 'public', table: 'portal_products' },
          () => {
            try { onChange(); }
            catch (e) { console.warn("[subscribePortalProducts] handler threw:", e?.message || e); }
          })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribePortalProducts] channel status:", status,
            "— realtime unavailable; catalog will only refresh on next page load.");
        }
      });
  } catch (e) {
    console.warn("[subscribePortalProducts] setup failed:", e?.message || e);
    return () => {};
  }
  return () => {
    try { if (channel) supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribePortalProducts] teardown:", e?.message || e); }
  };
}

/** Portal-wide content settings (terms, signup copy, tier descriptions).
 * Stored as a JSONB blob on the portal_products singleton row (id=1). Same
 * RLS as products: admin writes, everyone reads — including anonymous
 * signup visitors, which is exactly what we want so the Terms modal can
 * show the admin's latest copy regardless of which browser the signer uses. */
const LS_PORTAL_SETTINGS = "mpcare_portal_settings_cache";
function readPortalSettingsCache() {
  try { return JSON.parse(localStorage.getItem(LS_PORTAL_SETTINGS) || "null"); } catch(e) { return null; }
}
function writePortalSettingsCache(data) {
  try { localStorage.setItem(LS_PORTAL_SETTINGS, JSON.stringify(data)); } catch(e) {}
}
export async function getPortalSettings() {
  try {
    const { data, error } = await supabase.from('portal_products').select('settings').eq('id', 1).maybeSingle();
    if (error) {
      // Column may not exist yet (migration 0006 not run). Fall back to cache + empty.
      if(error.code === '42703' || /column .* does not exist/i.test(error.message||"")) {
        console.warn("[getPortalSettings] portal_products.settings column missing — run migration 0006. Falling back to localStorage cache.");
      } else {
        console.warn("[getPortalSettings] Supabase error:", error.message, "— falling back to localStorage cache");
      }
    } else if (data && data.settings && typeof data.settings === 'object') {
      writePortalSettingsCache(data.settings);
      return data.settings;
    }
  } catch(e) {
    console.warn("[getPortalSettings] threw:", e?.message||e);
  }
  const cache = readPortalSettingsCache();
  return cache || {};
}
export async function savePortalSettings(settings) {
  const clean = (settings && typeof settings === 'object') ? settings : {};
  writePortalSettingsCache(clean);
  try {
    // Partial update — doesn't touch products/kits columns
    const { data, error } = await supabase.from('portal_products')
      .update({ settings: clean, updated_at: new Date().toISOString() })
      .eq('id', 1).select().maybeSingle();
    if (!error && data) return data;
    if (error && (error.code === '42703' || /column .* does not exist/i.test(error.message||""))) {
      console.warn("[savePortalSettings] portal_products.settings column missing — admin must run migration 0006.");
      return null;
    }
    // Row missing — insert one with defaults so admin's first save sticks
    const { data: inserted, error: insertErr } = await supabase.from('portal_products')
      .insert({ id: 1, products: "[]", kits: "[]", settings: clean, updated_at: new Date().toISOString() })
      .select().single();
    if (!insertErr) return inserted;
    console.warn("[savePortalSettings] insert fallback failed:", insertErr?.message);
  } catch(e) {
    console.warn("[savePortalSettings] threw:", e?.message||e);
  }
  return null;
}

export async function setRepCommissionRate(repId, rate) {
  // onConflict: 'rep_id' is REQUIRED — production's rep_commission_rates has a
  // separate UNIQUE constraint on rep_id (not just a primary key), so the
  // default upsert path tries an INSERT and fails on the unique constraint
  // when a row for this rep_id already exists. Telling postgrest "the
  // conflict target is rep_id" turns it into a proper UPDATE for existing
  // rows. Symptom of the missing flag was every commission-rate save after
  // the very first failing with:
  //   "duplicate key value violates unique constraint
  //    'rep_commission_rates_rep_id_key'"
  const { data, error } = await supabase.from('rep_commission_rates')
    .upsert(
      { rep_id: repId, rate, updated_at: new Date().toISOString() },
      { onConflict: 'rep_id' }
    )
    .select().single();
  if (error) throw error; return data;
}

export async function getRepCommissionRates() {
  const { data, error } = await supabase.from('rep_commission_rates').select('rep_id, rate');
  if (error) { console.warn("Failed to load rep commission rates:", error.message); return []; }
  return data || [];
}

// ─── Rep Commission Payments (per-invoice payouts) ─────────────────────────
// Supabase table `rep_commission_payments` stores one row per invoice per
// payout. Replaces the old per-period localStorage flag that caused brand-new
// invoices to inherit paid status from an already-paid period (root cause:
// paid-state was keyed by `${repName}__${periodLabel}` so every future
// invoice in that month counted as paid). With per-invoice rows a new order
// arriving after admin's payout has no matching row → stays pending forever.
//
// Schema (from 0012_rep_commission_payments.sql):
//   invoice_id, rep_id (auth.uid()::text), rep_name, paid_at, method, notes,
//   batch_id (groups one click of Mark Paid), commission_amount,
//   commission_rate, invoice_amount.

/**
 * Like listRepCommissionPayments but REPORTS failure instead of swallowing it
 * (the adjustment-approve guard must not treat a failed / RLS-blocked read as
 * "no commission paid"). Returns { ok: true, ids: Set<string> } or
 * { ok: false, error: string }. listRepCommissionPayments is unchanged.
 */
export async function listCommissionPaidInvoiceIds() {
  // Pages with .range() (PostgREST caps one response at ~1000 rows) until a
  // short page; any page error, or hitting the page safety cap, is ok:false so
  // a truncated set is never mistaken for "no commission paid".
  // Offsets advance by the rows actually received and the exact row count is
  // compared at the end, so a server max-rows cap BELOW the page size (a short
  // first page) is detected instead of being mistaken for the last page.
  const PAGE = 1000;
  const MAX_PAGES = 200;
  try {
    const ids = new Set();
    let offset = 0;
    let total = null;
    for (let page = 0; page < MAX_PAGES; page++) {
      const { data, error, count } = await supabase.from('rep_commission_payments').select('invoice_id', { count: 'exact' })
        .order('id', { ascending: true }).range(offset, offset + PAGE - 1);
      if (error) {
        console.warn("[listCommissionPaidInvoiceIds] SELECT failed on page", page, ":", error.message);
        return { ok: false, error: error.message || "query failed" };
      }
      const list = data || [];
      if (typeof count === 'number') total = count;
      list.forEach(r => { if (r && r.invoice_id) ids.add(String(r.invoice_id)); });
      offset += list.length;
      if (total != null) {
        if (offset >= total) return { ok: true, ids };
        if (list.length === 0) {
          console.warn("[listCommissionPaidInvoiceIds] server returned an empty page before the exact count (" + offset + "/" + total + ")");
          return { ok: false, error: "commission payment rows could not be read completely (" + offset + " of " + total + ")" };
        }
      } else if (list.length < PAGE) {
        return { ok: true, ids };
      }
    }
    console.warn("[listCommissionPaidInvoiceIds] page safety cap reached (" + MAX_PAGES + " pages); refusing to return a possibly truncated set.");
    return { ok: false, error: "too many commission payment rows to check safely (page cap reached)" };
  } catch (e) {
    console.warn("[listCommissionPaidInvoiceIds] threw:", e?.message || e);
    return { ok: false, error: e?.message || String(e) };
  }
}

/**
 * Fetch all commission payment rows. Admin sees everything (RLS is_admin());
 * a rep sees only their own rows via the policy `rep_id = auth.uid()::text`.
 * Returns [] on any error so the caller can fall back to legacy localStorage
 * without breaking the UI.
 */
export async function listRepCommissionPayments() {
  try {
    const { data, error } = await supabase.from('rep_commission_payments')
      .select('*').order('paid_at', { ascending: false });
    if (error) { console.warn("[listRepCommissionPayments]", error.message); return []; }
    return data || [];
  } catch (e) {
    console.warn("[listRepCommissionPayments] threw:", e?.message || e);
    return [];
  }
}

/**
 * Insert one row per invoice for a single payout click. `rows` is an array
 * shaped exactly like the table (snake_case), all sharing the same batch_id.
 * Called by AdminCommissionTab.markPaid and by the legacy-backfill path.
 *
 * Plain INSERT — no DB-side UNIQUE guard on (rep_id, invoice_id). Idempotency
 * comes from the caller's `existingIds` pre-check (see AdminCommissionTab
 * backfill) plus the single-admin usage pattern. Returns { ok, count, error }.
 */
export async function markInvoicesCommissionPaid(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return { ok:false, count:0, error:"no rows" };
  try {
    const { data, error } = await supabase.from('rep_commission_payments').insert(rows).select();
    if (error) {
      console.warn("[markInvoicesCommissionPaid]", error.message);
      return { ok:false, count:0, error: error.message };
    }
    return { ok:true, count: (data||[]).length };
  } catch (e) {
    console.warn("[markInvoicesCommissionPaid] threw:", e?.message || e);
    return { ok:false, count:0, error: e?.message || "threw" };
  }
}

/**
 * Delete every commission payment row that matches a given invoice_id — used
 * by admin "undo this invoice's payout". Deleting the row flips the invoice
 * back to pending (no row = pending). Returns { ok, count, error }.
 */
export async function unmarkInvoiceCommissionPaid(invoiceId) {
  if (!invoiceId) return { ok:false, count:0, error:"no invoiceId" };
  try {
    const { data, error } = await supabase.from('rep_commission_payments')
      .delete().eq('invoice_id', invoiceId).select();
    if (error) {
      console.warn("[unmarkInvoiceCommissionPaid]", error.message);
      return { ok:false, count:0, error: error.message };
    }
    return { ok:true, count: (data||[]).length };
  } catch (e) {
    console.warn("[unmarkInvoiceCommissionPaid] threw:", e?.message || e);
    return { ok:false, count:0, error: e?.message || "threw" };
  }
}

/**
 * Undo an entire payout batch in one shot (admin only). Deletes every row
 * sharing the given batch_id. Used when admin clicks "undo this period
 * payout" — re-opens every invoice in that batch back to pending. Returns
 * { ok, count, error }.
 */
export async function unmarkBatchCommissionPaid(batchId) {
  if (!batchId) return { ok:false, count:0, error:"no batchId" };
  try {
    const { data, error } = await supabase.from('rep_commission_payments')
      .delete().eq('batch_id', batchId).select();
    if (error) {
      console.warn("[unmarkBatchCommissionPaid]", error.message);
      return { ok:false, count:0, error: error.message };
    }
    return { ok:true, count: (data||[]).length };
  } catch (e) {
    console.warn("[unmarkBatchCommissionPaid] threw:", e?.message || e);
    return { ok:false, count:0, error: e?.message || "threw" };
  }
}

/**
 * Realtime subscription on rep_commission_payments. Fires onChange whenever
 * admin INSERTs a payout batch or DELETEs an undo row — lets the rep's
 * commission page update live. Enabled by the realtime publication add in
 * 0012_rep_commission_payments.sql.
 * @param {(payload:{eventType:string,new:object,old:object})=>void} onChange
 * @returns {()=>void} unsubscribe function
 */
export function subscribeRepCommissionPayments(onChange) {
  if (typeof onChange !== 'function') return () => {};
  let channel;
  try {
    // Channel name MUST be unique per call. The Supabase JS client treats
    // a fixed channel name as the same channel across callers; if a second
    // caller mounts after a first one has already invoked .subscribe(), the
    // .on() registration on the second mount throws "cannot add
    // postgres_changes callbacks for realtime:<name> after subscribe()" and
    // that subscriber silently loses realtime updates. This file has three
    // mount sites for this helper (RepPortal, AdminNotificationsTab, the
    // admin reps/commission view) which collide on the admin Sales Reps
    // page. Per-call random suffix matches the pattern used by
    // subscribeToInventoryChanges, subscribeMaterialOrders,
    // subscribeShopVisits, and subscribeInventoryRequests.
    const channelName = 'rep_commission_payments_realtime_' + Math.random().toString(36).slice(2, 10);
    channel = supabase
      .channel(channelName)
      .on('postgres_changes',
          { event: '*', schema: 'public', table: 'rep_commission_payments' },
          payload => {
            try { onChange(payload); }
            catch (e) { console.warn("[subscribeRepCommissionPayments] handler threw:", e?.message || e); }
          })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribeRepCommissionPayments] channel status:", status,
            "— realtime unavailable; commission page will still update on tab focus.");
        }
      });
  } catch (e) {
    console.warn("[subscribeRepCommissionPayments] setup failed:", e?.message || e);
    return () => {};
  }
  return () => {
    try { if (channel) supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribeRepCommissionPayments] teardown:", e?.message || e); }
  };
}

// ─── Rep Inventory Management ───────────────────────────────────────────────
// Two Supabase tables back this feature (see 0004_rep_inventory.sql):
//   • warehouse_stock       — SKU → qty (mutable, admin-managed)
//   • inventory_movements   — append-only ledger of every inventory movement
//
// Everything here mirrors to localStorage so same-browser demo / offline use
// still works if the tables don't exist yet. Once the migration runs, Supabase
// becomes source of truth and localStorage is just a cache.

const LS_WAREHOUSE_STOCK = "mpcare_warehouse_stock_cache";
const LS_INVENTORY_MOVEMENTS = "mpcare_inventory_movements_cache";

function readWarehouseCache() {
  try { return JSON.parse(localStorage.getItem(LS_WAREHOUSE_STOCK) || "{}"); } catch(e) { return {}; }
}
function writeWarehouseCache(cache) {
  try { localStorage.setItem(LS_WAREHOUSE_STOCK, JSON.stringify(cache)); }
  catch(e) {
    logInventoryError("writeWarehouseCache", e, { cache: LS_WAREHOUSE_STOCK, skus: Object.keys(cache || {}).length, note: "localStorage write failed (quota or blocked); Supabase is unaffected" });
  }
}
function readMovementsCache() {
  try { return JSON.parse(localStorage.getItem(LS_INVENTORY_MOVEMENTS) || "[]"); } catch(e) { return []; }
}
// The movements cache is a small OFFLINE mirror of the logged-in rep's own rows, never a
// copy of the ledger: it holds at most the newest MOVEMENTS_CACHE_MAX rows so it cannot
// fill the origin's shared localStorage quota (other mirrors, such as the local copy of
// an invoice whose Supabase write failed, live in the same quota).
const MOVEMENTS_CACHE_MAX = 2000;
function writeMovementsCache(arr) {
  const rows = (Array.isArray(arr) ? arr : []).slice(0, MOVEMENTS_CACHE_MAX);
  try { localStorage.setItem(LS_INVENTORY_MOVEMENTS, JSON.stringify(rows)); }
  catch(e) {
    logInventoryError("writeMovementsCache", e, { cache: LS_INVENTORY_MOVEMENTS, rows: rows.length, note: "localStorage write failed (quota or blocked); retrying with the newest 200 rows. Supabase is unaffected" });
    try { localStorage.setItem(LS_INVENTORY_MOVEMENTS, JSON.stringify(rows.slice(0, 200))); }
    catch(e2) { logInventoryError("writeMovementsCache", e2, { cache: LS_INVENTORY_MOVEMENTS, rows: Math.min(rows.length, 200), note: "reduced write failed too; the offline mirror is not updated" }); }
  }
}

/** Log an inventory error with its full context (CLAUDE.md rule 4: no silent
 *  catches). `err` may be a PostgREST error object or a thrown exception. */
function logInventoryError(fn, err, ctx) {
  console.warn(
    `[${fn}] ${(err && err.message) || err}`,
    { ...(ctx || {}), code: err && err.code, details: err && err.details, hint: err && err.hint },
    err
  );
}

/** Warehouse rows, with the failure reported instead of hidden.
 *  @returns {{ok:boolean, rows:Array, error:string|null, fromCache:boolean}}
 *  ok:true  -> rows are the live Supabase rows (the cache is refreshed from them).
 *  ok:false -> the read failed (logged); rows are the last cached rows, fromCache:true. */
export async function getWarehouseStockChecked() {
  let failure = null;
  try {
    const { data, error } = await supabase.from('warehouse_stock').select('*').order('sku');
    if(!error && data) {
      // Refresh cache with truth from Supabase
      const cache = {};
      data.forEach(r=>{ cache[r.sku] = { sku:r.sku, product_name:r.product_name||"", qty:Number(r.qty)||0, updated_at:r.updated_at }; });
      writeWarehouseCache(cache);
      return { ok:true, rows:Object.values(cache), error:null, fromCache:false };
    }
    failure = error || new Error("warehouse_stock returned no data");
  } catch(e) { failure = e; }
  logInventoryError("getWarehouseStock", failure, { fallback: "localStorage cache" });
  return { ok:false, rows:Object.values(readWarehouseCache()), error:(failure && failure.message) || "Could not load the warehouse stock.", fromCache:true };
}

/** Fetch all warehouse stock rows as an array of {sku, product_name, qty, updated_at}.
 *  Thin wrapper for existing callers (array result); failures are logged. */
export async function getWarehouseStock() {
  return (await getWarehouseStockChecked()).rows;
}

/** The warehouse row for ONE sku read FRESH from Supabase (never the localStorage
 *  cache): { exists, qty } (a SKU with no row has exists:false, qty:0). Throws (and
 *  logs) when the read fails. */
async function readWarehouseRowFresh(fn, sku) {
  let res;
  try { res = await supabase.from('warehouse_stock').select('qty').eq('sku', sku).maybeSingle(); }
  catch(e) { logInventoryError(fn, e, { step: "read warehouse_stock", sku }); throw new Error(`Could not read the warehouse count for ${sku}: ${(e && e.message) || e}`); }
  if(res.error) {
    logInventoryError(fn, res.error, { step: "read warehouse_stock", sku });
    throw new Error(`Could not read the warehouse count for ${sku}: ${res.error.message}`);
  }
  return { exists: !!res.data, qty: res.data ? (Number(res.data.qty) || 0) : 0 };
}

/** Shown to the admin when an assign / return finds no warehouse row for the exact SKU:
 *  the warehouse step is skipped and NO warehouse row is created implicitly. */
export const WAREHOUSE_NO_ROW_NOTE = "Warehouse has no row for this SKU: the warehouse count was not changed.";

/** Set an absolute warehouse qty for a SKU. Upserts in Supabase, THEN mirrors to
 *  the localStorage cache. Throws (and logs) on ANY failure: a cached or local
 *  value is never returned as if it had been saved. */
export async function setWarehouseStock(sku, qty, productName, updatedBy) {
  if(!sku) return null;
  const payload = {
    sku,
    product_name: productName || "",
    qty: Math.max(0, Number(qty)||0),
    updated_at: new Date().toISOString(),
    updated_by: updatedBy || ""
  };
  let res;
  try { res = await supabase.from('warehouse_stock').upsert(payload, { onConflict: 'sku' }).select().single(); }
  catch(e) { logInventoryError("setWarehouseStock", e, { sku, payload }); throw new Error(`Warehouse update failed for ${sku}: ${(e && e.message) || e}`); }
  if(res.error || !res.data) {
    const err = res.error || new Error("no row returned");
    logInventoryError("setWarehouseStock", err, { sku, payload });
    throw new Error(`Warehouse update failed for ${sku}: ${err.message}`);
  }
  const cache = readWarehouseCache();
  cache[sku] = { sku, product_name:res.data.product_name||payload.product_name, qty:Number(res.data.qty)||0, updated_at:res.data.updated_at||payload.updated_at };
  writeWarehouseCache(cache);
  return res.data;
}

/** Insert one movement into the append-only ledger. Never updates; never deletes.
 *  Supabase FIRST: the row is mirrored to the localStorage cache only after the
 *  server confirmed it. Throws (and logs) on ANY failure; a local placeholder row
 *  is never returned as if it had been saved.
 *  @param {object} m
 *  @param {string|null} m.rep_id        Supabase user id or rep name (null for warehouse-only)
 *  @param {string} m.rep_name           display name (snapshot, stable even if rep renamed)
 *  @param {string} m.sku
 *  @param {string} m.product_name
 *  @param {number} m.qty                signed delta (positive into rep/warehouse, negative out)
 *  @param {string} m.movement_type      'assign' | 'return' | 'adjust_rep' | 'warehouse_receive' | 'warehouse_adjust'
 *  @param {string} [m.reason]
 *  @param {string} [m.reference]        e.g. invoice id, PO number
 *  @param {string} [m.created_by]       admin email */
export async function createInventoryMovement(m) {
  if(!m || !m.sku || !m.movement_type) throw new Error("A ledger movement needs a SKU and a movement type.");
  const row = {
    rep_id: m.rep_id || null,
    rep_name: m.rep_name || "",
    sku: m.sku,
    product_name: m.product_name || "",
    qty: Number(m.qty)||0,
    movement_type: m.movement_type,
    reason: m.reason || "",
    reference: m.reference || "",
    created_by: m.created_by || "",
    created_at: new Date().toISOString()
  };
  let res;
  try { res = await supabase.from('inventory_movements').insert(row).select().single(); }
  catch(e) { logInventoryError("createInventoryMovement", e, { row }); throw new Error(`The inventory ledger entry was NOT saved: ${(e && e.message) || e}`); }
  if(res.error || !res.data) {
    const err = res.error || new Error("no row returned");
    logInventoryError("createInventoryMovement", err, { row });
    throw new Error(`The inventory ledger entry was NOT saved: ${err.message}`);
  }
  // No localStorage mirror here: this is an ADMIN write for some rep, and the offline
  // mirror holds only the logged-in rep's own rows (written by that rep's own read).
  return res.data;
}

// Big tables are read in pages: PostgREST caps one response (default 1000 rows, and a
// project can set a LOWER max-rows), and a silent cap made old assign/receive rows
// vanish from the admin totals.
const MOVEMENTS_PAGE_SIZE = 1000;
const MOVEMENTS_MAX_PAGES = 500;

/** Read EVERY row of a query, page by page, and prove it is complete.
 *  `makeQuery()` returns a fresh builder: select('*', { count: 'exact' }), the filters
 *  and a STABLE order (created_at then id). The reader asks the server for the exact
 *  row count on every page, continues by offset using the number of rows it ACTUALLY
 *  received (a server max-rows below the page size cannot truncate the result), and
 *  returns only when the distinct rows collected equal the exact count. A short page is
 *  never assumed to be the last one. Failures are thrown (the caller reports them):
 *   - a query error;
 *   - data === null with no error (never treated as an empty page);
 *   - no exact count reported;
 *   - the page cap;
 *   - a count that does not match what was collected (one automatic retry when the
 *     count changed while reading, e.g. a row was inserted between two pages).
 *  Rows inserted while paging shift the offsets, so rows are de-duplicated by id. */
async function readAllPagesChecked(label, makeQuery) {
  let lastProblem = "";
  for(let attempt = 1; attempt <= 2; attempt++) {
    const rows = [];
    const seen = new Set();
    let offset = 0, expected = null, countChanged = false;
    for(let page = 0; ; page++) {
      if(page >= MOVEMENTS_MAX_PAGES) throw new Error(`${label}: more than ${MOVEMENTS_MAX_PAGES} pages; not all of it could be read.`);
      const { data, error, count } = await makeQuery().range(offset, offset + MOVEMENTS_PAGE_SIZE - 1);
      if(error) throw error;
      if(!Array.isArray(data)) throw new Error(`${label}: the server returned no data for rows ${offset}+ (not treated as an empty page).`);
      if(count == null || !Number.isFinite(Number(count))) throw new Error(`${label}: the server did not report the exact row count, so a complete read cannot be verified.`);
      const n = Number(count);
      if(expected != null && n !== expected) countChanged = true;
      expected = n;
      let added = 0;
      data.forEach(r => {
        if(!r) return;
        if(r.id != null) { const k = String(r.id); if(seen.has(k)) return; seen.add(k); }
        rows.push(r); added++;
      });
      offset += data.length;                                     // rows actually received, not the page size
      if(rows.length >= expected) break;                         // the exact count is reached
      if(data.length === 0 || added === 0) break;                // nothing new: the count cannot be reached
    }
    if(rows.length === expected) return rows;
    lastProblem = `${label}: read ${rows.length} of ${expected} rows${countChanged ? " (the row count changed while reading)" : ""}.`;
    logInventoryError("readAllPagesChecked", new Error(lastProblem), { label, attempt, rowsRead: rows.length, expected, countChanged });
  }
  throw new Error(`${lastProblem} The result is incomplete, so it was not used.`);
}

/** Cached ledger rows of ONE rep that are real (a leftover "local-..." placeholder
 *  written by an older build was never saved on the server and must not be shown as
 *  truth). Only a rep read keeps an offline mirror, so any other read has none. */
function cachedMovementsFor(filters) {
  if(!filters.repId) return [];
  return readMovementsCache().filter(r => r && !String(r.id).startsWith("local-") && r.rep_id === filters.repId);
}

/** Fetch the ledger, ALL pages (newest first), reporting failure instead of hiding it.
 *  Filters: repId, sku, fromDate (ISO), toDate (ISO).
 *  opts.cache === true (the rep portal reading its OWN rows by repId only) keeps an
 *  offline mirror of those rows, capped to the newest MOVEMENTS_CACHE_MAX. No other read
 *  is cached: the admin's unfiltered read of the whole ledger never touches localStorage.
 *  @returns {Promise<{ok:boolean, rows:Array, error:string|null, fromCache:boolean}>}
 *  ok:true  -> every row, verified against the exact server count, fromCache:false.
 *  ok:false -> the read failed (logged with full context); rows are the rep's last
 *              cached rows with fromCache:true for a cached rep read, otherwise [].
 *              Callers keep their last GOOD in-memory ledger rather than adopting them. */
export async function getInventoryMovementsChecked(filters = {}, opts = {}) {
  const useCache = !!(opts && opts.cache === true && filters.repId && !filters.sku && !filters.fromDate && !filters.toDate);
  try {
    const rows = await readAllPagesChecked("inventory_movements", () => {
      let q = supabase.from('inventory_movements').select('*', { count: 'exact' })
        .order('created_at', { ascending: false }).order('id', { ascending: false });
      if(filters.repId)    q = q.eq('rep_id', filters.repId);
      if(filters.sku)      q = q.eq('sku', filters.sku);
      if(filters.fromDate) q = q.gte('created_at', filters.fromDate);
      if(filters.toDate)   q = q.lte('created_at', filters.toDate);
      return q;
    });
    if(useCache) writeMovementsCache(rows.filter(r => r && !String(r.id).startsWith("local-")));
    return { ok:true, rows, error:null, fromCache:false };
  } catch(e) {
    logInventoryError("getInventoryMovements", e, { filters, fallback: useCache ? "localStorage cache (this rep's own rows)" : "none" });
    return { ok:false, rows: useCache ? cachedMovementsFor(filters) : [], error:(e && e.message) || "Could not load the inventory ledger.", fromCache:useCache };
  }
}

/** Fetch ALL portal_orders rows (every page, verified against the exact count), newest
 *  copy per invoice, reporting failure instead of returning an empty list as if it were
 *  the truth. getAllPortalOrders (unpaged, errors become []) is unchanged; this checked
 *  twin exists for writes that must be based on fresh server data (the admin Adjust).
 *  @returns {Promise<{ok:boolean, rows:Array, error:string|null}>} */
export async function getAllPortalOrdersChecked() {
  try {
    const raw = await readAllPagesChecked("portal_orders", () =>
      supabase.from('portal_orders').select('*', { count: 'exact' })
        .order('created_at', { ascending: false }).order('id', { ascending: false }));
    return { ok:true, rows: dedupeOrderRows(raw), error:null };
  } catch(e) {
    logInventoryError("getAllPortalOrdersChecked", e, { note: "no rows returned; callers must not write anything based on it" });
    return { ok:false, rows:[], error:(e && e.message) || "Could not load the orders." };
  }
}

/** Fetch movements (array result, for older callers). Optional filters: repId, sku,
 *  fromDate (ISO), toDate (ISO), limit. Reads every page; failures are logged and an
 *  empty list is returned. Prefer getInventoryMovementsChecked, which tells the caller
 *  whether the rows are live. */
export async function getInventoryMovements(filters = {}) {
  const res = await getInventoryMovementsChecked(filters);
  return filters.limit ? res.rows.slice(0, filters.limit) : res.rows;
}

/** The ledger row is saved but the warehouse counter write that must follow it
 *  failed. The ledger is append-only, so the row stays; the message tells the admin
 *  exactly what happened so the warehouse count can be corrected by hand. */
function ledgerSavedWarehouseFailed(what, sku, err) {
  const e = new Error(
    `${what} was recorded in the inventory ledger, but the warehouse count for ${sku} could NOT be updated ` +
    `(${(err && err.message) || err}). Check the warehouse count and fix it with Receive / adjust.`
  );
  e.ledgerSaved = true;
  return e;
}

/** Convenience: assign N units of SKU to rep.
 *  Writes one 'assign' movement (rep +N) under the EXACT sku given (a variation SKU is
 *  never rewritten to its parent) and decrements the warehouse row of that exact SKU by
 *  N. Order: read the warehouse row FRESH from Supabase, write the ledger row, and only
 *  if that succeeded touch the warehouse. If the warehouse has NO row for this SKU the
 *  warehouse step is skipped (no row is created) and the result says so
 *  (warehouseSkipped, warehouseNote). Returns { movement, warehouse, ... } or throws
 *  (a failed ledger insert never touches the warehouse; a row that exists but holds
 *  fewer than N units still refuses the assignment). */
export async function assignInventoryToRep({ repId, repName, sku, productName, qty, reason, createdBy }) {
  const n = Math.max(0, Math.floor(Number(qty)||0));
  if(n<=0) throw new Error("Qty must be greater than zero.");
  // Check warehouse availability against the live row, not the localStorage cache.
  const wh = await readWarehouseRowFresh("assignInventoryToRep", sku);
  if(wh.exists && wh.qty < n) throw new Error(`Warehouse has only ${wh.qty} unit${wh.qty===1?"":"s"} of ${sku}.`);
  // Ledger FIRST: if this throws nothing else has been touched.
  const movement = await createInventoryMovement({
    rep_id: repId, rep_name: repName, sku, product_name: productName,
    qty: n, movement_type: 'assign', reason: reason||"", created_by: createdBy||""
  });
  if(!wh.exists) return { movement, warehouse: null, warehouseSkipped: true, warehouseNote: WAREHOUSE_NO_ROW_NOTE };
  let warehouse;
  try { warehouse = await setWarehouseStock(sku, wh.qty - n, productName, createdBy); }
  catch(e) { throw ledgerSavedWarehouseFailed(`The assignment of ${n} x ${sku}`, sku, e); }
  return { movement, warehouse };
}

/** Convenience: return N units from rep back to warehouse.
 *  Writes one 'return' movement (rep -N) FIRST, then increments the warehouse row of
 *  that exact SKU by N from a fresh Supabase read. A failed ledger insert never touches
 *  the warehouse. With no warehouse row for the SKU the warehouse step is skipped (no row
 *  is created) and the result says so (warehouseSkipped, warehouseNote). */
export async function returnInventoryFromRep({ repId, repName, sku, productName, qty, reason, createdBy }) {
  const n = Math.max(0, Math.floor(Number(qty)||0));
  if(n<=0) throw new Error("Qty must be greater than zero.");
  const movement = await createInventoryMovement({
    rep_id: repId, rep_name: repName, sku, product_name: productName,
    qty: -n, movement_type: 'return', reason: reason||"", created_by: createdBy||""
  });
  let warehouse;
  try {
    const wh = await readWarehouseRowFresh("returnInventoryFromRep", sku);
    if(!wh.exists) return { movement, warehouse: null, warehouseSkipped: true, warehouseNote: WAREHOUSE_NO_ROW_NOTE };
    warehouse = await setWarehouseStock(sku, wh.qty + n, productName, createdBy);
  } catch(e) { throw ledgerSavedWarehouseFailed(`The return of ${n} x ${sku}`, sku, e); }
  return { movement, warehouse };
}

/** Convenience: manually adjust a rep's on-hand count by a signed delta.
 *  Writes one 'adjust_rep' movement (or the movementType the caller passes, e.g.
 *  'adjust_rep' for both signs from the admin Adjust modal). Warehouse unchanged.
 *  Use when admin has physically counted rep stock and needs to match reality.
 *  Throws (and logs) if the ledger row could not be saved. */
export async function adjustRepInventory({ repId, repName, sku, productName, delta, reason, createdBy, movementType }) {
  const d = Math.trunc(Number(delta)||0);
  if(d===0) return null;
  return await createInventoryMovement({
    rep_id: repId, rep_name: repName, sku, product_name: productName,
    qty: d, movement_type: movementType || 'adjust_rep', reason: reason||"", created_by: createdBy||""
  });
}

/** Convenience: log a warehouse-only receipt or adjustment.
 *  Writes one warehouse_receive / warehouse_adjust movement (rep_id NULL) and
 *  mutates warehouse_stock.qty accordingly. The delta is computed from a FRESH
 *  Supabase read of the warehouse row; the ledger row is written first and the
 *  warehouse is only touched if it succeeded. */
export async function adjustWarehouseStock({ sku, productName, newQty, reason, createdBy }) {
  const current = (await readWarehouseRowFresh("adjustWarehouseStock", sku)).qty;
  const target = Math.max(0, Math.floor(Number(newQty)||0));
  const delta = target - current;
  if(delta===0) return null;
  const movement = await createInventoryMovement({
    rep_id: null, rep_name: "",
    sku, product_name: productName,
    qty: delta,
    movement_type: delta>0 ? 'warehouse_receive' : 'warehouse_adjust',
    reason: reason||"", created_by: createdBy||""
  });
  let warehouse;
  try { warehouse = await setWarehouseStock(sku, target, productName, createdBy); }
  catch(e) { throw ledgerSavedWarehouseFailed(`The warehouse change for ${sku}`, sku, e); }
  return { movement, warehouse };
}

/** @deprecated As of migration 0020 (PR introducing inventory_requests),
 *  reps no longer write directly to inventory_movements. Every rep
 *  self-reported stock receipt now goes through the admin approval queue:
 *  rep calls createInventoryRequest() → admin reviews and Approves (which
 *  writes the rep_receive ledger row server-side) or Rejects (no ledger
 *  row). The inv_mov_rep_insert RLS policy was dropped in 0020, so calls
 *  below would also fail at the database level — the throw below surfaces
 *  the change immediately at the call site rather than letting it silently
 *  fall through to a confusing RLS error.
 *
 *  Kept as an exported function (rather than removed) so any stale import
 *  in legacy code does not break the build; the runtime throw forces the
 *  caller to migrate to createInventoryRequest.
 */
export async function receiveStockAsRep() {
  throw new Error(
    "receiveStockAsRep() is deprecated as of migration 0020. " +
    "Rep self-reported stock receipts now require admin approval — " +
    "use createInventoryRequest() instead. The inv_mov_rep_insert RLS " +
    "policy has been removed; direct rep INSERTs on inventory_movements " +
    "are denied at the database level."
  );
}

// Legacy implementation kept here in commented form for historical context
// only — DO NOT uncomment. The previous direct-write path lived inside
// receiveStockAsRep above and looked roughly like:
//   const row = { rep_id, rep_name, sku, product_name, qty, movement_type: 'rep_receive',
//                 source: 'rep', reason, reference: "", created_by, created_at };
//   await supabase.from('inventory_movements').insert(row).select().single();
// It was replaced by createInventoryRequest() + admin approve which produces
// the same shape of inventory_movements row but only after admin review.

// eslint-disable-next-line no-unused-vars
async function _receiveStockAsRep_legacy_unused({ repId, repName, sku, productName, qty, reason, createdBy }) {
  const n = Math.max(0, Math.floor(Number(qty)||0));
  if(n<=0) throw new Error("Qty must be greater than zero.");
  if(!repId) throw new Error("Rep id is required (must equal auth.uid() for RLS).");
  if(!sku)   throw new Error("SKU is required.");
  const row = {
    rep_id: repId,
    rep_name: repName || "",
    sku,
    product_name: productName || "",
    qty: n,
    movement_type: 'rep_receive',
    source: 'rep',
    reason: reason || "",
    reference: "",
    created_by: createdBy || "",
    created_at: new Date().toISOString()
  };
  // Cache immediately so the rep's own UI reflects the receipt even if the
  // Supabase insert fails (e.g. offline, RLS misconfigured). The cached row gets
  // replaced by the real DB row on success so subsequent reads see the real id.
  const cache = readMovementsCache();
  const cached = { id: `local-${Date.now()}-${Math.random().toString(36).slice(2,8)}`, ...row };
  cache.unshift(cached);
  writeMovementsCache(cache);
  try {
    const { data, error } = await supabase.from('inventory_movements').insert(row).select().single();
    if(error) {
      console.warn("[receiveStockAsRep] Supabase insert failed (RLS policy? migration 0005 not applied?):", error.message, "— localStorage cache still has the row.");
    } else if(data) {
      const idx = cache.findIndex(x=>x.id===cached.id);
      if(idx>=0) { cache[idx] = data; writeMovementsCache(cache); }
      return data;
    }
  } catch(e) {
    console.warn("[receiveStockAsRep] Supabase exception:", e?.message||e, "— localStorage cache still has the row.");
  }
  return cached;
}

/** Real-time subscription to inventory_movements (any INSERT) and warehouse_stock
 *  (any change). Invokes `callback({ source, event, row })` each time Supabase
 *  streams a change. Returns an unsubscribe function.
 *
 *  RLS still filters what the caller receives:
 *    • A rep subscribes → receives INSERTs on inventory_movements where
 *      rep_id = their auth.uid() (matches inv_mov_rep_read policy).
 *    • An admin subscribes → receives everything (matches inv_mov_admin_read).
 *  So the same subscription helper is safe to call from either role.
 *
 *  The caller is expected to re-fetch from getInventoryMovements / getWarehouseStock
 *  inside the callback; we don't pass partial state so on-hand math stays derived
 *  from the same getters the initial render used.
 */
export function subscribeToInventoryChanges(callback) {
  const channelName = 'inv-live-' + Math.random().toString(36).slice(2,10);
  let channel;
  try {
    channel = supabase.channel(channelName)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'inventory_movements' }, payload => {
        try { callback({ source: 'inventory_movements', event: 'INSERT', row: payload.new }); }
        catch(e) { console.warn("[subscribeToInventoryChanges] callback threw on movement:", e?.message||e); }
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'warehouse_stock' }, payload => {
        try { callback({ source: 'warehouse_stock', event: payload.eventType, row: payload.new || payload.old }); }
        catch(e) { console.warn("[subscribeToInventoryChanges] callback threw on warehouse:", e?.message||e); }
      })
      .subscribe(status => {
        if(status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribeToInventoryChanges] channel status:", status, "— realtime may be disabled on this project; admin/rep inventory will still sync on page reload.");
        }
      });
  } catch(e) {
    console.warn("[subscribeToInventoryChanges] failed to open channel:", e?.message||e);
    return () => {};
  }
  return () => {
    try { supabase.removeChannel(channel); }
    catch(e) { console.warn("[subscribeToInventoryChanges] removeChannel failed:", e?.message||e); }
  };
}

// ─── Rep stock-receipt approval queue (inventory_requests) ───────────────────
// Migration 0020 introduces public.inventory_requests, a separate table that
// holds rep self-reported stock receipts in a pending state. Admin reviews,
// optionally edits approved_qty inline, then Approve (which writes ONE
// rep_receive row to inventory_movements via the existing inv_mov_admin_insert
// policy) or Reject (no ledger row).
//
// inventory_movements rows are unchanged in shape — admin approve produces
// the same rep_receive row a rep used to write directly. Rep on-hand math
// is therefore unchanged: SUM(qty WHERE rep_id=me AND type IN
// ('assign','return','adjust_rep','rep_receive')). Pending requests do NOT
// affect on-hand because nothing is in inventory_movements until approve.

const LS_INVENTORY_REQUESTS = "mpcare_inventory_requests_cache";

function readInventoryRequestsCache() {
  try {
    const raw = localStorage.getItem(LS_INVENTORY_REQUESTS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn("[readInventoryRequestsCache] parse failed:", e?.message || e);
    return [];
  }
}

function writeInventoryRequestsCache(rows) {
  try {
    localStorage.setItem(LS_INVENTORY_REQUESTS, JSON.stringify(rows || []));
  } catch (e) {
    console.warn("[writeInventoryRequestsCache] localStorage write failed:", e?.message || e);
  }
}

function genInvReqClientId() {
  // Stable per-call id used as the ON CONFLICT target for idempotent upsert
  // and as the de-dupe key in the localStorage cache. Not user-visible.
  return `inv-req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Rep-facing: create a new pending inventory request. Supabase-first per
 *  Rule Zero — the row only "exists" once Supabase confirms the INSERT.
 *  localStorage is updated alongside as an offline cache.
 *
 *  RLS on inventory_requests enforces: rep_id = auth.uid()::text,
 *  status = 'pending', and all decision-side fields NULL on insert.
 *
 *  @param {object} args
 *  @param {string} args.repId        must equal auth.uid()::text (RLS)
 *  @param {string} [args.repName]
 *  @param {string} args.sku
 *  @param {string} [args.productName]
 *  @param {number} args.qty          requested qty (must be > 0)
 *  @param {string} [args.reason]     rep's note, e.g. "Received Wave 7 shipment"
 *  @returns {Promise<object>} the inserted row from Supabase
 */
export async function createInventoryRequest({ repId, repName, sku, productName, qty, reason }) {
  const n = Math.max(0, Math.floor(Number(qty) || 0));
  if (n <= 0) throw new Error("Qty must be greater than zero.");
  if (!repId) throw new Error("Rep id is required (must equal auth.uid() for RLS).");
  if (!sku)   throw new Error("SKU is required.");
  const payload = {
    rep_id:        repId,
    rep_name:      repName || "",
    sku,
    product_name:  productName || "",
    requested_qty: n,
    reason:        reason || "",
    status:        'pending',
    client_id:     genInvReqClientId(),
  };
  const { data, error } = await supabase
    .from('inventory_requests')
    .insert(payload)
    .select()
    .single();
  if (error) {
    console.warn("[createInventoryRequest] Supabase INSERT failed:", error.message, "payload:", payload);
    throw error;
  }
  // Cache after the truth lands. Cache is offline-fallback only — never
  // authoritative.
  const cache = readInventoryRequestsCache();
  cache.unshift(data);
  writeInventoryRequestsCache(cache);
  return data;
}

/** Rep-facing: cancel one of your own pending requests. RLS enforces:
 *  rep_id = auth.uid(), current status = 'pending'. Returns the updated row.
 */
export async function cancelInventoryRequest(id) {
  if (!id) throw new Error("Request id is required.");
  const { data, error } = await supabase
    .from('inventory_requests')
    .update({ status: 'cancelled' })
    .eq('id', id)
    .select()
    .single();
  if (error) {
    console.warn("[cancelInventoryRequest] Supabase UPDATE failed for id", id, ":", error.message);
    throw error;
  }
  const cache = readInventoryRequestsCache();
  const idx = cache.findIndex(r => r.id === id);
  if (idx >= 0 && data) { cache[idx] = data; writeInventoryRequestsCache(cache); }
  return data;
}

/** Admin-facing: approve a pending request. Sequenced three-step:
 *    1. Re-fetch the request fresh from Supabase (CLAUDE.md hard-rule #3:
 *       never write payloads built from possibly-stale state).
 *    2. UPDATE the request → status='approved', approved_qty, decision
 *       fields. Optimistic-locked on status='pending'.
 *    3. INSERT one rep_receive row into inventory_movements (qty=approved_qty,
 *       source='rep' to preserve the audit fact that this is rep-reported,
 *       reference='req:<id>' for traceability).
 *    4. UPDATE the request → movement_id pointing at the new ledger row.
 *
 *  If step 3 fails, step 2 is rolled back to status='pending' so admin can
 *  retry. If step 4 fails, the ledger row exists and is correct; only the
 *  audit pointer is missing — logged loudly but not rolled back.
 *
 *  Cross-table atomic transactions are not available client-side; this
 *  sequence-and-compensate is the safest shape.
 */
export async function approveInventoryRequest(id, opts = {}) {
  try {
    return await approveInventoryRequestSteps(id, opts);
  } catch (e) {
    // Rule 4: every failure path logs the real error (the step logs below carry the
    // step context; this one covers thrown exceptions and the final rethrown error).
    logInventoryError("approveInventoryRequest", e, { requestId: id, approvedQty: opts && opts.approvedQty });
    throw e;
  }
}

async function approveInventoryRequestSteps(id, { approvedQty, note, adminId, adminEmail } = {}) {
  if (!id) throw new Error("Request id is required.");
  const n = Math.max(0, Math.floor(Number(approvedQty) || 0));
  if (n <= 0) throw new Error("Approved qty must be greater than zero. Use Reject instead.");
  // Step 1 — fresh fetch.
  const { data: current, error: fetchErr } = await supabase
    .from('inventory_requests')
    .select('*')
    .eq('id', id)
    .single();
  if (fetchErr) {
    logInventoryError("approveInventoryRequest", fetchErr, { step: "1 fetch request", requestId: id });
    throw fetchErr;
  }
  if (!current) throw new Error(`Request ${id} not found.`);
  if (current.status !== 'pending') {
    throw new Error(`Request ${id} is already ${current.status}; cannot approve.`);
  }
  // Step 2 — mark approved (optimistic-locked on status='pending').
  const decidedAt = new Date().toISOString();
  const decidedBy = adminEmail || adminId || null;
  const { data: approvedRow, error: u1Err } = await supabase
    .from('inventory_requests')
    .update({
      status:        'approved',
      approved_qty:  n,
      decision_note: note || null,
      decided_by:    decidedBy,
      decided_at:    decidedAt,
    })
    .eq('id', id)
    .eq('status', 'pending')
    .select()
    .single();
  if (u1Err) {
    logInventoryError("approveInventoryRequest", u1Err, { step: "2 mark approved", requestId: id });
    throw u1Err;
  }
  // Step 3 — write the ledger row.
  const movementPayload = {
    rep_id:        current.rep_id,
    rep_name:      current.rep_name || "",
    sku:           current.sku,
    product_name:  current.product_name || "",
    qty:           n,
    movement_type: 'rep_receive',
    source:        'rep', // preserves the audit fact that the rep self-reported the receipt
    reason:        current.reason || "",
    reference:     `req:${id}`,
    created_by:    decidedBy || "",
  };
  const { data: movement, error: insErr } = await supabase
    .from('inventory_movements')
    .insert(movementPayload)
    .select()
    .single();
  if (insErr) {
    logInventoryError("approveInventoryRequest", insErr, { step: "3 insert ledger row", requestId: id, payload: movementPayload });
    // Compensate — flip the request back to pending so admin can retry.
    const { error: revErr } = await supabase
      .from('inventory_requests')
      .update({
        status:        'pending',
        approved_qty:  null,
        decision_note: null,
        decided_by:    null,
        decided_at:    null,
      })
      .eq('id', id);
    if (revErr) {
      logInventoryError("approveInventoryRequest", revErr, {
        step: "3b roll back to pending", requestId: id,
        critical: "request is in 'approved' state but no ledger row exists; admin must re-approve once the underlying ledger error is fixed",
      });
    }
    throw insErr;
  }
  // Step 4 — link movement_id back on the request.
  const { data: finalReq, error: u2Err } = await supabase
    .from('inventory_requests')
    .update({ movement_id: movement.id })
    .eq('id', id)
    .select()
    .single();
  if (u2Err) {
    logInventoryError("approveInventoryRequest", u2Err, {
      step: "4 link movement_id", requestId: id, ledgerRowId: movement.id,
      note: `ledger row exists with reference 'req:${id}'; the audit pointer can be back-filled from inventory_movements.reference`,
    });
    return { request: approvedRow, movement };
  }
  // Update cache with the final linked row.
  const cache = readInventoryRequestsCache();
  const idx = cache.findIndex(r => r.id === id);
  if (idx >= 0) { cache[idx] = finalReq; writeInventoryRequestsCache(cache); }
  return { request: finalReq, movement };
}

/** Admin-facing: reject a pending request. No ledger row is written.
 *  Optimistic-locked on status='pending' so a concurrent approve doesn't
 *  silently overwrite. Returns the updated row.
 */
export async function rejectInventoryRequest(id, { note, adminId, adminEmail } = {}) {
  if (!id) throw new Error("Request id is required.");
  const { data, error } = await supabase
    .from('inventory_requests')
    .update({
      status:        'rejected',
      decision_note: note || null,
      decided_by:    adminEmail || adminId || null,
      decided_at:    new Date().toISOString(),
    })
    .eq('id', id)
    .eq('status', 'pending')
    .select()
    .single();
  if (error) {
    console.warn("[rejectInventoryRequest] Supabase UPDATE failed for id", id, ":", error.message);
    throw error;
  }
  const cache = readInventoryRequestsCache();
  const idx = cache.findIndex(r => r.id === id);
  if (idx >= 0 && data) { cache[idx] = data; writeInventoryRequestsCache(cache); }
  return data;
}

/** Fetch inventory_requests with optional filters. Supabase-first; on failure
 *  falls back to localStorage cache (read-only). RLS server-side filters the
 *  rows to admin-all or rep-own.
 *
 *  @param {object} [filters]
 *  @param {string} [filters.repId]   filter to a single rep (admin only)
 *  @param {string} [filters.status]  'pending' | 'approved' | 'rejected' | 'cancelled'
 *  @param {number} [filters.limit]
 */
export async function listInventoryRequests({ repId, status, limit } = {}) {
  try {
    let q = supabase
      .from('inventory_requests')
      .select('*')
      .order('created_at', { ascending: false });
    if (repId)  q = q.eq('rep_id', repId);
    if (status) q = q.eq('status', status);
    if (limit)  q = q.limit(limit);
    const { data, error } = await q;
    if (error) {
      console.warn("[listInventoryRequests] Supabase SELECT failed:", error.message, "— falling back to localStorage cache.");
      throw error;
    }
    if (data) {
      writeInventoryRequestsCache(data);
      return data;
    }
  } catch (e) {
    console.warn("[listInventoryRequests] using localStorage fallback:", e?.message || e);
  }
  let rows = readInventoryRequestsCache();
  if (repId)  rows = rows.filter(r => r.rep_id === repId);
  if (status) rows = rows.filter(r => r.status === status);
  if (limit)  rows = rows.slice(0, limit);
  return rows;
}

/** Real-time subscription to inventory_requests (any INSERT/UPDATE). Returns
 *  an unsubscribe function. RLS server-side filters the stream:
 *    • rep subscribes  → receives only their own rep_id rows
 *    • admin subscribes → receives every row
 *  Caller is expected to re-fetch via listInventoryRequests inside the
 *  callback so derived state stays in sync with the same getter that
 *  populated the initial render.
 */
export function subscribeInventoryRequests(callback) {
  if (typeof callback !== 'function') {
    throw new Error("subscribeInventoryRequests requires a callback function.");
  }
  const channelName = 'inv-req-live-' + Math.random().toString(36).slice(2, 10);
  let channel;
  try {
    channel = supabase.channel(channelName)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'inventory_requests' },
        payload => {
          try {
            callback({
              event: payload.eventType,
              row: payload.new || payload.old || null
            });
          } catch (e) {
            console.warn("[subscribeInventoryRequests] callback threw:", e?.message || e);
          }
        })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn(
            "[subscribeInventoryRequests] channel status:", status,
            "— realtime may be disabled; admin/rep will sync on page reload."
          );
        }
      });
  } catch (e) {
    console.warn("[subscribeInventoryRequests] failed to open channel:", e?.message || e);
    return () => {};
  }
  return () => {
    try { supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribeInventoryRequests] removeChannel failed:", e?.message || e); }
  };
}

// ─── Rep invoice void-request approval queue (invoice_void_requests) ─────────
// Migration 0022 introduces public.invoice_void_requests, a separate table
// that holds rep-initiated invoice void requests in a pending state. Admin
// reviews on the Admin Orders tab + Notifications card, then Approve (which
// flips portal_orders.status='cancelled' so existing repMetrics math
// auto-excludes the invoice from commission and from the rep's sold total —
// restoring inventory) or Reject (leaves the invoice exactly as it was).
//
// Same shape as the inventory_requests approval flow from PR #83 + migration
// 0020. Inventory + commission self-correct from the cancellation filter
// already in repMetrics.js — no inventory_movements or rep_commission_payments
// rows are written by this flow.

const LS_INVOICE_VOID_REQUESTS = "mpcare_invoice_void_requests_cache";

function readInvoiceVoidRequestsCache() {
  try {
    const raw = localStorage.getItem(LS_INVOICE_VOID_REQUESTS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn("[readInvoiceVoidRequestsCache] parse failed:", e?.message || e);
    return [];
  }
}

function writeInvoiceVoidRequestsCache(rows) {
  try {
    localStorage.setItem(LS_INVOICE_VOID_REQUESTS, JSON.stringify(rows || []));
  } catch (e) {
    console.warn("[writeInvoiceVoidRequestsCache] localStorage write failed:", e?.message || e);
  }
}

function genVoidReqClientId() {
  return `inv-void-req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Friendly message for a missing invoice_void_requests table (migration 0022 not
// run yet), matched against the same isMissingTableError() used for the sibling
// adjustment-request table (ADJ_MISSING_MSG) — every helper below throws THIS
// instead of the raw PostgREST "Could not find the table ... in the schema cache"
// error, which was previously surfaced verbatim to reps and admins.
const VOID_REQ_MISSING_MSG = "The void-request table does not exist yet. Ask the developer to run migration 0022_invoice_void_requests.sql in Supabase, then try again.";

/** Rep-facing: create a new pending invoice void request. Supabase-first per
 *  Rule Zero — the row only "exists" once Supabase confirms the INSERT.
 *  invoice_snapshot is stored as audit-only JSON; it is NEVER used for math.
 *
 *  RLS enforces: rep_id = auth.uid()::text, status = 'pending', no decision
 *  fields set on insert.
 *
 *  @param {object} args
 *  @param {string} args.repId            must equal auth.uid()::text
 *  @param {string} [args.repName]
 *  @param {string} args.invoiceId        the user-facing invoice id (e.g. "INV-F290")
 *  @param {number} [args.invoiceDbId]    optional FK to portal_orders.id
 *  @param {string} [args.reason]         rep's note (why void)
 *  @param {object} [args.invoiceSnapshot] audit-only snapshot of the invoice
 *  @returns {Promise<object>} the inserted row from Supabase
 */
export async function createInvoiceVoidRequest({ repId, repName, invoiceId, invoiceDbId, reason, invoiceSnapshot }) {
  if (!repId)     throw new Error("Rep id is required (must equal auth.uid() for RLS).");
  if (!invoiceId) throw new Error("Invoice id is required.");
  // Fresh pre-insert check (migration 0028): only ONE pending request of either
  // kind (void or adjustment) per invoice at a time.
  const pendingOther = await findPendingRequestForInvoice(invoiceId);
  if (pendingOther) {
    throw new Error(`A ${pendingOther.kind === 'void' ? 'void' : 'adjustment'} request is already waiting for admin review on this invoice.`);
  }
  const payload = {
    invoice_id:       invoiceId,
    invoice_db_id:    invoiceDbId || null,
    rep_id:           repId,
    rep_name:         repName || "",
    reason:           reason || "",
    status:           'pending',
    invoice_snapshot: invoiceSnapshot && typeof invoiceSnapshot === 'object' ? invoiceSnapshot : null,
    client_id:        genVoidReqClientId(),
  };
  const { data, error } = await supabase
    .from('invoice_void_requests')
    .insert(payload)
    .select()
    .single();
  if (error) {
    console.warn("[createInvoiceVoidRequest] Supabase INSERT failed:", error.message, "payload:", payload);
    throw new Error(isMissingTableError(error) ? VOID_REQ_MISSING_MSG : error.message);
  }
  const cache = readInvoiceVoidRequestsCache();
  cache.unshift(data);
  writeInvoiceVoidRequestsCache(cache);
  return data;
}

/** Rep-facing: cancel one of your own pending void requests. RLS enforces:
 *  rep_id = auth.uid(), current status = 'pending'. Returns the updated row.
 */
export async function cancelInvoiceVoidRequest(id) {
  if (!id) throw new Error("Request id is required.");
  const { data, error } = await supabase
    .from('invoice_void_requests')
    .update({ status: 'cancelled' })
    .eq('id', id)
    .select()
    .single();
  if (error) {
    console.warn("[cancelInvoiceVoidRequest] Supabase UPDATE failed for id", id, ":", error.message);
    throw new Error(isMissingTableError(error) ? VOID_REQ_MISSING_MSG : error.message);
  }
  const cache = readInvoiceVoidRequestsCache();
  const idx = cache.findIndex(r => r.id === id);
  if (idx >= 0 && data) { cache[idx] = data; writeInvoiceVoidRequestsCache(cache); }
  return data;
}

/** Admin-facing: approve a pending void request. Sequence-and-compensate:
 *    1. Re-fetch the request fresh from Supabase (CLAUDE.md hard-rule #3:
 *       never write payloads built from possibly-stale state).
 *    2. UPDATE the void_request → status='approved', decision fields.
 *       Optimistic-locked on status='pending'.
 *    3. UPDATE portal_orders → status='cancelled', cancel_reason=...,
 *       cancelled_date=NOW(). Matches by invoice_db_id when present, else
 *       by invoice_ref. Only those three fields are written — items, total,
 *       customer, rep, payment_terms, all other fields are NEVER modified.
 *
 *  If step 3 fails (no matching row, RLS denial, network), step 2 is rolled
 *  back to status='pending' so admin can retry.
 *
 *  Inventory + commission self-correct from the status='cancelled' flag via
 *  existing repMetrics.js filters — no inventory_movements or
 *  rep_commission_payments rows are touched by this helper.
 */
export async function approveInvoiceVoidRequest(id, { note, adminEmail, adminId } = {}) {
  if (!id) throw new Error("Request id is required.");
  // Step 1 — fresh fetch.
  const { data: current, error: fetchErr } = await supabase
    .from('invoice_void_requests')
    .select('*')
    .eq('id', id)
    .single();
  if (fetchErr) {
    console.warn("[approveInvoiceVoidRequest] Could not fetch request id", id, ":", fetchErr.message);
    throw new Error(isMissingTableError(fetchErr) ? VOID_REQ_MISSING_MSG : fetchErr.message);
  }
  if (!current) throw new Error(`Void request ${id} not found.`);
  if (current.status !== 'pending') {
    throw new Error(`Void request ${id} is already ${current.status}; cannot approve.`);
  }
  // Step 1b — fresh check of the invoice itself. If the admin already voided it
  // (Orders / Commission tab) or it is cancelled for any other reason, approving
  // must ONLY mark this request decided: portal_orders is not touched again (no
  // overwrite of the original cancel_reason / cancelled_date). Fail closed: a
  // failed read throws before anything is written.
  let alreadyVoided = false;
  if (current.invoice_id) {
    let freshRows;
    try { freshRows = await fetchOrderRowsForInvoice(String(current.invoice_id)); }
    catch (e) {
      console.warn("[approveInvoiceVoidRequest] fresh invoice check failed for request", id, "invoice", current.invoice_id, ":", e?.message || e, "- nothing was written.");
      throw new Error("Could not check the current state of invoice " + current.invoice_id + " (" + (e?.message || "read failed") + "). Nothing was changed; try again.");
    }
    alreadyVoided = Array.isArray(freshRows) && freshRows.length > 0 && freshRows.some(r => r && r.status === 'cancelled');
    if (alreadyVoided) {
      const auto = "Invoice was already voided/cancelled before this request was decided (already voided); the invoice was not changed by this approval.";
      note = note ? (String(note) + " | " + auto) : auto;
    }
  }
  // Step 2 — mark approved (optimistic-locked).
  const decidedAt = new Date().toISOString();
  const decidedBy = adminEmail || adminId || null;
  const repReason = (current.reason || "").trim();
  const cancelReason = `Void approved by admin (req:${id})` + (repReason ? ` — rep reason: ${repReason}` : "");
  const { data: approvedRow, error: u1Err } = await supabase
    .from('invoice_void_requests')
    .update({
      status:        'approved',
      decision_note: note || null,
      decided_by:    decidedBy,
      decided_at:    decidedAt,
    })
    .eq('id', id)
    .eq('status', 'pending')
    .select()
    .single();
  if (u1Err) {
    console.warn("[approveInvoiceVoidRequest] Step 2 (mark approved) failed for id", id, ":", u1Err.message);
    throw new Error(isMissingTableError(u1Err) ? VOID_REQ_MISSING_MSG : u1Err.message);
  }
  if (alreadyVoided) {
    // Request marked approved (decision note says already voided); portal_orders untouched.
    const cacheAv = readInvoiceVoidRequestsCache();
    const idxAv = cacheAv.findIndex(r => r.id === id);
    if (idxAv >= 0) { cacheAv[idxAv] = approvedRow; writeInvoiceVoidRequestsCache(cacheAv); }
    return { request: approvedRow, cancelReason: null, alreadyVoided: true };
  }
  // Step 3 — flip portal_orders.status to 'cancelled'. Try invoice_db_id
  // first (exact row match) then fall back to invoice_ref. Surgical UPDATE
  // — only status / cancel_reason / cancelled_date written.
  const orderPatch = {
    status:         'cancelled',
    cancel_reason:  cancelReason,
    cancelled_date: decidedAt,
  };
  let orderUpdated = false;
  let orderErr = null;
  if (current.invoice_db_id != null) {
    try {
      const { data, error } = await supabase
        .from('portal_orders')
        .update(orderPatch)
        .eq('id', current.invoice_db_id)
        .or('status.is.null,status.neq.cancelled')   // never overwrite an invoice a void just cancelled
        .select();
      if (!error && Array.isArray(data) && data.length > 0) orderUpdated = true;
      else if (error) orderErr = error;
    } catch (e) { orderErr = e; }
  }
  if (!orderUpdated && current.invoice_id) {
    try {
      const { data, error } = await supabase
        .from('portal_orders')
        .update(orderPatch)
        .eq('invoice_ref', current.invoice_id)
        .or('status.is.null,status.neq.cancelled')   // never overwrite an invoice a void just cancelled
        .select();
      if (!error && Array.isArray(data) && data.length > 0) orderUpdated = true;
      else if (error) orderErr = error;
    } catch (e) { orderErr = e; }
  }
  if (!orderUpdated && !orderErr && current.invoice_id) {
    // Zero rows matched and no error: a void may have landed between the Step 1b
    // guard read and this UPDATE (the not-cancelled filter skipped it). Re-select:
    // if every row is cancelled the invoice is already voided, so only the request
    // stays approved (its note says so) and nothing is rolled back or overwritten.
    let reRows = null;
    try { reRows = await fetchOrderRowsForInvoice(String(current.invoice_id)); }
    catch (e) { console.warn("[approveInvoiceVoidRequest] Step 3 re-select failed for request", id, "invoice", current.invoice_id, ":", e?.message || e); }
    if (Array.isArray(reRows) && reRows.length > 0 && reRows.every(r => r && r.status === 'cancelled')) {
      const autoLate = "Invoice was already voided/cancelled before this request was decided (already voided); the invoice was not changed by this approval.";
      const lateNote = note ? (String(note) + " | " + autoLate) : autoLate;
      let finalRow = approvedRow;
      try {
        const { data: nd, error: ne } = await supabase.from('invoice_void_requests').update({ decision_note: lateNote }).eq('id', id).select().single();
        if (ne) console.warn("[approveInvoiceVoidRequest] could not record the already-voided note on request", id, ":", ne.message);
        else if (nd) finalRow = nd;
      } catch (e) { console.warn("[approveInvoiceVoidRequest] already-voided note update threw for request", id, ":", e?.message || e); }
      const cacheLv = readInvoiceVoidRequestsCache();
      const idxLv = cacheLv.findIndex(r => r.id === id);
      if (idxLv >= 0) { cacheLv[idxLv] = finalRow; writeInvoiceVoidRequestsCache(cacheLv); }
      return { request: finalRow, cancelReason: null, alreadyVoided: true };
    }
  }
  if (!orderUpdated) {
    console.warn(
      "[approveInvoiceVoidRequest] Step 3 (cancel portal_orders) failed for request",
      id, "invoice_id", current.invoice_id, "— rolling back void_request to pending.",
      orderErr ? "Last error: " + (orderErr.message || orderErr) : "No matching row."
    );
    // Compensate — flip the request back to pending so admin can retry.
    const { error: revErr } = await supabase
      .from('invoice_void_requests')
      .update({
        status:        'pending',
        decision_note: null,
        decided_by:    null,
        decided_at:    null,
      })
      .eq('id', id);
    if (revErr) {
      console.warn(
        "[approveInvoiceVoidRequest] CRITICAL: failed to roll back request", id,
        "after portal_orders UPDATE failure:", revErr.message
      );
    }
    throw new Error(orderErr?.message || `No portal_orders row matched invoice_id ${current.invoice_id}.`);
  }
  // Cache update.
  const cache = readInvoiceVoidRequestsCache();
  const idx = cache.findIndex(r => r.id === id);
  if (idx >= 0) { cache[idx] = approvedRow; writeInvoiceVoidRequestsCache(cache); }
  return { request: approvedRow, cancelReason };
}

/** Admin-facing: reject a pending void request. No portal_orders write.
 *  Optimistic-locked on status='pending'.
 */
export async function rejectInvoiceVoidRequest(id, { note, adminEmail, adminId } = {}) {
  if (!id) throw new Error("Request id is required.");
  const { data, error } = await supabase
    .from('invoice_void_requests')
    .update({
      status:        'rejected',
      decision_note: note || null,
      decided_by:    adminEmail || adminId || null,
      decided_at:    new Date().toISOString(),
    })
    .eq('id', id)
    .eq('status', 'pending')
    .select()
    .single();
  if (error) {
    console.warn("[rejectInvoiceVoidRequest] Supabase UPDATE failed for id", id, ":", error.message);
    throw new Error(isMissingTableError(error) ? VOID_REQ_MISSING_MSG : error.message);
  }
  const cache = readInvoiceVoidRequestsCache();
  const idx = cache.findIndex(r => r.id === id);
  if (idx >= 0 && data) { cache[idx] = data; writeInvoiceVoidRequestsCache(cache); }
  return data;
}

/** Fetch invoice_void_requests with optional filters. Supabase-first; on
 *  failure falls back to localStorage cache (read-only). RLS server-side
 *  filters to admin-all or rep-own.
 *
 *  @param {object} [filters]
 *  @param {string} [filters.repId]      filter to a single rep (admin only)
 *  @param {string} [filters.invoiceId]  filter to a single invoice
 *  @param {string} [filters.status]     'pending' | 'approved' | 'rejected' | 'cancelled'
 *  @param {number} [filters.limit]
 */
export async function listInvoiceVoidRequests({ repId, invoiceId, status, limit } = {}) {
  try {
    let q = supabase
      .from('invoice_void_requests')
      .select('*')
      .order('created_at', { ascending: false });
    if (repId)     q = q.eq('rep_id', repId);
    if (invoiceId) q = q.eq('invoice_id', invoiceId);
    if (status)    q = q.eq('status', status);
    if (limit)     q = q.limit(limit);
    const { data, error } = await q;
    if (error) {
      if (isMissingTableError(error)) {
        console.warn("[listInvoiceVoidRequests] table missing (run migration 0022) — returning no requests.");
        return [];
      }
      console.warn("[listInvoiceVoidRequests] Supabase SELECT failed:", error.message, "— falling back to localStorage cache.");
      throw error;
    }
    if (data) {
      writeInvoiceVoidRequestsCache(data);
      return data;
    }
  } catch (e) {
    console.warn("[listInvoiceVoidRequests] using localStorage fallback:", e?.message || e);
  }
  let rows = readInvoiceVoidRequestsCache();
  if (repId)     rows = rows.filter(r => r.rep_id === repId);
  if (invoiceId) rows = rows.filter(r => r.invoice_id === invoiceId);
  if (status)    rows = rows.filter(r => r.status === status);
  if (limit)     rows = rows.slice(0, limit);
  return rows;
}

/** Real-time subscription on invoice_void_requests. Per-call random channel
 *  name (same pattern as subscribeInventoryRequests / subscribePortalProducts
 *  per PR #85 fix). RLS server-side filters the stream.
 */
export function subscribeInvoiceVoidRequests(callback) {
  if (typeof callback !== 'function') {
    throw new Error("subscribeInvoiceVoidRequests requires a callback function.");
  }
  const channelName = 'inv-void-req-live-' + Math.random().toString(36).slice(2, 10);
  let channel;
  try {
    channel = supabase.channel(channelName)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'invoice_void_requests' },
        payload => {
          try {
            callback({
              event: payload.eventType,
              row: payload.new || payload.old || null
            });
          } catch (e) {
            console.warn("[subscribeInvoiceVoidRequests] callback threw:", e?.message || e);
          }
        })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn(
            "[subscribeInvoiceVoidRequests] channel status:", status,
            "— realtime may be disabled; admin/rep will sync on page reload."
          );
        }
      });
  } catch (e) {
    console.warn("[subscribeInvoiceVoidRequests] failed to open channel:", e?.message || e);
    return () => {};
  }
  return () => {
    try { supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribeInvoiceVoidRequests] removeChannel failed:", e?.message || e); }
  };
}

// ─── Invoice ADJUSTMENT requests (migration 0028 — Rule Zero) ────────────────
// Rep asks admin to correct an unpaid rep-created invoice. Nothing changes on
// the invoice until an admin approves. Supabase is the source of truth; the
// localStorage cache below is a read-only offline fallback.

const LS_INVOICE_ADJ_REQUESTS = "mpcare_invoice_adjustment_requests_cache";

function readAdjRequestsCache() {
  try {
    const raw = localStorage.getItem(LS_INVOICE_ADJ_REQUESTS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn("[readAdjRequestsCache] parse failed:", e?.message || e);
    return [];
  }
}
function writeAdjRequestsCache(rows) {
  try { localStorage.setItem(LS_INVOICE_ADJ_REQUESTS, JSON.stringify(rows || [])); }
  catch (e) { console.warn("[writeAdjRequestsCache] localStorage write failed:", e?.message || e); }
}
function genAdjReqClientId() {
  return `inv-adj-req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 6)}`;
}
const ADJ_MISSING_MSG = "The adjustment-request table does not exist yet. Ask the developer to run migration 0028_invoice_adjustment_requests.sql in Supabase, then try again.";

/** Fresh pre-insert check shared by adjust + void creation: is there already a
 *  PENDING request (either kind) for this invoice? Returns {kind,row} | null.
 *  A missing table (migration not run) is treated as "none" and logged. */
async function findPendingRequestForInvoice(invoiceId) {
  for (const [kind, table] of [['adjust', 'invoice_adjustment_requests'], ['void', 'invoice_void_requests']]) {
    const { data, error } = await supabase
      .from(table).select('id,status,invoice_id').eq('invoice_id', invoiceId).eq('status', 'pending').limit(1);
    if (error) {
      if (isMissingTableError(error)) {
        console.warn(`[findPendingRequestForInvoice] ${table} missing — skipping pending check for it.`);
        continue;
      }
      console.warn("[findPendingRequestForInvoice] SELECT failed on", table, ":", error.message);
      throw error;
    }
    if (data && data.length) return { kind, row: data[0] };
  }
  return null;
}

/** Fresh read of EVERY portal_orders row for an invoice_ref (duplicates per
 *  invoice_ref are possible). Same query order as getAllPortalOrders (created_at
 *  desc) and the same shared dedupe rule (orderRowsNewestFirst), so row [0] is
 *  the row getAllPortalOrders exposes. (Postgres does not define the order of
 *  rows with EQUAL created_at; both callers rely on the same rule and query.) */
async function fetchOrderRowsForInvoice(invoiceRef) {
  const { data, error } = await supabase
    .from('portal_orders').select('*').eq('invoice_ref', invoiceRef).order('created_at', { ascending: false });
  if (error) {
    console.warn("[fetchOrderRowsForInvoice] SELECT failed for", invoiceRef, ":", error.message);
    throw error;
  }
  return orderRowsNewestFirst(data || []);
}

/** Rep-facing: create a pending ADJUSTMENT request. Supabase-first + awaited;
 *  the invoice itself is never touched. Throws (and logs) on any refusal or
 *  failure so the UI can show an error and change nothing. */
export async function createAdjustmentRequest({ repId, repName, repEmail, invoice, proposedItems, proposedTerms, reason }) {
  if (!repId) throw new Error("Rep id is required (must equal auth.uid() for RLS).");
  if (!invoice || !invoice.id) throw new Error("Invoice is required.");
  const why = String(reason || "").trim();
  if (!why) throw new Error("Please enter a reason so admin knows why.");
  const originalItems = parseAdjItems(invoice.items);
  const extras = extrasFromInvoice(invoice);
  if (!totalFormulaConsistent(invoice)) {
    throw new Error("This invoice total does not match its line items, so it cannot be adjusted automatically. Ask admin, or request a void.");
  }
  const items = Array.isArray(proposedItems) ? proposedItems : [];
  const proposed = {
    items,
    paymentTerms: String(proposedTerms || invoice.paymentTerms || "").trim(),
    total: recomputeTotal(items, extras),
    extras,
  };
  const bad = validateProposal(proposed, extras);
  if (bad) throw new Error(bad);
  if (!hasAdjustmentChange(originalItems, items, invoice.paymentTerms, proposed.paymentTerms)) {
    throw new Error("Nothing was changed. Edit a line or the payment terms first.");
  }
  // Fresh pre-insert checks.
  const pending = await findPendingRequestForInvoice(invoice.id);
  if (pending) throw new Error(`A ${pending.kind === 'void' ? 'void' : 'adjustment'} request is already waiting for admin review on this invoice.`);
  const rows = await fetchOrderRowsForInvoice(invoice.id);
  if (!rows.length) throw new Error("This invoice has not reached the database yet (it may still be syncing). Try again in a minute.");
  const base = rows[0];
  if (String(base.status || '').toLowerCase() === 'cancelled') throw new Error("This invoice is already cancelled.");
  // The rep must be editing what the database actually holds. If the local copy
  // is stale (e.g. an earlier approved adjustment has not reached this device),
  // refuse rather than build a proposal from stale lines.
  if (!sameItems(originalItems, parseAdjItems(base.items)) || Math.abs(round2(base.total) - round2(invoice.total)) > 0.005) {
    throw new Error("This invoice was changed elsewhere and your copy is out of date. Reload the Invoices page, then try again.");
  }
  // Account discount (0034): the rate the database stores for this invoice must be the rate the
  // proposal was worked out with, otherwise the approve check would refuse it later.
  if (!samePct(rowDiscountPct(base), extras.discountPct)) {
    throw new Error("This invoice was changed elsewhere and your copy is out of date. Reload the Invoices page, then try again.");
  }
  const payload = {
    client_id:         genAdjReqClientId(),
    invoice_id:        invoice.id,
    invoice_db_id:     base.id ?? null,
    rep_id:            repId,
    rep_name:          repName || "",
    rep_email:         repEmail || "",
    customer:          invoice.customer || "",
    reason:            why,
    status:            'pending',
    original_snapshot: {
      id: invoice.id, customer: invoice.customer || "", date: invoice.date || "", status: invoice.status || "",
      paymentTerms: invoice.paymentTerms || "", total: round2(invoice.total), items: originalItems, extras,
      db: { items: base.items, total: base.total, status: base.status, rep: base.rep || "", ...(rowDiscountPct(base) > 0 ? { discount_pct: rowDiscountPct(base) } : {}) },
    },
    proposed_changes:  proposed,
  };
  const { data, error } = await supabase.from('invoice_adjustment_requests').insert(payload).select().single();
  if (error) {
    console.warn("[createAdjustmentRequest] Supabase INSERT failed:", error.message, "invoice:", invoice.id);
    // The partial unique index inv_adj_req_one_pending_per_invoice (0028) refuses a second pending row.
    if (error.code === '23505' && /one_pending/i.test(String(error.message || "") + String(error.details || ""))) {
      throw new Error("An adjustment request is already waiting for admin review on this invoice.");
    }
    throw new Error(isMissingTableError(error) ? ADJ_MISSING_MSG : error.message);
  }
  const cache = readAdjRequestsCache();
  cache.unshift(data);
  writeAdjRequestsCache(cache);
  return data;
}

/**
 * Admin-facing: adjust an UNPAID rep-created invoice directly (Commission tab).
 * Reuses the rep-request engine end to end (option (a), migration 0032): it files
 * a PENDING request on the rep's behalf (initiated_by 'admin') and immediately
 * runs approveAdjustmentRequest with the same options the Orders card uses, so the
 * audit trail (pre_apply_order, approved_changes, decided_by), the compare-and-set
 * apply, the request flip and the rep's realtime resync are all the existing,
 * tested code. It NEVER throws: every failure is a result object.
 *
 * Order (Supabase first, cache mirror and commission correction happen in the
 * caller only after ok:true):
 *   0. input + eligibility guards (pure, no I/O)
 *   1. FRESH reads: order rows for the invoice_ref, pending requests of both kinds
 *      (strict, fail closed), the invoice's paid commission rows and corrections
 *      (must load: the commission consequence is shown from them), rep profiles
 *   2. guards on the fresh reads (DB row exists, newest not cancelled, not a
 *      dealer order, cache copy == DB row, rep resolved to ONE profile)
 *   3. INSERT the pending request (nothing on the invoice changes yet)
 *   4. approveAdjustmentRequest (audit -> surgical CAS row writes -> flip)
 * A crash / failure leaves:
 *   before 3: nothing written.
 *   3 done, approve failed BEFORE its audit write: the pending row is cancelled
 *      again (the guard allows it while no apply is in progress); if that cancel
 *      fails the pending row stays and the message says so.
 *   approve failed AFTER its audit write / partial apply: the pending row is left
 *      for the normal Finish / Approve card in Orders (safe to press repeatedly).
 *
 * @param {object} p
 *   invoice        the admin cache invoice (read BY VALUE up front)
 *   lines          [{sku, qty}] as edited (qty change, removed line, added catalog SKU)
 *   lookup         (sku) => catalog product | null (catalog price for NEW skus only)
 *   reason         required; visible to the rep
 *   confirmedUnpaid must be true (admin confirmed in Square / accounting)
 *   admin          { id, email } of the acting admin
 * @returns {Promise<{ok:true, request, rows, oldTotal, newTotal, rep, requestId}
 *   | {ok:false, message, code?, nothingChanged:boolean, pendingRequestId?, mayBePartlyApplied?}>}
 */
export async function createAdminAdjustment({ invoice, lines, lookup, reason, confirmedUnpaid, admin, legacyPaid } = {}) {
  const refuse = (message, code) => ({ ok: false, message, code: code || 'refused', nothingChanged: true });
  try {
    // 0 ── input + eligibility (pure)
    if (!invoice || !invoice.id) return refuse("Invoice is required.", 'input');
    const invId = String(invoice.id);
    const why = String(reason || "").trim();
    if (!why) return refuse("Please enter a reason. The rep will see it.", 'input');
    const actor = String((admin && (admin.email || admin.id)) || "").trim();
    if (!actor) return refuse("Could not identify the signed-in admin, so the change cannot be recorded. Reload the page and sign in again.", 'input');
    if (confirmedUnpaid !== true) return refuse("Confirm in Square / accounting that this invoice has not been paid before adjusting it.", 'unpaid_confirm');
    if (!isRepCreatedInvoice(invoice)) {
      if (invoice.source === 'admin_invoice') return refuse("Invoices created in the office cannot be adjusted yet. Void it from Delete and create a new invoice.", 'admin_invoice');
      return refuse("Only rep-created invoices can be adjusted here. Dealer-placed orders cannot be adjusted (their discount and shipping are not stored on the server).", 'dealer_order');
    }
    if (statusClass(invoice.status) !== 'unpaid') return refuse("This invoice is paid, awaiting payment confirmation or cancelled on this device, so it cannot be adjusted here. Void it with a refund instead.", 'status');
    if (!totalFormulaConsistent(invoice)) return refuse("This invoice total does not match its line items, so it cannot be adjusted automatically.", 'formula');
    const originalItems = parseAdjItems(invoice.items);
    const extras = extrasFromInvoice(invoice);
    const built = buildProposedItems(originalItems, lines, lookup);
    if (built.missing.length) return refuse(`Product not found in the catalog: ${built.missing.join(", ")}`, 'input');
    const items = built.items;
    const badItems = validateProposal({ items }, extras);
    if (badItems) return refuse(badItems, 'input');
    if (sameItems(originalItems, items)) return refuse("Nothing was changed. Edit a quantity, remove a line or add a product first.", 'input');
    const newTotal = recomputeTotal(items, extras);
    const badTotal = validateProposal({ items, total: newTotal }, extras);
    if (badTotal) return refuse(badTotal, 'input');

    // 1 ── FRESH reads (never the cache for anything that gates a write)
    const [pend, paid, corr, rows, profiles, remit] = await Promise.all([
      listPendingRequestsForInvoiceStrict(invId),
      listCommissionPaymentsPaged({ invoiceId: invId }),
      listCorrections({ invoiceId: invId }),
      fetchOrderRowsForInvoice(invId).then((r) => ({ ok: true, rows: r }), (e) => ({ ok: false, error: e?.message || String(e) })),
      getAllProfiles().then((r) => ({ ok: true, rows: r }), (e) => ({ ok: false, error: e?.message || String(e) })),
      getActiveRemittanceForInvoiceStrict(invId),
    ]);
    // Central paid signal (0033): the rep already handed this payment in. FAIL CLOSED on a read error.
    if (!remit.ok) return refuse(remittanceReadFailedMsg(remit.error), 'read');
    if (remit.remittance) return refuse(REMIT_ADJUST_BLOCK_MSG, 'remittance_received');
    if (!pend.ok) return refuse(`The pending rep requests for this invoice could not be checked (${pend.error}). Nothing was changed.`, 'read');
    if ((pend.adjust || []).length > 0 || (pend.void || []).length > 0) return refuse("The rep has a pending adjustment or void request for this invoice. Decide it in Orders first. Nothing was changed.", 'pending_request');
    if (!paid.ok) return refuse(`The commission payout records could not be read (${paid.error}). Nothing was changed.`, 'read');
    if (!corr.ok) return refuse(`The commission corrections could not be read (${corr.error}). Nothing was changed.`, 'read');
    // Fail closed BEFORE any write: corrections table missing (0031 not run) while commission was already
    // paid, or a legacy-only paid invoice (no ledger row to correct against).
    const pre = preflightCorrectionState({ invoiceId: invId, paidRowsCount: (paid.rows || []).length, corr, legacyPaid: legacyPaid === true });
    if (!pre.ok) return refuse(pre.message, pre.code);
    if (!rows.ok) return refuse(`The invoice could not be read from the database (${rows.error}). Nothing was changed.`, 'read');
    if (!profiles.ok) return refuse(`The rep accounts could not be read (${profiles.error}). Nothing was changed.`, 'read');

    // 2 ── guards on the fresh reads
    const base = rows.rows[0];
    if (!base) return refuse("This invoice has no record in the database (it exists only on some devices), so it cannot be adjusted. Nothing was changed.", 'no_db_row');
    if (norm0(base.status) === 'cancelled') return refuse("This invoice is already cancelled in the database. Nothing was changed.", 'cancelled');
    if (rows.rows.some((r) => norm0(r.status) === 'cancelled')) return refuse("A record of this invoice is cancelled in the database. Nothing was changed.", 'cancelled');
    if (base.source === 'admin_invoice') return refuse("This invoice was created in the office and cannot be adjusted yet. Void it from Delete and create a new invoice.", 'admin_invoice');
    if (base.source === 'portal_order' && !String(base.invoice_ref || invId).startsWith("INV-F")) return refuse("This is a dealer-placed order and cannot be adjusted here.", 'dealer_order');
    if (!sameItems(originalItems, parseAdjItems(base.items)) || Math.abs(round2(base.total) - round2(invoice.total)) > 0.005) {
      return refuse("This invoice was changed elsewhere and this page is out of date. Reload the admin portal, then try again. Nothing was changed.", 'stale');
    }
    // Account discount (0034): the stored rate must be the rate this page worked the new total out with.
    if (!samePct(rowDiscountPct(base), extras.discountPct)) {
      return refuse("This invoice's customer discount in the database differs from what this page shows. Reload the admin portal, then try again. Nothing was changed.", 'stale');
    }
    const who = resolveRepIdentity({ invoice, dbRow: base, profiles: profiles.rows });
    if (!who.ok) return refuse(who.reason, who.code);
    const repNameOnRow = String(base.rep || "").trim();
    if (!repNameOnRow) return refuse("The invoice record has no rep name, so its owner cannot be verified. Nothing was changed.", 'unresolved');

    // 3 ── INSERT the pending request (initiated_by 'admin')
    const payload = {
      client_id:         genAdjReqClientId(),
      invoice_id:        invId,
      invoice_db_id:     base.id ?? null,
      rep_id:            who.rep.id,
      // The approve engine compares this with the order row's rep NAME.
      rep_name:          repNameOnRow,
      rep_email:         who.rep.email || "",
      customer:          invoice.customer || "",
      reason:            why,
      status:            'pending',
      initiated_by:      'admin',
      admin_actor:       actor,
      original_snapshot: {
        id: invId, customer: invoice.customer || "", date: invoice.date || "", status: invoice.status || "",
        paymentTerms: invoice.paymentTerms || "", total: round2(invoice.total), items: originalItems, extras,
        db: { items: base.items, total: base.total, status: base.status, rep: base.rep || "", ...(rowDiscountPct(base) > 0 ? { discount_pct: rowDiscountPct(base) } : {}) },
      },
      // Payment terms are NOT changed by an admin adjust: carry the DB value so the
      // approve engine writes no payment_terms change.
      proposed_changes:  { items, paymentTerms: String(base.payment_terms || "").trim(), total: newTotal, extras },
    };
    const { data: inserted, error: insErr } = await supabase.from('invoice_adjustment_requests').insert(payload).select().single();
    if (insErr || !inserted) {
      console.warn("[createAdminAdjustment] Supabase INSERT failed:", insErr?.message, "invoice:", invId);
      if (insErr && insErr.code === '23505' && /one_pending/i.test(String(insErr.message || "") + String(insErr.details || ""))) {
        return refuse("A request is already waiting for review on this invoice. Decide it in Orders first. Nothing was changed.", 'pending_request');
      }
      if (insErr && /initiated_by|admin_actor|row-level security/i.test(String(insErr.message || ""))) {
        return refuse("The database does not allow admin-filed adjustments yet. Run migration 0032_admin_initiated_adjustments.sql in Supabase, then try again. (" + insErr.message + ") Nothing was changed.", 'migration');
      }
      return refuse(isMissingTableError(insErr) ? ADJ_MISSING_MSG : `The adjustment could not be started (${insErr?.message || "no row returned"}). Nothing was changed.`, 'insert');
    }

    // Cancel the orphan pending request (allowed by the guard while no apply is in progress) and refuse.
    const cancelOrphanAndRefuse = async (detail, code) => {
      try {
        await cancelAdjustmentRequest(inserted.id);
        return { ok: false, nothingChanged: true, code: code || 'approve_refused', message: `${detail} Nothing was changed on the invoice (the temporary request was cancelled).` };
      } catch (ce) {
        console.warn("[createAdminAdjustment] could not cancel the orphan pending request", inserted.id, ":", ce?.message || ce);
        return { ok: false, nothingChanged: true, pendingRequestId: inserted.id, code: 'orphan_pending',
          message: `${detail} Nothing was changed on the invoice, but the temporary request #${inserted.id} is still pending in Orders (${ce?.message || "could not be cancelled"}). Reject or Finish it there.` };
      }
    };

    // 3b ── central paid signal (0033), RE-CHECKED right before the approve engine writes: the first
    // check above ran before several awaits, so a remittance recorded in between is caught here.
    // Fail closed on a read error. Nothing on the invoice has been written yet.
    const remitAgain = await getActiveRemittanceForInvoiceStrict(invId);
    if (!remitAgain.ok) return cancelOrphanAndRefuse(remittanceReadFailedMsg(remitAgain.error), 'read');
    if (remitAgain.remittance) return cancelOrphanAndRefuse(REMIT_ADJUST_BLOCK_MSG, 'remittance_received');

    // 4 ── approve with the same options the Orders card uses
    try {
      const { request, rows: appliedRows } = await approveAdjustmentRequest(inserted.id, {
        note: null,
        adminEmail: admin.email || "",
        adminId: admin.id || undefined,
        expectedUpdatedAt: inserted.updated_at,
        cacheStatus: invoice.status ?? "",
        cacheFound: true,
        confirmedUnpaid: true,
      });
      return { ok: true, request, rows: appliedRows, oldTotal: round2(invoice.total), newTotal, rep: who.rep, requestId: inserted.id, paidRowsCount: (paid.rows || []).length };
    } catch (e) {
      console.warn("[createAdminAdjustment] approve failed for request", inserted.id, "invoice", invId, ":", e?.message || e);
      const detail = e?.message || "unknown error";
      const partly = e instanceof PartialApplyError || e?.nothingChanged === false;
      if (partly) {
        return { ok: false, nothingChanged: false, mayBePartlyApplied: true, pendingRequestId: inserted.id, code: 'partial',
          message: `Adjusting ${invId} may be PARTLY applied: ${detail} The pending request #${inserted.id} was left in Orders: open Orders and press Approve (Finish approval) to complete it. It is safe to repeat.` };
      }
      if (e?.alreadyDecided) {
        return { ok: false, nothingChanged: true, pendingRequestId: inserted.id, code: 'decided', message: `${detail} Nothing was changed on the invoice.` };
      }
      // Proven: no portal_orders write happened. Take the orphan pending row back.
      return cancelOrphanAndRefuse(detail);
    }
  } catch (err) {
    console.warn("[createAdminAdjustment] unexpected failure for", invoice && invoice.id, ":", err?.message || err);
    return { ok: false, nothingChanged: false, code: 'unexpected', message: `Adjusting the invoice failed unexpectedly (${err?.message || err}). Check Orders and the Commission tab before trying again.` };
  }
}

/** Empty result of a conditional status UPDATE (.eq status pending): the request
 *  was already decided by someone else. Re-reads the status and throws an Error
 *  with alreadyDecided = true and a readable message (never a raw PostgREST one). */
async function throwAlreadyDecided(id, who) {
  let status = null;
  try {
    const { data: l, error: lErr } = await supabase.from('invoice_adjustment_requests').select('status').eq('id', id).single();
    if (lErr) console.warn("[" + who + "] status re-read failed for request", id, ":", lErr.message);
    else status = l && l.status;
  } catch (e) { console.warn("[" + who + "] status re-read threw for request", id, ":", e?.message || e); }
  if (status && status !== 'pending') {
    const e = new Error("This request was already " + status + ".");
    e.alreadyDecided = true;
    throw e;
  }
  throw new Error("The request could not be updated (it may have changed). Refresh the list and try again.");
}

/** Rep-facing: cancel one of your OWN pending adjustment requests. */
export async function cancelAdjustmentRequest(id) {
  if (!id) throw new Error("Request id is required.");
  // Fast-path pre-check (the DB guard trigger in 0028 is the authority). Same
  // predicate as admin reject: a marker on any order row, OR the audit written
  // with no conclusive failure recorded since. A conclusively refused approve
  // (last_apply_error set, nothing written) does NOT lock the rep out.
  try {
    const { data: cur, error: curErr } = await supabase
      .from('invoice_adjustment_requests').select('id,status,pre_apply_order,last_apply_error').eq('id', id).single();
    if (curErr) console.warn("[cancelAdjustmentRequest] pre-check read failed for id", id, ":", curErr.message, "- relying on the database guard.");
    else if (cur && cur.status !== 'pending') return throwAlreadyDecided(id, "cancelAdjustmentRequest");
    else if (applyInProgressByFields(cur)) throw new Error(ADJ_PROCESSING_MSG);
    if (await isAdjustmentRequestApplied(id)) throw new Error(ADJ_PROCESSING_MSG);
  } catch (e) {
    if (e && (e.message === ADJ_PROCESSING_MSG || e.alreadyDecided)) throw e;
    console.warn("[cancelAdjustmentRequest] pre-check failed for id", id, ":", e?.message || e, "- relying on the database guard.");
  }
  const { data: rows, error } = await supabase
    .from('invoice_adjustment_requests')
    .update({ status: 'cancelled' })
    .eq('id', id)
    .eq('status', 'pending')
    .select();
  if (error) {
    console.warn("[cancelAdjustmentRequest] Supabase UPDATE failed for id", id, ":", error.message);
    if (/admin is processing/i.test(String(error.message || ""))) throw new Error(ADJ_PROCESSING_MSG);
    throw error;
  }
  if (!Array.isArray(rows) || rows.length !== 1) return throwAlreadyDecided(id, "cancelAdjustmentRequest");
  const data = rows[0];
  const cache = readAdjRequestsCache();
  const idx = cache.findIndex(r => r.id === id);
  if (idx >= 0 && data) { cache[idx] = data; writeAdjRequestsCache(cache); }
  return data;
}

/** Supabase-first list; localStorage cache is a read-only fallback. RLS scopes
 *  the result (admin: all, rep: own). */
export async function listAdjustmentRequests({ repId, invoiceId, status, limit } = {}) {
  try {
    let q = supabase.from('invoice_adjustment_requests').select('*').order('created_at', { ascending: false });
    if (repId)     q = q.eq('rep_id', repId);
    if (invoiceId) q = q.eq('invoice_id', invoiceId);
    if (status)    q = q.eq('status', status);
    if (limit)     q = q.limit(limit);
    const { data, error } = await q;
    if (error) {
      if (isMissingTableError(error)) {
        console.warn("[listAdjustmentRequests] table missing (run migration 0028) — returning no requests.");
        return [];
      }
      console.warn("[listAdjustmentRequests] Supabase SELECT failed:", error.message, "— falling back to localStorage cache.");
      throw error;
    }
    if (data) {
      // Only an unfiltered-by-status/invoice fetch may replace the cache.
      if (!invoiceId && !status) writeAdjRequestsCache(data);
      return data;
    }
  } catch (e) {
    console.warn("[listAdjustmentRequests] using localStorage fallback:", e?.message || e);
  }
  let rows = readAdjRequestsCache();
  if (repId)     rows = rows.filter(r => r.rep_id === repId);
  if (invoiceId) rows = rows.filter(r => r.invoice_id === invoiceId);
  if (status)    rows = rows.filter(r => r.status === status);
  if (limit)     rows = rows.slice(0, limit);
  return rows;
}

/** Real-time subscription. Per-call random channel name (PR #85 lesson). */
export function subscribeAdjustmentRequests(callback) {
  if (typeof callback !== 'function') throw new Error("subscribeAdjustmentRequests requires a callback function.");
  const channelName = 'inv-adj-req-live-' + Math.random().toString(36).slice(2, 10);
  let channel;
  try {
    channel = supabase.channel(channelName)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'invoice_adjustment_requests' },
        payload => {
          try { callback({ event: payload.eventType, row: payload.new || payload.old || null }); }
          catch (e) { console.warn("[subscribeAdjustmentRequests] callback threw:", e?.message || e); }
        })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribeAdjustmentRequests] channel status:", status, "— realtime may be disabled; will sync on reload.");
        }
      });
  } catch (e) {
    console.warn("[subscribeAdjustmentRequests] failed to open channel:", e?.message || e);
    return () => {};
  }
  return () => {
    try { supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribeAdjustmentRequests] removeChannel failed:", e?.message || e); }
  };
}

function norm0(s) { return String(s == null ? "" : s).trim().toLowerCase(); }

/** Thrown when an approve got far enough that the invoice rows MAY already carry
 *  the adjustment while the request is still PENDING (e.g. the final request
 *  flip failed, or a write's outcome could not be confirmed). The UI must not
 *  say "Nothing was changed"; pressing Approve again is safe because the apply
 *  step is idempotent per request id (and a request whose rows already carry
 *  its id takes the finish-only path). */
export class PartialApplyError extends Error {
  constructor(message, lastApplyError) {
    super(message);
    this.name = 'PartialApplyError';
    this.lastApplyError = lastApplyError || message;
  }
}

/** Thrown by the FINISH path when the invoice already carries this request's id
 *  but its lines no longer equal the proposal (edited afterwards). Rule: the
 *  admin may finish anyway by passing acknowledgeDivergence: true (the UI asks
 *  them to type FINISH); nothing on the invoice is changed by that. Reject is
 *  refused in this state because the invoice already carries the change, so
 *  Finish is always the one legal action. */
export class NeedsAcknowledgementError extends Error {
  constructor(message) {
    super(message);
    this.name = 'NeedsAcknowledgementError';
    this.needsAcknowledgement = true;
  }
}

const ADJ_PROCESSING_MSG = "The admin is processing this request: it can no longer be cancelled.";
// A PostgREST / Postgres error carries a non-empty SQLSTATE-style `code`: the
// server answered, so the statement did NOT commit. A transport failure has no
// code (or a thrown exception): the write may or may not have committed.
const isDefinitiveDbError = (e) => !!(e && typeof e === 'object' && typeof e.code === 'string' && e.code !== '');

/** True when ANY portal_orders row already carries this request's id (the
 *  approve has written the invoice, fully or partly). Throws on a read error so
 *  callers never mistake "could not check" for "not applied". */
export async function isAdjustmentRequestApplied(id) {
  const { data, error } = await supabase
    .from('portal_orders').select('id').eq('adjustment_request_id', String(id)).limit(1);
  if (error) {
    console.warn("[isAdjustmentRequestApplied] SELECT failed for request", id, ":", error.message);
    throw error;
  }
  return !!(data && data.length);
}

/** Fresh read of EVERY portal_orders row that carries this request's id,
 *  whatever its invoice_ref. This is the ONE definition of "applied" shared by
 *  reject (isAdjustmentRequestApplied) and the approve finish path. Throws on a
 *  read error so "could not check" is never mistaken for "not applied". */
async function fetchOrderRowsByRequestId(id) {
  const { data, error } = await supabase
    .from('portal_orders').select('*').eq('adjustment_request_id', String(id));
  if (error) {
    console.warn("[fetchOrderRowsByRequestId] SELECT failed for request", id, ":", error.message);
    throw error;
  }
  return Array.isArray(data) ? data : [];
}

async function readOrderRow(rowId) {
  try {
    const { data, error } = await supabase.from('portal_orders').select('*').eq('id', rowId).single();
    if (error || !data) return { row: null, error: error || new Error("row not returned") };
    return { row: data, error: null };
  } catch (e) {
    return { row: null, error: e };
  }
}

/** Admin-facing: approve + APPLY an adjustment request. Crash-safe order: the
 *  request only flips to 'approved' AFTER the invoice rows carry the change, so
 *  a client crash can never leave an approved request on an unchanged invoice.
 *   1. fresh fetch of the request; must still be pending (and unchanged since
 *      the admin opened it when expectedUpdatedAt is given, except on a retry:
 *      pre_apply_order already set)
 *   2. fresh fetch of ALL portal_orders rows for the invoice_ref, newest first
 *      by the SAME rule getAllPortalOrders uses. If ANY row already carries THIS
 *      request id -> FINISH-ONLY path (skips 2b, 3 and the not-paid tick; see
 *      below). Otherwise (2b): refuse when none / newest cancelled / dealer-
 *      placed / not owned by the requesting rep / admin cache missing, paid,
 *      payment_pending or with a status that differs from the rep's snapshot /
 *      no confirmation tick / the NEWEST row changed since the rep filed.
 *   3. (normal path) while the request is STILL pending: pre_apply_order in its
 *      OWN conditional UPDATE (.is(pre_apply_order, null): never overwritten;
 *      0 rows = already audited, re-read and continue), then approved_changes +
 *      last_apply_error in a separate UPDATE. Never nulled later.
 *   4. per row, compare-and-set UPDATE of items, total, (payment_terms only if
 *      changed), adjusted_at, adjustment_request_id = request id. CAS = id +
 *      status not cancelled + marker unchanged-or-ours + total unchanged. Newest
 *      row first; older duplicates are best-effort. Any error / exception on the
 *      newest row is followed by a re-read: applied -> continue; conclusively
 *      not applied -> plain Error (nothing written); cannot tell ->
 *      PartialApplyError. FINISH-ONLY path: the newest row is never written; only
 *      older duplicates that lack any marker and are not cancelled are completed.
 *   5. only then: UPDATE request SET status='approved' WHERE id AND
 *      status='pending'. If this fails the request stays pending and the admin
 *      presses Approve again -> PartialApplyError.
 *  Thrown errors carry nothingChanged (true only when the helper proves no
 *  portal_orders write happened) and, for step-5 refusals, alreadyDecided.
 *  Never deletes; never touches user_id, customer, rep, status, shipping,
 *  tracking, notes or created_at. Returns { request, rows }. */
export async function approveAdjustmentRequest(id, opts) {
  const state = { invoiceTouched: false };
  try {
    return await approveAdjustmentInner(id, opts || {}, state);
  } catch (e) {
    if (e && typeof e === 'object' && !(e instanceof PartialApplyError) && e.nothingChanged === undefined) e.nothingChanged = !state.invoiceTouched;
    throw e;
  }
}

async function approveAdjustmentInner(id, { note, adminEmail, adminId, expectedUpdatedAt, cacheStatus, cacheFound, confirmedUnpaid, acknowledgeDivergence }, state) {
  if (!id) throw new Error("Request id is required.");
  // 1 ── fresh request
  const { data: current, error: fetchErr } = await supabase
    .from('invoice_adjustment_requests').select('*').eq('id', id).single();
  if (fetchErr) {
    console.warn("[approveAdjustmentRequest] Could not fetch request id", id, ":", fetchErr.message);
    throw fetchErr;
  }
  if (!current) throw new Error(`Adjustment request ${id} not found.`);
  if (current.status !== 'pending') {
    const e = new Error(`This request is already ${current.status}.`);
    e.alreadyDecided = true;
    throw e;
  }
  // A retry (audit already written by an earlier attempt) skips the staleness
  // check: the earlier attempt's own audit / error writes bumped updated_at.
  const isRetry = current.pre_apply_order != null;
  if (!isRetry && expectedUpdatedAt && current.updated_at && String(current.updated_at) !== String(expectedUpdatedAt)) {
    throw new Error("This request changed since you opened it. Refresh the list and review it again.");
  }
  // 2 ── fresh order rows (newest first, shared rule)
  const rows = await fetchOrderRowsForInvoice(current.invoice_id);
  const snap = current.original_snapshot && typeof current.original_snapshot === 'object' ? current.original_snapshot : {};
  const proposed = current.proposed_changes || {};
  const itemsErr = validateProposal({ items: proposed.items });
  if (itemsErr) throw new Error(itemsErr);
  const newItems = proposed.items;
  // Same query reject uses, so both actions agree on what "applied" means even if
  // a marked row's invoice_ref was changed afterwards.
  const markerRows = await fetchOrderRowsByRequestId(id);
  const finishOnly = markerRows.length > 0;
  const markerInRows = rows.some(r => r.adjustment_request_id != null && String(r.adjustment_request_id) === String(id));
  // Finish-only and the marked row is NOT among the invoice_ref rows: the marked row IS the
  // adjusted one (prefer the one that already carries the proposed lines), not an unmarked
  // older duplicate that still has the invoice_ref.
  const newest = (finishOnly && !markerInRows)
    ? (markerRows.find(r => rowAlreadyApplied(r, id, newItems, proposed.total)) || markerRows[0])
    : rows[0];

  // A definitive step-2b refusal on a RETRY (audit written by an earlier attempt, no marker on
  // any row): record last_apply_error so the trigger predicate (audit AND no error) becomes
  // false and Reject stays a legal admin action. Safe because nothing was written in this
  // attempt and a fresh re-read still shows no marker. First attempts do not need it (no
  // audit exists yet, so Reject is already legal).
  const refuseStep2b = async (msg) => {
    console.warn("[approveAdjustmentRequest] refused for", current.invoice_id, ":", msg);
    if (isRetry) {
      try {
        const again = await fetchOrderRowsByRequestId(id);
        if (again.length === 0) {
          const { error: recErr } = await supabase.from('invoice_adjustment_requests')
            .update({ last_apply_error: String(msg).slice(0, 500) }).eq('id', id).eq('status', 'pending');
          if (recErr) console.warn("[approveAdjustmentRequest] could not record last_apply_error on request", id, ":", recErr.message);
        }
      } catch (e) { console.warn("[approveAdjustmentRequest] could not record last_apply_error on request", id, ":", e?.message || e); }
    }
    throw new Error(msg);
  };

  if (finishOnly) {
    // FINISH-ONLY: the invoice already carries this request. No cache / payment /
    // status-class / ownership / baseline checks and no not-paid tick: nothing new
    // is written to the newest row, so none of them can protect anything.
    const newestDone = !!newest && rowAlreadyApplied(newest, id, newItems, proposed.total);
    if (!newestDone && acknowledgeDivergence !== true) {
      throw new NeedsAcknowledgementError(`The invoice already carries this request, but the newest order row (#${newest ? newest.id : "?"}) no longer matches what the rep asked for: it was edited afterwards or is not the row that was adjusted. Reject is not possible because the invoice already carries the change. Type FINISH to mark the request approved as it stands (nothing on the invoice will be changed).`);
    }
  } else {
    if (confirmedUnpaid !== true) {
      throw new Error("Confirm in Square / accounting that this invoice has not been paid before approving.");
    }
    // Admin-initiated adjusts only (a rep's own request keeps its warn-only card): refuse when the
    // rep already handed this payment in (0033), and fail closed when that cannot be read.
    if (current.initiated_by === 'admin') {
      const rm = await getActiveRemittanceForInvoiceStrict(current.invoice_id);
      if (!rm.ok) await refuseStep2b(remittanceReadFailedMsg(rm.error));
      if (rm.remittance) await refuseStep2b(REMIT_ADJUST_BLOCK_MSG);
    }
    const block = approvalBlockReason({ request: current, orderRows: rows, cacheStatus, cacheFound, snapshotStatus: snap.status == null ? null : snap.status });
    if (block) await refuseStep2b(block);
    const ownerProblem = requestOwnershipProblem(current, newest);
    if (ownerProblem) await refuseStep2b(ownerProblem);
    if (rowBaselineMismatch(snap.db, newest)) {
      await refuseStep2b(`Order row #${newest.id} (the newest row for this invoice) changed after the rep filed the request. Reject this request and ask the rep to file a new one from the current invoice.`);
    }
    if (parseAdjItems(newest.items).length === 0) {
      await refuseStep2b("The order in the database has no line items to adjust. Nothing was changed.");
    }
    // A row with a stored account rate (0034) is re-priced at that percent; every other row carries its dollar delta.
    if (Math.abs(expectedNewTotal(newest.items, newest.total, newItems, rowDiscountPct(newest)) - round2(proposed.total)) > 0.011) {
      await refuseStep2b("The proposed total does not match what the line items add up to for this invoice. Reject and ask the rep to file a new request.");
    }
  }
  const newItemsText = JSON.stringify(newItems);
  const decidedAt = new Date().toISOString();
  const decidedBy = adminEmail || adminId || null;
  // Per-row plan (decided from the rows as read). Recorded in pre_apply_order.
  const carriesThis = (r) => r.adjustment_request_id != null && String(r.adjustment_request_id) === String(id);
  const plan = rows.map((r, idx) => {
    const isNewest = idx === 0;
    const newTotal = expectedNewTotal(r.items, r.total, newItems, rowDiscountPct(r));
    let action = 'apply';
    if (norm0(r.status) === 'cancelled') action = 'skip-cancelled';
    else if (rowAlreadyApplied(r, id, newItems, newTotal)) action = 'skip-already-applied';
    else if (carriesThis(r)) action = 'skip-marker-differs';
    else if (finishOnly && (isNewest || !markerInRows)) action = 'skip-finish-only';
    else if (finishOnly && r.adjustment_request_id != null && String(r.adjustment_request_id) !== "") action = 'skip-other-marker';
    else if (!isNewest && requestOwnershipProblem(current, r)) action = 'skip-other-owner';
    else if (!isNewest && parseAdjItems(r.items).length === 0) action = 'skip-no-items';
    return { row: r, action, newTotal };
  });
  // 3 ── audit BEFORE the apply, while the request is still pending (normal path).
  if (!finishOnly) {
    if (current.pre_apply_order == null) {
      const preApply = plan.map(p => ({ id: p.row.id, plan: p.action, items: p.row.items, total: p.row.total, payment_terms: p.row.payment_terms ?? null, adjusted_at: p.row.adjusted_at ?? null, adjustment_request_id: p.row.adjustment_request_id ?? null, status: p.row.status ?? null,
        // Account-discount rows (0034): keep the pre-change breakdown too, so the audit can restore it.
        ...(rowDiscountPct(p.row) > 0 ? { subtotal: p.row.subtotal ?? null, discount_pct: p.row.discount_pct ?? null, discount_amount: p.row.discount_amount ?? null } : {}) }));
      // Its OWN conditional UPDATE: the first audit is never overwritten.
      const { data: a1, error: e1 } = await supabase
        .from('invoice_adjustment_requests')
        .update({ pre_apply_order: preApply })
        .eq('id', id).eq('status', 'pending').is('pre_apply_order', null)
        .select('id');
      if (e1) {
        console.warn("[approveAdjustmentRequest] Step 3a (pre_apply_order write) failed for id", id, ":", e1.message);
        throw new Error(e1.message);
      }
      if (!Array.isArray(a1) || a1.length !== 1) {
        // 0 rows: another admin audited first, or the request is no longer pending.
        const { data: again, error: againErr } = await supabase
          .from('invoice_adjustment_requests').select('status,pre_apply_order').eq('id', id).single();
        if (againErr || !again) {
          console.warn("[approveAdjustmentRequest] Step 3a re-read failed for id", id, ":", againErr?.message || "no row");
          throw new Error("Could not confirm the audit record for this request. Nothing was changed on the invoice. Try again.");
        }
        if (again.status !== 'pending') {
          const e = new Error(`This request was already ${again.status} by someone else.`);
          e.alreadyDecided = true;
          throw e;
        }
        if (again.pre_apply_order == null) throw new Error("Could not record the audit for this request. Nothing was changed on the invoice. Try again.");
        // already audited by the other admin: continue (their pre-change values stay)
      }
    }
    const { data: audited, error: auditErr } = await supabase
      .from('invoice_adjustment_requests')
      .update({ approved_changes: proposed, last_apply_error: null })
      .eq('id', id).eq('status', 'pending')
      .select('id');
    if (auditErr || !Array.isArray(audited) || audited.length !== 1) {
      console.warn("[approveAdjustmentRequest] Step 3b (audit write) failed for id", id, ":", auditErr?.message || "no pending row (already decided?)");
      const e = new Error(auditErr?.message ? auditErr.message : "This request was already decided by someone else.");
      if (!auditErr) e.alreadyDecided = true;
      throw e;
    }
  } else if (current.approved_changes == null) {
    // Finish path: record what is being finished, only if never recorded (audit only).
    const { error: acErr } = await supabase
      .from('invoice_adjustment_requests')
      .update({ approved_changes: proposed })
      .eq('id', id).eq('status', 'pending').is('approved_changes', null);
    if (acErr) console.warn("[approveAdjustmentRequest] finish path: could not record approved_changes on request", id, ":", acErr.message);
  }
  // 4 ── surgical CAS apply, newest row first
  const wantTerms = String(proposed.paymentTerms || "").trim();
  const finalRows = [];
  const skippedNotes = [];
  const applyRow = async (row, patch) => {
    try {
      // Compare-and-set: never a cancelled row; the marker must be what was
      // read or already ours; the total must be what was read. The (possibly
      // long) items text is added only when short enough for the URL.
      const markerOr = row.adjustment_request_id == null
        ? `adjustment_request_id.is.null,adjustment_request_id.eq.${id}`
        : `adjustment_request_id.eq.${row.adjustment_request_id},adjustment_request_id.eq.${id}`;
      let q = supabase.from('portal_orders').update(patch).eq('id', row.id).eq('total', row.total)
        .or('status.is.null,status.neq.cancelled').or(markerOr);
      if (String(row.items).length <= 2500) q = q.eq('items', row.items);
      const { data, error } = await q.select('*');
      if (error) {
        const err = /adjust(ed_at|ment_request_id)/i.test(String(error.message || ""))
          ? Object.assign(new Error("The portal_orders table is missing the adjustment columns. Run migration 0028_invoice_adjustment_requests.sql in Supabase, then try again. (" + error.message + ")"), { code: error.code })
          : error;
        return { kind: isDefinitiveDbError(error) ? 'refused' : 'unknown', error: err };
      }
      if (Array.isArray(data) && data.length === 1) return { kind: 'applied', row: data[0] };
      // 0 rows: the CAS refused (nothing written by this statement). Another
      // admin approving at once may have applied THIS request already.
      const again = await readOrderRow(row.id);
      if (again.row && rowAlreadyApplied(again.row, id, newItems, expectedNewTotal(row.items, row.total, newItems, rowDiscountPct(row)))) return { kind: 'applied', row: again.row };
      return { kind: 'refused', error: new Error(`Order row #${row.id} changed while applying (or was cancelled / not writable). Nothing further was changed.`) };
    } catch (e) {
      return { kind: 'unknown', error: e };
    }
  };
  for (let i = 0; i < plan.length; i++) {
    const { row, action, newTotal } = plan[i];
    const isNewest = i === 0;
    if (action !== 'apply') {
      if (action === 'skip-already-applied') finalRows.push(row);
      else skippedNotes.push(`row ${row.id}: ${action}`);
      continue;
    }
    const patch = { items: newItemsText, total: newTotal, adjusted_at: decidedAt, adjustment_request_id: String(id) };
    // Account-discount row (0034): keep the stored breakdown in step with the new total (the rate itself never changes).
    const rowPct = rowDiscountPct(row);
    if (rowPct > 0) {
      const newSub = adjItemsSum(newItems);
      patch.subtotal = newSub;
      patch.discount_amount = round2((newSub * rowPct) / 100);
    }
    if (wantTerms && wantTerms !== (row.payment_terms || "")) patch.payment_terms = wantTerms;
    state.invoiceTouched = true;   // a write may now be in flight
    let res = await applyRow(row, patch);
    if (res.kind === 'applied') { finalRows.push(res.row); continue; }
    const msg = String(res.error?.message || res.error || "Unknown error").slice(0, 500);
    console.warn("[approveAdjustmentRequest] Step 4 (apply to portal_orders) failed for request", id, "invoice", current.invoice_id, "row", row.id, ":", msg, res.kind === 'unknown' ? "(outcome unknown, re-reading)" : "");
    if (res.kind === 'unknown') {
      // The statement may have committed with its response lost: re-read.
      const v = await readOrderRow(row.id);
      if (v.row && rowAlreadyApplied(v.row, id, newItems, newTotal)) { finalRows.push(v.row); continue; }
      if (isNewest) {
        // Unknown outcome: even when the re-read shows the row unmarked, a timed-out
        // request can still commit afterwards, so NEVER claim nothing was written.
        // last_apply_error is deliberately NOT recorded here: leaving it null keeps
        // the guard-trigger predicate true (audit written, no conclusive failure), so
        // Reject stays refused while the invoice may be changing; Approve again is
        // always safe (idempotent per request id, finish path if it did land).
        const partial = v.row
          ? `The update of the invoice could not be confirmed (${msg}); it may or may not have been applied.`
          : `The invoice may already have been updated, but the update could not be confirmed (${msg}).`;
        throw new PartialApplyError(partial + " Press Approve again to check and finish.", partial);
      }
      // older duplicate with an unknown outcome: best-effort, never fails the approval
    }
    if (!isNewest) { skippedNotes.push(`row ${row.id}: apply failed (${msg})`); continue; } // older duplicates never fail the approval
    // Newest row, conclusively not written (it is the first write of the normal
    // path): nothing to roll back.
    state.invoiceTouched = false;
    const { error: recErr } = await supabase.from('invoice_adjustment_requests')
      .update({ last_apply_error: msg }).eq('id', id).eq('status', 'pending');
    if (recErr) console.warn("[approveAdjustmentRequest] could not record last_apply_error on request", id, ":", recErr.message);
    throw new Error(msg);
  }
  if (skippedNotes.length) console.warn("[approveAdjustmentRequest] request", id, "older duplicate rows not applied:", skippedNotes.join("; "));
  // 5 ── flip the request LAST (optimistic lock). Failure leaves it pending; the
  // apply above is idempotent so the admin simply presses Approve again (finish).
  let claimed = null;
  let claimErr = null;
  try {
    const res = await supabase
      .from('invoice_adjustment_requests')
      .update({ status: 'approved', decision_note: note || null, decided_by: decidedBy, decided_at: decidedAt, last_apply_error: skippedNotes.length ? `older duplicate rows not applied: ${skippedNotes.join("; ")}`.slice(0, 500) : null })
      .eq('id', id).eq('status', 'pending')
      .select().single();
    claimed = res.data; claimErr = res.error;
  } catch (e) { claimErr = e; }
  if (claimErr || !claimed) {
    const emsg = String(claimErr?.message || "the request is no longer pending").slice(0, 500);
    console.warn("[approveAdjustmentRequest] Step 5 (flip request to approved) failed for id", id, ":", emsg);
    // Someone else decided in the meantime?
    let latest = null;
    try {
      const { data: l } = await supabase.from('invoice_adjustment_requests').select('status').eq('id', id).single();
      latest = l;
    } catch (e) { console.warn("[approveAdjustmentRequest] re-read of request", id, "failed:", e?.message || e); }
    if (latest && latest.status && latest.status !== 'pending') {
      const e = new Error(`This request was already ${latest.status} by someone else. ${latest.status === 'approved' ? 'The invoice lines were already updated.' : 'Check the invoice: the lines may already be updated.'}`);
      e.alreadyDecided = true;
      throw e;
    }
    const partial = `The invoice lines were updated but the request could not be marked approved (${emsg}).`;
    try {
      await supabase.from('invoice_adjustment_requests').update({ last_apply_error: partial }).eq('id', id).eq('status', 'pending');
    } catch (e) { console.warn("[approveAdjustmentRequest] could not record last_apply_error on request", id, ":", e?.message || e); }
    throw new PartialApplyError(partial + " Press Approve again to finish.", partial);
  }
  const cache = readAdjRequestsCache();
  const idx = cache.findIndex(r => r.id === id);
  if (idx >= 0) { cache[idx] = claimed; writeAdjRequestsCache(cache); }
  return { request: claimed, rows: finalRows };
}


const ADJ_REJECT_BLOCKED_MSG = "Approval is in progress or already applied on the invoice: press Approve / Finish approval.";

/** Admin-facing: reject a pending adjustment request. Writes only the request
 *  row (never portal_orders). A note is required so the rep learns why.
 *  The database is the authority: the guard trigger in migration 0028 refuses a
 *  move to 'rejected' while an apply is in progress or applied (a marker on any
 *  portal_orders row, or pre_apply_order set with last_apply_error null). The
 *  checks here are only a fast path that produces a clear message; the trigger's
 *  refusal is mapped to the same message. Because approve writes its audit
 *  BEFORE any invoice write, a reject either commits first (approve then matches
 *  0 rows and aborts with alreadyDecided, nothing written) or is refused. */
export async function rejectAdjustmentRequest(id, { note, adminEmail, adminId } = {}) {
  if (!id) throw new Error("Request id is required.");
  if (!String(note || "").trim()) throw new Error("Please add a note explaining why this is rejected (the rep will see it).");
  // Fast path (same predicate as the trigger). A failed read is only logged: the
  // trigger still decides.
  try {
    const { data: cur, error: curErr } = await supabase
      .from('invoice_adjustment_requests').select('id,status,pre_apply_order,last_apply_error').eq('id', id).single();
    if (curErr) console.warn("[rejectAdjustmentRequest] pre-check read failed for id", id, ":", curErr.message, "- relying on the database guard.");
    else if (cur && cur.status && cur.status !== 'pending') {
      const e = new Error("This request was already " + cur.status + ".");
      e.alreadyDecided = true;
      throw e;
    } else if (applyInProgressByFields(cur)) throw new Error(ADJ_REJECT_BLOCKED_MSG);
    if (await isAdjustmentRequestApplied(id)) throw new Error(ADJ_REJECT_BLOCKED_MSG);
  } catch (e) {
    if (e && (e.message === ADJ_REJECT_BLOCKED_MSG || e.alreadyDecided)) throw e;
    console.warn("[rejectAdjustmentRequest] pre-check failed for request", id, ":", e?.message || e, "- relying on the database guard.");
  }
  const { data: rows, error } = await supabase
    .from('invoice_adjustment_requests')
    .update({ status: 'rejected', decision_note: String(note).trim(), decided_by: adminEmail || adminId || null, decided_at: new Date().toISOString() })
    .eq('id', id).eq('status', 'pending')
    .select();
  if (error) {
    console.warn("[rejectAdjustmentRequest] Supabase UPDATE failed for id", id, ":", error.message);
    if (/in progress or already applied/i.test(String(error.message || ""))) throw new Error(ADJ_REJECT_BLOCKED_MSG);
    throw new Error(error.message || "Reject failed.");
  }
  if (!Array.isArray(rows) || rows.length !== 1) return throwAlreadyDecided(id, "rejectAdjustmentRequest");
  const data = rows[0];
  const cache = readAdjRequestsCache();
  const idx = cache.findIndex(r => r.id === id);
  if (idx >= 0 && data) { cache[idx] = data; writeAdjRequestsCache(cache); }
  return data;
}

// ─── Training videos (migration 0023 — Rule Zero) ────────────────────────────
// Migration 0023 introduces public.training_videos. RLS: SELECT open to every
// authenticated user (client filters to show_to_customers / show_to_reps);
// INSERT / UPDATE / DELETE admin-only.
//
// Before this migration:
//   • adminVideos state persisted only to per-browser localStorage
//   • CustomerTraining hardcoded to ADMIN_TRAINING_STUB (never saw uploads)
//   • Rep TrainingHub had no persistence (INIT_TRAINING reset on refresh)
// After: every role reads from Supabase; admin writes via helpers below.
// localStorage stays as a defensive offline-fallback cache — never
// authoritative, never hides the "empty Supabase" state.

const LS_TRAINING_VIDEOS = "mpcare_training_videos_cache";

function readTrainingVideosCache() {
  try {
    const raw = localStorage.getItem(LS_TRAINING_VIDEOS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn("[readTrainingVideosCache] parse failed:", e?.message || e);
    return [];
  }
}

function writeTrainingVideosCache(rows) {
  try {
    localStorage.setItem(LS_TRAINING_VIDEOS, JSON.stringify(rows || []));
  } catch (e) {
    console.warn("[writeTrainingVideosCache] localStorage write failed:", e?.message || e);
  }
}

function genTrainingClientId() {
  return `training-video-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Map a Supabase row (snake_case) → the client-side shape the existing
// admin / rep / customer UIs already consume. Keeps the migration
// pluggable into current render code without a UI-wide rename.
function trainingRowToClient(r) {
  if (!r) return null;
  // questions is a JSONB column (migration 0025). May arrive as an object
  // (default) or a JSON string depending on how the Supabase client parses
  // it. Normalize to an array either way; empty array on any parse issue.
  let questions = [];
  if (Array.isArray(r.questions)) questions = r.questions;
  else if (typeof r.questions === 'string') {
    try { const parsed = JSON.parse(r.questions); if (Array.isArray(parsed)) questions = parsed; }
    catch (e) { /* fall through to empty */ }
  }
  return {
    id:              r.id,
    title:           r.title || "",
    category:        r.category || "product",
    duration:        r.duration || "",
    required:        !!r.required,
    videoUrl:        r.video_url || "",
    videoType:       r.video_type || "youtube",
    thumbnail:       r.thumbnail || "🎬",
    thumbnailUrl:    r.thumbnail_url || "",
    desc:            r.description || "",
    uploadedBy:      r.uploaded_by || "",
    date:            r.date_str || "",
    showToCustomers: !!r.show_to_customers,
    showToReps:      r.show_to_reps !== false,      // default true
    active:          r.active !== false,             // default true
    questions,                                        // quiz for certification (migration 0025)
    clientId:        r.client_id || null,
    _createdAt:      r.created_at,
    _updatedAt:      r.updated_at,
  };
}

// Inverse: client shape → Supabase snake_case payload for insert/update.
// questions is only INCLUDED in the payload when the caller explicitly
// provides it — otherwise the existing DB value is preserved (a
// video-metadata edit that doesn't touch the quiz shouldn't clobber it).
function trainingClientToRow(v) {
  if (!v) return null;
  const row = {
    title:             v.title || "",
    category:          v.category || "product",
    duration:          v.duration || "",
    required:          !!v.required,
    video_url:         v.videoUrl || "",
    video_type:        v.videoType || "youtube",
    thumbnail:         v.thumbnail || "🎬",
    thumbnail_url:     v.thumbnailUrl || "",
    description:       v.desc || v.description || "",
    uploaded_by:       v.uploadedBy || "",
    date_str:          v.date || "",
    show_to_customers: !!v.showToCustomers,
    show_to_reps:      v.showToReps !== false,
    active:            v.active !== false,
  };
  // Only write questions when the caller supplied them, so metadata-only
  // saves preserve the existing quiz in the DB (avoids the same class of
  // bug where an edit path silently reset unrelated fields).
  if (Array.isArray(v.questions)) row.questions = v.questions;
  return row;
}

/** Fetch training videos. Supabase-first; on failure falls back to the
 *  localStorage cache (read-only). Returned rows are in the client shape
 *  the existing UI code already consumes.
 *
 *  @param {object} [filters]
 *  @param {boolean} [filters.customerVisibleOnly] filter to show_to_customers=true + active=true
 *  @param {boolean} [filters.repVisibleOnly]      filter to show_to_reps=true + active=true
 *  @param {boolean} [filters.includeInactive]     admin — include active=false rows
 *  @param {number}  [filters.limit]
 */
export async function listTrainingVideos({ customerVisibleOnly, repVisibleOnly, includeInactive, limit } = {}) {
  try {
    let q = supabase.from('training_videos').select('*').order('created_at', { ascending: false });
    if (customerVisibleOnly) q = q.eq('show_to_customers', true).eq('active', true);
    if (repVisibleOnly)      q = q.eq('show_to_reps',      true).eq('active', true);
    if (!includeInactive && !customerVisibleOnly && !repVisibleOnly) {
      // Default behavior for admin-scope: return everything, active + inactive.
      // (No filter added.)
    }
    if (limit) q = q.limit(limit);
    const { data, error } = await q;
    if (error) {
      console.warn("[listTrainingVideos] Supabase SELECT failed:", error.message, "— falling back to localStorage cache.");
      throw error;
    }
    if (data) {
      const mapped = data.map(trainingRowToClient).filter(Boolean);
      writeTrainingVideosCache(mapped);
      return mapped;
    }
  } catch (e) {
    console.warn("[listTrainingVideos] using localStorage fallback:", e?.message || e);
  }
  let rows = readTrainingVideosCache();
  if (customerVisibleOnly) rows = rows.filter(v => v.showToCustomers && v.active !== false);
  if (repVisibleOnly)      rows = rows.filter(v => v.showToReps !== false && v.active !== false);
  if (limit)               rows = rows.slice(0, limit);
  return rows;
}

/** Admin-only: create OR update a training video. If `id` is present on the
 *  payload the helper UPDATEs that row; otherwise INSERTs. RLS enforces the
 *  admin-only write. Returns the resulting client-shaped row.
 *
 *  Never DELETEs or overwrites data belonging to a different row — the
 *  UPDATE is scoped to `eq('id', v.id)` and only writes the fields present
 *  on the payload.
 */
export async function upsertTrainingVideo(v) {
  if (!v || !v.title) throw new Error("Training video requires at least a title.");
  const payload = trainingClientToRow(v);
  if (v.id) {
    const { data, error } = await supabase
      .from('training_videos')
      .update(payload)
      .eq('id', v.id)
      .select()
      .single();
    if (error) {
      console.warn("[upsertTrainingVideo] Supabase UPDATE failed for id", v.id, ":", error.message);
      throw error;
    }
    const client = trainingRowToClient(data);
    const cache = readTrainingVideosCache();
    const idx = cache.findIndex(x => x.id === client.id);
    if (idx >= 0) cache[idx] = client; else cache.unshift(client);
    writeTrainingVideosCache(cache);
    return client;
  }
  // INSERT path — attach a client_id for idempotent identification
  const insertPayload = { ...payload, client_id: v.clientId || genTrainingClientId() };
  const { data, error } = await supabase
    .from('training_videos')
    .insert(insertPayload)
    .select()
    .single();
  if (error) {
    console.warn("[upsertTrainingVideo] Supabase INSERT failed:", error.message, "payload:", insertPayload);
    throw error;
  }
  const client = trainingRowToClient(data);
  const cache = readTrainingVideosCache();
  cache.unshift(client);
  writeTrainingVideosCache(cache);
  return client;
}

/** Admin-only: delete a training video by id. RLS enforces admin-only.
 *  Note: this is a hard delete — the row is gone. If audit history is
 *  needed later, switch to `active=false` (soft delete via
 *  upsertTrainingVideo) instead. For now we mirror the pre-migration
 *  behavior where the admin's Remove button dropped the row entirely.
 */
export async function deleteTrainingVideo(id) {
  if (!id) throw new Error("Training video id is required.");
  const { error } = await supabase
    .from('training_videos')
    .delete()
    .eq('id', id);
  if (error) {
    console.warn("[deleteTrainingVideo] Supabase DELETE failed for id", id, ":", error.message);
    throw error;
  }
  const cache = readTrainingVideosCache();
  writeTrainingVideosCache(cache.filter(x => x.id !== id));
  return true;
}

/** Realtime subscription. Per-call random channel name so multiple
 *  subscribers across admin / rep / customer don't collide (PR #85
 *  pattern).
 */
export function subscribeTrainingVideos(callback) {
  if (typeof callback !== 'function') return () => {};
  const channelName = 'training-videos-live-' + Math.random().toString(36).slice(2, 10);
  let channel;
  try {
    channel = supabase.channel(channelName)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'training_videos' },
        payload => {
          try {
            callback({
              event: payload.eventType,
              row: payload.new ? trainingRowToClient(payload.new) : (payload.old ? trainingRowToClient(payload.old) : null)
            });
          } catch (e) {
            console.warn("[subscribeTrainingVideos] callback threw:", e?.message || e);
          }
        })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribeTrainingVideos] channel status:", status,
            "— realtime unavailable; videos will sync on next page load.");
        }
      });
  } catch (e) {
    console.warn("[subscribeTrainingVideos] failed to open channel:", e?.message || e);
    return () => {};
  }
  return () => {
    try { supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribeTrainingVideos] removeChannel failed:", e?.message || e); }
  };
}

// ─── Rep training progress + certifications (migration 0026) ────────────────
// Fixes: TrainingHub's "watched" state and earned certificate previously
// lived in local React state only — wiped on every refresh, and on every
// subscribeTrainingVideos refetch (any admin edit to any video anywhere
// replaced the whole `videos` array, which has no `watched` field to
// restore). See migration 0026 header for the full write-up.

/** All progress rows for one rep. Returns [] on any failure — TrainingHub
 *  falls back to "nothing completed yet" rather than blocking the page. */
export async function listRepTrainingProgress(repId) {
  if (!repId) return [];
  try {
    const { data, error } = await supabase
      .from('rep_training_progress')
      .select('*')
      .eq('rep_id', String(repId));
    if (error) {
      console.warn("[listRepTrainingProgress] fetch failed:", error.message);
      return [];
    }
    return data || [];
  } catch (e) {
    console.warn("[listRepTrainingProgress] threw:", e?.message || e);
    return [];
  }
}

/** Idempotent upsert of one (rep, video) progress row. Conflict target is
 *  (rep_id, video_id) — the UNIQUE constraint from migration 0026. Safe to
 *  call again on retake; a passing retake overwrites a prior failing score. */
export async function upsertRepTrainingProgress({ repId, repName, videoId, watched, passed, quizScore }) {
  if (!repId || !videoId) {
    console.warn("[upsertRepTrainingProgress] missing repId or videoId — refusing write");
    return null;
  }
  const payload = {
    rep_id: String(repId),
    rep_name: repName || null,
    video_id: videoId,
    watched: !!watched,
    passed: !!passed,
    quiz_score: (quizScore === undefined || quizScore === null) ? null : Number(quizScore),
    completed_at: passed ? new Date().toISOString() : null,
    updated_at: new Date().toISOString(),
  };
  try {
    const { data, error } = await supabase
      .from('rep_training_progress')
      .upsert(payload, { onConflict: 'rep_id,video_id' })
      .select()
      .single();
    if (error) {
      console.warn("[upsertRepTrainingProgress] upsert failed for video", videoId, ":", error.message);
      return null;
    }
    return data;
  } catch (e) {
    console.warn("[upsertRepTrainingProgress] threw for video", videoId, ":", e?.message || e);
    return null;
  }
}

export function subscribeRepTrainingProgress(repId, onChange) {
  if (typeof onChange !== 'function' || !repId) return () => {};
  let channel;
  try {
    channel = supabase
      .channel(`rep_training_progress_${repId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'rep_training_progress', filter: `rep_id=eq.${String(repId)}` },
        payload => {
          try { onChange({ event: payload.eventType, row: payload.new || payload.old }); }
          catch (e) { console.warn("[subscribeRepTrainingProgress] callback threw:", e?.message || e); }
        })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribeRepTrainingProgress] channel status:", status, "— realtime may be disabled.");
        }
      });
  } catch (e) {
    console.warn("[subscribeRepTrainingProgress] failed to open channel:", e?.message || e);
    return () => {};
  }
  return () => {
    try { supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribeRepTrainingProgress] removeChannel failed:", e?.message || e); }
  };
}

/** The rep's own earned certificate, or null if not yet earned. At most one
 *  row per rep (UNIQUE(rep_id) in migration 0026). */
export async function getRepCertification(repId) {
  if (!repId) return null;
  try {
    const { data, error } = await supabase
      .from('rep_certifications')
      .select('*')
      .eq('rep_id', String(repId))
      .maybeSingle();
    if (error) {
      console.warn("[getRepCertification] fetch failed:", error.message);
      return null;
    }
    return data || null;
  } catch (e) {
    console.warn("[getRepCertification] threw:", e?.message || e);
    return null;
  }
}

/** Insert-once. Deliberately NOT an upsert — a rep's certificate, once
 *  earned, is a frozen snapshot (see migration 0026 comment: adding a new
 *  required video later must not retroactively touch an already-issued
 *  cert). UNIQUE(rep_id) makes a duplicate insert a no-op: on a 23505
 *  conflict (e.g. two tabs racing to certify at once) we just re-fetch and
 *  return the row that won, rather than surfacing an error. */
export async function insertRepCertification({ repId, repName, certNum, score, completedVideos }) {
  if (!repId || !certNum) {
    console.warn("[insertRepCertification] missing repId or certNum — refusing write");
    return null;
  }
  const payload = {
    rep_id: String(repId),
    rep_name: repName || null,
    cert_num: certNum,
    score: (score === undefined || score === null) ? null : Number(score),
    completed_videos: completedVideos || [],
  };
  try {
    const { data, error } = await supabase
      .from('rep_certifications')
      .insert(payload)
      .select()
      .single();
    if (error) {
      if (error.code === '23505') {
        // Another tab/device already issued this rep's certificate — fetch
        // and return the row that won the race instead of erroring.
        return await getRepCertification(repId);
      }
      console.warn("[insertRepCertification] insert failed:", error.message);
      return null;
    }
    return data;
  } catch (e) {
    console.warn("[insertRepCertification] threw:", e?.message || e);
    return null;
  }
}

export function subscribeRepCertifications(repId, onChange) {
  if (typeof onChange !== 'function' || !repId) return () => {};
  let channel;
  try {
    channel = supabase
      .channel(`rep_certifications_${repId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'rep_certifications', filter: `rep_id=eq.${String(repId)}` },
        payload => {
          try { onChange({ event: payload.eventType, row: payload.new || payload.old }); }
          catch (e) { console.warn("[subscribeRepCertifications] callback threw:", e?.message || e); }
        })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribeRepCertifications] channel status:", status, "— realtime may be disabled.");
        }
      });
  } catch (e) {
    console.warn("[subscribeRepCertifications] failed to open channel:", e?.message || e);
    return () => {};
  }
  return () => {
    try { supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribeRepCertifications] removeChannel failed:", e?.message || e); }
  };
}

// ─── Admin alert log — cross-device dedupe (migration 0024) ─────────────────
// public.admin_alert_log stores one row per (invoice_id, alert_type) with a
// UNIQUE constraint on that pair. Race-safe atomic upsert with ON CONFLICT
// DO NOTHING decides the winner admin that sends the email — every other
// admin session sees the already-existing row and skips.
//
// Fixes the pre-migration bug where the per-browser localStorage dedupe
// (`mpcare_overdue_alerts`, `mpcare_reminded_invoices`) got out of sync
// across devices — admin signing in on a new browser or after cache
// clear re-fired every outstanding overdue email (user reported 10
// duplicates in one session).

/** Fetch every row from admin_alert_log. RLS is admin-only so a rep or
 *  customer call returns an error → empty array. `since` is an optional
 *  ISO date to limit the fetch to recent entries (defaults to all).
 */
export async function listAdminAlertLog({ alertType, since } = {}) {
  try {
    let q = supabase
      .from('admin_alert_log')
      .select('invoice_id, alert_type, sent_at, sent_by')
      .order('sent_at', { ascending: false });
    if (alertType) q = q.eq('alert_type', alertType);
    if (since)     q = q.gte('sent_at', since);
    const { data, error } = await q;
    if (error) {
      console.warn("[listAdminAlertLog] Supabase SELECT failed:", error.message);
      return [];
    }
    return data || [];
  } catch (e) {
    console.warn("[listAdminAlertLog] threw:", e?.message || e);
    return [];
  }
}

/** Attempt to claim an admin alert send. Returns TRUE if we won the race
 *  (this admin should send the email) or FALSE if another admin (or a
 *  previous session) already sent it (skip).
 *
 *  Implementation: upsert on the UNIQUE (invoice_id, alert_type) pair
 *  with `ignoreDuplicates: true` — the ON CONFLICT DO NOTHING is atomic
 *  at the database level, so two concurrent admins claiming the same
 *  alert result in ONE insert (the winner) and ONE no-op (the loser).
 *  Only the winner's call returns a non-empty data array.
 *
 *  Fail-safe: any error (network / RLS / anything) returns false so the
 *  caller does NOT send the email. Better to under-send once than to
 *  double-send.
 *
 *  @param {object} args
 *  @param {string} args.invoiceId   the invoice's user-facing id (e.g. "INV-F290")
 *  @param {string} args.alertType   'overdue' | 'due_tomorrow' | future types
 *  @param {string} [args.sentBy]    admin email/id for audit
 *  @returns {Promise<boolean>}      true = go ahead and send; false = skip
 */
export async function claimAdminAlert({ invoiceId, alertType, sentBy } = {}) {
  if (!invoiceId || !alertType) {
    console.warn("[claimAdminAlert] missing invoiceId or alertType — skipping send");
    return false;
  }
  try {
    const { data, error } = await supabase
      .from('admin_alert_log')
      .upsert(
        { invoice_id: invoiceId, alert_type: alertType, sent_by: sentBy || "" },
        { onConflict: 'invoice_id,alert_type', ignoreDuplicates: true }
      )
      .select();
    if (error) {
      console.warn(
        "[claimAdminAlert] Supabase upsert failed for", invoiceId, alertType, ":",
        error.message, "— skipping send to avoid duplicates"
      );
      return false;
    }
    // data non-empty → INSERT happened → we won → send
    // data empty      → ON CONFLICT DO NOTHING fired → another admin already
    //                   sent it → skip
    return Array.isArray(data) && data.length > 0;
  } catch (e) {
    console.warn(
      "[claimAdminAlert] threw for", invoiceId, alertType, ":", e?.message || e,
      "— skipping send"
    );
    return false;
  }
}

// ─── Rep route customers (Phase A — migrated from SHARED_REP_CUSTOMERS) ─────
// Supabase table `rep_route_customers` stores one row per (rep × customer).
// Schema lives in supabase/migrations/0013_rep_route_customers.sql. This is
// the CLAUDE.md §0 (Rule Zero) migration for the rep portal's customer list:
// admin-assigned dealers and rep-manually-added route customers both live
// here, keyed by `customer_key` (dealer's profiles.id for live accounts, or
// a rep-generated `local-<id>` for walk-ins without a login).
//
// Un-assign is a soft delete (active=false) so history is preserved.

/** List this rep's route customers (active only). repId must be the rep's
 *  profiles.id (UUID) or demo int id as text — whichever matches how rows
 *  were inserted. Returns [] on any read error so the UI can fall back to
 *  localStorage without throwing. */
export async function listRepRouteCustomers(repId) {
  if (!repId) return [];
  try {
    const { data, error } = await supabase
      .from('rep_route_customers')
      .select('*')
      .eq('rep_id', String(repId))
      .eq('active', true)
      .order('updated_at', { ascending: false });
    if (error) {
      console.warn("[listRepRouteCustomers] fetch failed:", error.message);
      return [];
    }
    return data || [];
  } catch(e) {
    console.warn("[listRepRouteCustomers] threw:", e?.message || e);
    return [];
  }
}

/** Idempotent upsert of a single rep_route_customers row. Awaited.
 *  Returns the inserted/updated row, or null on failure.
 *  Conflict target is (rep_id, customer_key) — the UNIQUE constraint from
 *  migration 0013. This lets admin assign a dealer and then later re-assign
 *  without creating duplicates. */
export async function upsertRepRouteCustomer({
  repId, repName, customerKey, customerData, isProspect, source, active
}) {
  if (!repId || !customerKey) {
    console.warn("[upsertRepRouteCustomer] missing repId or customerKey — refusing write");
    return null;
  }
  const payload = {
    rep_id: String(repId),
    rep_name: String(repName || ""),
    customer_key: String(customerKey),
    customer_data: customerData || {},
    is_prospect: !!isProspect,
    source: source || 'rep_manual',
    active: active !== false,  // default true
    updated_at: new Date().toISOString(),
  };
  try {
    const { data, error } = await supabase
      .from('rep_route_customers')
      .upsert(payload, { onConflict: 'rep_id,customer_key' })
      .select()
      .single();
    if (error) {
      console.warn("[upsertRepRouteCustomer] upsert failed for", customerKey, ":", error.message);
      return null;
    }
    return data;
  } catch(e) {
    console.warn("[upsertRepRouteCustomer] threw for", customerKey, ":", e?.message || e);
    return null;
  }
}

/** Soft-delete: flip active=false on a (rep_id, customer_key) row. UPDATE
 *  only; never DELETE, so history and audit trail are preserved. Returns
 *  the updated row, or null if the row doesn't exist or the update failed. */
export async function deactivateRepRouteCustomer(repId, customerKey, opts) {
  const strict = !!(opts && opts.strict);
  if (!repId || !customerKey) {
    console.warn("[deactivateRepRouteCustomer] missing repId or customerKey — refusing write");
    if (strict) throw new Error("deactivateRepRouteCustomer: missing repId or customerKey");
    return null;
  }
  try {
    const { data, error } = await supabase
      .from('rep_route_customers')
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('rep_id', String(repId))
      .eq('customer_key', String(customerKey))
      .select()
      .maybeSingle();
    if (error) {
      console.warn("[deactivateRepRouteCustomer] failed for", customerKey, ":", error.message);
      if (strict) throw new Error("Could not deactivate the old rep's Route Planner row: " + error.message);
      return null;
    }
    return data || null;
  } catch(e) {
    console.warn("[deactivateRepRouteCustomer] threw for", customerKey, ":", e?.message || e);
    if (strict) throw e instanceof Error ? e : new Error(String(e));
    return null;
  }
}

/** STRICT variant for callers that must know whether the old rep's row was really deactivated.
 *  PostgREST returns NO error and zero rows when RLS silently blocks an UPDATE, so "update returned
 *  nothing" alone cannot mean "no row existed". This helper therefore SELECTs the active row
 *  (rep_id, customer_key, active=true) first:
 *    - no active row  -> { ok: true, existed: false } (nothing to do, nothing written)
 *    - active row, update flips it -> { ok: true, existed: true, row }
 *    - active row, update returns nothing / leaves it active -> THROWS (RLS denial or concurrent change)
 *  It also THROWS on any read or update error. Only ever sets active=false; never DELETEs.
 *  ASSUMPTION (unverified against production RLS): the admin can SELECT rep_route_customers. If a
 *  policy hid the row from the admin's SELECT too, the pre-read would see nothing and this helper
 *  would report existed:false. */
export async function deactivateRepRouteCustomerStrict(repId, customerKey) {
  if (!repId || !customerKey) throw new Error("deactivateRepRouteCustomerStrict: missing repId or customerKey");
  const activeRow = async (stage) => {
    const { data, error } = await supabase
      .from('rep_route_customers')
      .select('*')
      .eq('rep_id', String(repId))
      .eq('customer_key', String(customerKey))
      .eq('active', true)
      .maybeSingle();
    if (error) {
      console.warn("[deactivateRepRouteCustomerStrict] " + stage + " read failed for", customerKey, ":", error.message);
      throw new Error("Could not check the old rep's Route Planner row: " + error.message);
    }
    return data || null;
  };
  const before = await activeRow("pre-update");
  if (!before) return { ok: true, existed: false };
  const row = await deactivateRepRouteCustomer(repId, customerKey, { strict: true });
  if (row && row.active === false) return { ok: true, existed: true, row };
  const after = await activeRow("post-update");
  if (after || row) {
    console.warn("[deactivateRepRouteCustomerStrict] active row was NOT deactivated for", customerKey, "(update returned", row ? "a still-active row" : "no row", ")");
    throw new Error("The old rep's Route Planner row is still active: the database did not apply the change (a permission rule or a concurrent change). Nothing was removed.");
  }
  return { ok: true, existed: true, row: null };   // someone else deactivated it between our read and write
}

/** Realtime subscription for a rep's own rows. Admin's assign action
 *  propagates to the rep's open tab within seconds. Gracefully no-ops if
 *  realtime is disabled on the project — the 60-second re-poll the rep
 *  portal already runs keeps the view consistent. */
export function subscribeRepRouteCustomers(repId, onChange) {
  if (typeof onChange !== 'function') return () => {};
  if (!repId) return () => {};
  let channel;
  try {
    channel = supabase
      .channel(`rep_route_customers_${repId}`)
      .on('postgres_changes',
          { event: '*', schema: 'public', table: 'rep_route_customers', filter: `rep_id=eq.${String(repId)}` },
          payload => {
            try { onChange({ event: payload.eventType, row: payload.new || payload.old }); }
            catch(e) { console.warn("[subscribeRepRouteCustomers] callback threw:", e?.message || e); }
          })
      .subscribe(status => {
        if(status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribeRepRouteCustomers] channel status:", status, "— realtime may be disabled; 60s re-poll still keeps view consistent.");
        }
      });
  } catch(e) {
    console.warn("[subscribeRepRouteCustomers] failed to open channel:", e?.message || e);
    return () => {};
  }
  return () => {
    try { supabase.removeChannel(channel); }
    catch(e) { console.warn("[subscribeRepRouteCustomers] removeChannel failed:", e?.message || e); }
  };
}

/** Admin-side helper. Fetches every active rep_route_customers row in one
 *  round-trip. Closes the Phase A gap where the admin's view of "rep's
 *  customers" only ever read this admin browser's localStorage cache,
 *  which was empty for any rep whose route-planner edits happened on a
 *  different device. With this helper the admin's mount populates
 *  SHARED_REP_CUSTOMERS from Supabase truth so the Reps tab counts and
 *  detail lists match what every rep actually has saved.
 *
 *  RLS auto-narrows results: admin (`is_admin()`) sees every rep's rows
 *  per migration 0013's admin_all policy; a rep sees just their own
 *  rows; a customer sees nothing. So calling this for any signed-in
 *  user is safe — the response shape is identical, just the row count
 *  differs.
 *
 *  Returns [] on any read error so the admin UI can fall back to the
 *  existing localStorage cache without throwing. */
export async function listAllRepRouteCustomers() {
  try {
    const { data, error } = await supabase
      .from('rep_route_customers')
      .select('*')
      .eq('active', true)
      .order('updated_at', { ascending: false });
    if (error) {
      console.warn("[listAllRepRouteCustomers] fetch failed:", error.message);
      return [];
    }
    return data || [];
  } catch (e) {
    console.warn("[listAllRepRouteCustomers] threw:", e?.message || e);
    return [];
  }
}

/** Admin-wide realtime subscription on the rep_route_customers table.
 *  Fires onChange for every INSERT / UPDATE / DELETE so the admin's Reps
 *  tab can re-fetch and re-broadcast via SHARED_REP_CUSTOMERS within
 *  seconds of any rep's add / edit / soft-delete. The per-rep variant
 *  above (subscribeRepRouteCustomers) is for the rep's own portal; this
 *  one is unscoped because the admin needs to see changes from every rep.
 *
 *  RLS narrows the events that reach the client: admin sees every change,
 *  reps see only their own, customers see nothing. So the same channel
 *  is safe for any caller — non-admin callers just receive a smaller
 *  stream of events.
 *
 *  Gracefully no-ops if realtime is disabled — admin's Reps tab will
 *  refresh on next page load via the on-mount hydrate path instead.
 *
 *  @param {(payload:any)=>void} onChange
 *  @returns {()=>void} unsubscribe */
export function subscribeAllRepRouteCustomers(onChange) {
  if (typeof onChange !== 'function') return () => {};
  let channel;
  try {
    channel = supabase
      .channel('rep_route_customers_admin_realtime')
      .on('postgres_changes',
          { event: '*', schema: 'public', table: 'rep_route_customers' },
          payload => {
            try { onChange(payload); }
            catch (e) { console.warn("[subscribeAllRepRouteCustomers] handler threw:", e?.message || e); }
          })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribeAllRepRouteCustomers] channel status:", status,
            "— realtime unavailable; admin Reps tab will hydrate on next page load instead.");
        }
      });
  } catch (e) {
    console.warn("[subscribeAllRepRouteCustomers] setup failed:", e?.message || e);
    return () => {};
  }
  return () => {
    try { if (channel) supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribeAllRepRouteCustomers] removeChannel failed:", e?.message || e); }
  };
}

// ─── Shop visits (Phase B — migrated from SHARED_VISITS) ───────────────────
// Supabase table `shop_visits` stores one row per rep visit. Schema pre-dates
// this repo; migration 0014 adds the additive columns this layer needs:
//   rep_name, visit_type, duration_minutes, active (soft-delete), updated_at,
//   client_id (TEXT, partial-unique on non-null — exact-key dedupe for
//   backfill idempotency).
//
// Column-name mapping between SHARED_VISITS entries and shop_visits rows is
// handled here — client code passes friendly JS-side names, helpers write the
// DB column names. Existing columns (shop_name, notes, visited_at, status)
// kept as-is to avoid destructive renames.
//
// Removal is soft: deactivateShopVisit flips active=false; rows are NEVER
// deleted. listShopVisits filters to active=true.

/** List this rep's active visits. Returns newest first. repId must match
 *  the rep's profiles.id (UUID) — RLS policy reps_own_visits enforces
 *  auth.uid() = rep_id, so passing the wrong id returns an empty set.
 *  Returns [] on any read error so the caller can fall back to localStorage
 *  without throwing. When repId is null/falsy (admin's session on behalf of
 *  all reps), returns every row the caller's RLS lets them see. */
export async function listShopVisits(repId) {
  try {
    let q = supabase
      .from('shop_visits')
      .select('*')
      .eq('active', true)
      .order('visited_at', { ascending: false });
    if (repId) q = q.eq('rep_id', String(repId));
    const { data, error } = await q;
    if (error) {
      console.warn("[listShopVisits] fetch failed:", error.message);
      return [];
    }
    return data || [];
  } catch(e) {
    console.warn("[listShopVisits] threw:", e?.message || e);
    return [];
  }
}

/** Idempotent insert of a single shop_visits row. Awaited. Returns the
 *  inserted row on success, null on failure. client_id MUST be provided;
 *  the partial unique index `shop_visits_client_id_unique` dedupes on it so
 *  re-running the backfill after a partial failure is a no-op on already-
 *  landed rows.
 *
 *  Input uses JS-side names (company, note, date, type, duration, repName);
 *  helper maps to DB columns (shop_name, notes, visited_at, visit_type,
 *  duration_minutes, rep_name) so callers don't have to think about schema
 *  drift between localStorage shape and DB shape.
 *
 *  This is INSERT-with-conflict-check, NOT UPSERT-updating. If a row with
 *  the same client_id already exists we LEAVE IT ALONE — the existing row
 *  is the authoritative version. A future "edit visit" UI would need a
 *  separate updateShopVisit helper that explicitly targets an id. */
export async function upsertShopVisit({
  clientId, repId, repName, company, note, date, type, duration, status, address
}) {
  if (!clientId) {
    console.warn("[upsertShopVisit] missing clientId — refusing write (dedupe would be impossible)");
    return null;
  }
  // Parse the date — SHARED_VISITS stores a locale date string like
  // "Apr 15, 2025" OR an ISO timestamp. Fall back to now() if unparseable.
  let visitedAtIso;
  try {
    const d = date ? new Date(date) : new Date();
    visitedAtIso = isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
  } catch { visitedAtIso = new Date().toISOString(); }

  const payload = {
    client_id: String(clientId),
    rep_id: repId ? String(repId) : null,
    rep_name: String(repName || ""),
    shop_name: String(company || ""),
    address: address || null,
    notes: note || "",
    visit_type: type || 'shop_visit',
    duration_minutes: Number.isFinite(Number(duration)) ? Number(duration) : 30,
    visited_at: visitedAtIso,
    status: status || 'completed',
    active: true,
    updated_at: new Date().toISOString(),
  };
  try {
    // We use upsert onConflict: client_id so the partial unique index is
    // the enforcement point. ignoreDuplicates=true leaves existing rows
    // untouched — we never want to update a visit silently on a backfill
    // retry. The caller can check the returned row: non-null means INSERT
    // succeeded, null means the client_id already existed (also success,
    // just a no-op).
    const { data, error } = await supabase
      .from('shop_visits')
      .upsert(payload, { onConflict: 'client_id', ignoreDuplicates: true })
      .select()
      .maybeSingle();
    if (error) {
      console.warn("[upsertShopVisit] failed for client_id", clientId, ":", error.message);
      return null;
    }
    return data || null;
  } catch(e) {
    console.warn("[upsertShopVisit] threw for client_id", clientId, ":", e?.message || e);
    return null;
  }
}

/** Soft-delete: flip active=false on a shop_visits row by its uuid id.
 *  UPDATE only; never DELETE. Returns the updated row, or null if the
 *  row doesn't exist. Reserved for a future "remove visit" UI — Phase B
 *  does not wire a UI action for this, but the helper exists so downstream
 *  features can soft-delete without reinventing the pattern. */
export async function deactivateShopVisit(id) {
  if (!id) {
    console.warn("[deactivateShopVisit] missing id — refusing write");
    return null;
  }
  try {
    const { data, error } = await supabase
      .from('shop_visits')
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('id', String(id))
      .select()
      .maybeSingle();
    if (error) {
      console.warn("[deactivateShopVisit] failed for", id, ":", error.message);
      return null;
    }
    return data || null;
  } catch(e) {
    console.warn("[deactivateShopVisit] threw for", id, ":", e?.message || e);
    return null;
  }
}

/** Realtime subscription for shop_visits. If repId is provided, filter to
 *  that rep's rows only (for the rep portal). Admin passes null to receive
 *  every row's INSERT/UPDATE. Gracefully no-ops if realtime is disabled on
 *  the project — the mount-time listShopVisits fetch still keeps the view
 *  consistent. */
export function subscribeShopVisits(repId, onChange) {
  if (typeof onChange !== 'function') return () => {};
  let channel;
  try {
    const chanName = repId ? `shop_visits_${repId}` : 'shop_visits_admin';
    const filter = repId ? `rep_id=eq.${String(repId)}` : undefined;
    const config = { event: '*', schema: 'public', table: 'shop_visits' };
    if (filter) config.filter = filter;
    channel = supabase
      .channel(chanName)
      .on('postgres_changes', config, payload => {
        try { onChange({ event: payload.eventType, row: payload.new || payload.old }); }
        catch(e) { console.warn("[subscribeShopVisits] callback threw:", e?.message || e); }
      })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribeShopVisits] channel status:", status, "— realtime may be disabled; mount-time fetch still keeps view consistent.");
        }
      });
  } catch(e) {
    console.warn("[subscribeShopVisits] failed to open channel:", e?.message || e);
    return () => {};
  }
  return () => {
    try { supabase.removeChannel(channel); }
    catch(e) { console.warn("[subscribeShopVisits] removeChannel failed:", e?.message || e); }
  };
}

// ─── Material orders (Phase C — migrated from SHARED_MATERIAL_ORDERS) ──────
// Supabase table `material_orders` stores one row per rep-placed or customer-
// placed marketing-material order. Schema from migration 0016. client_id
// carries the client-generated "MAT-XXXX" stable id for exact-key dedupe.
// source='rep'|'customer' distinguishes origin; RLS scopes writes by
// rep_id=auth.uid() or user_id=auth.uid() as appropriate.
//
// Removal is soft-delete via active=false; NEVER DELETE. Admin ship-status
// flip uses markMaterialOrderShipped (helper present; UI wiring is a future
// micro-PR per user's Option A scope decision).

/** List material orders. Pass { repId } for rep's own, { userId } for
 *  customer's own, or {} for admin's unfiltered view. Returns [] on any
 *  error so the caller can fall back to localStorage without throwing. */
export async function listMaterialOrders({ repId, userId } = {}) {
  try {
    let q = supabase
      .from('material_orders')
      .select('*')
      .eq('active', true)
      .order('placed_at', { ascending: false });
    if (repId) q = q.eq('rep_id', String(repId));
    if (userId) q = q.eq('user_id', String(userId));
    const { data, error } = await q;
    if (error) {
      console.warn("[listMaterialOrders] fetch failed:", error.message);
      return [];
    }
    return data || [];
  } catch(e) {
    console.warn("[listMaterialOrders] threw:", e?.message || e);
    return [];
  }
}

/** Idempotent insert of a single material_orders row. Awaited.
 *  Returns the inserted row on success, null on failure OR on a duplicate
 *  client_id (ignoreDuplicates:true means we never silently UPDATE an
 *  existing row — genuine edits would require a separate helper). Maps
 *  JS-side names (clientId, orderRef, accountName, repName, repId, userId,
 *  items, total, status, placedAt) to the DB columns. */
export async function upsertMaterialOrder({
  clientId, orderRef, source, accountName, repName, repId, userId,
  items, total, status, placedAt
}) {
  if (!clientId) {
    console.warn("[upsertMaterialOrder] missing clientId — refusing write (dedupe would be impossible)");
    return null;
  }
  if (!source || (source !== 'rep' && source !== 'customer')) {
    console.warn("[upsertMaterialOrder] invalid source — must be 'rep' or 'customer'");
    return null;
  }
  let placedAtIso;
  try {
    const d = placedAt ? new Date(placedAt) : new Date();
    placedAtIso = isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
  } catch { placedAtIso = new Date().toISOString(); }

  const payload = {
    client_id: String(clientId),
    order_ref: orderRef || String(clientId),
    source: String(source),
    account_name: String(accountName || ""),
    rep_name: repName || null,
    rep_id: repId ? String(repId) : null,
    user_id: userId ? String(userId) : null,
    items: Array.isArray(items) ? items : [],
    total: Number(total) || 0,
    status: status || 'pending',
    active: true,
    placed_at: placedAtIso,
    updated_at: new Date().toISOString(),
  };
  try {
    const { data, error } = await supabase
      .from('material_orders')
      .upsert(payload, { onConflict: 'client_id', ignoreDuplicates: true })
      .select()
      .maybeSingle();
    if (error) {
      console.warn("[upsertMaterialOrder] failed for client_id", clientId, ":", error.message);
      return null;
    }
    return data || null;
  } catch(e) {
    console.warn("[upsertMaterialOrder] threw for client_id", clientId, ":", e?.message || e);
    return null;
  }
}

/** UPDATE status='shipped' on a material_orders row. Scoped to exactly ONE
 *  row by the unique client_id (the stable "MAT-XXX" string that the client
 *  stamped at placeOrder time and that the UI has ready at hand). The
 *  non-partial unique index `material_orders_client_id_unique` from
 *  migration 0016 guarantees at most one match. No bulk edits.
 *
 *  Signature note (Phase C follow-up, mark-shipped UI): this filters by
 *  `client_id` rather than the bigserial `id` column because notifications
 *  carry only the client_id (see dbRowToOrder in App.js: `id: r.client_id
 *  || String(r.id)`). Filtering on the PK column wouldn't match a
 *  client-supplied client_id string. */
export async function markMaterialOrderShipped(clientId, shippedNotes) {
  if (!clientId) {
    console.warn("[markMaterialOrderShipped] missing clientId — refusing write");
    return null;
  }
  try {
    const { data, error } = await supabase
      .from('material_orders')
      .update({
        status: 'shipped',
        shipped_date: new Date().toISOString(),
        shipped_notes: shippedNotes || null,
        updated_at: new Date().toISOString(),
      })
      .eq('client_id', String(clientId))
      .select()
      .maybeSingle();
    if (error) {
      console.warn("[markMaterialOrderShipped] failed for client_id", clientId, ":", error.message);
      return null;
    }
    return data || null;
  } catch(e) {
    console.warn("[markMaterialOrderShipped] threw for client_id", clientId, ":", e?.message || e);
    return null;
  }
}

/** Soft-delete: flip active=false on a material_orders row by primary key.
 *  UPDATE only; never DELETE. Helper only — no UI action invokes it in
 *  Phase C. Reserved for a future cancellation flow. */
export async function deactivateMaterialOrder(id) {
  if (!id) {
    console.warn("[deactivateMaterialOrder] missing id — refusing write");
    return null;
  }
  try {
    const { data, error } = await supabase
      .from('material_orders')
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('id', String(id))
      .select()
      .maybeSingle();
    if (error) {
      console.warn("[deactivateMaterialOrder] failed for", id, ":", error.message);
      return null;
    }
    return data || null;
  } catch(e) {
    console.warn("[deactivateMaterialOrder] threw for", id, ":", e?.message || e);
    return null;
  }
}

/** Realtime subscription for material_orders. Pass { repId } for filtered
 *  rep view, { userId } for filtered customer view, or {} for admin's
 *  unfiltered view. Gracefully no-ops if realtime is disabled — mount-time
 *  listMaterialOrders still keeps the view consistent. */
export function subscribeMaterialOrders({ repId, userId } = {}, onChange) {
  if (typeof onChange !== 'function') return () => {};
  let channel;
  try {
    const chanName =
      repId  ? `material_orders_rep_${repId}` :
      userId ? `material_orders_user_${userId}` :
      'material_orders_admin';
    const filter =
      repId  ? `rep_id=eq.${String(repId)}` :
      userId ? `user_id=eq.${String(userId)}` :
      undefined;
    const config = { event: '*', schema: 'public', table: 'material_orders' };
    if (filter) config.filter = filter;
    channel = supabase
      .channel(chanName)
      .on('postgres_changes', config, payload => {
        try { onChange({ event: payload.eventType, row: payload.new || payload.old }); }
        catch(e) { console.warn("[subscribeMaterialOrders] callback threw:", e?.message || e); }
      })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribeMaterialOrders] channel status:", status, "— realtime may be disabled; mount-time fetch still keeps view consistent.");
        }
      });
  } catch(e) {
    console.warn("[subscribeMaterialOrders] failed to open channel:", e?.message || e);
    return () => {};
  }
  return () => {
    try { supabase.removeChannel(channel); }
    catch(e) { console.warn("[subscribeMaterialOrders] removeChannel failed:", e?.message || e); }
  };
}

// ─── Commission corrections (migration 0031) ─────────────────────────────────
// An invoice adjusted / voided AFTER its commission was marked Paid never
// rewrites rep_commission_payments: a signed correction line is recorded in
// rep_commission_corrections instead (pure math in commissionCorrections.js).
// Supabase-first and AWAITED; NO localStorage anywhere in this block: a
// correction that never reached Supabase must not exist. When 0031 has not been
// run, reads degrade to an empty list ({ ok:true, rows:[], missingTable:true })
// and writes throw a message naming the migration.

const CORR_TABLE = 'rep_commission_corrections';
const CORR_MISSING_MSG = "Commission corrections are not set up yet. Run migration 0031_rep_commission_corrections.sql in the Supabase SQL editor, then try again.";
const CORR_INSERT_COLS = ['client_id', 'rep_id', 'rep_name', 'invoice_id', 'invoice_ref', 'paid_batch_id', 'paid_amount', 'paid_rate', 'kind', 'old_total', 'new_total', 'old_commission', 'new_commission', 'delta', 'reason', 'source', 'created_by', 'created_by_email'];
const CORR_PAYMENT_COLS = 'id,invoice_id,rep_id,rep_name,batch_id,invoice_amount,commission_amount,commission_rate,amount,paid_at,method,notes';

function isMissingCorrectionsTable(err) {
  if (!err) return false;
  const code = String(err.code || "");
  if (code === "42P01" || code === "PGRST205") return true;
  const msg = String(err.message || err.details || err || "").toLowerCase();
  return msg.includes(CORR_TABLE) && (msg.includes("does not exist") || msg.includes("could not find the table") || msg.includes("schema cache"));
}

// Paged, error-reporting reader (same rules as listCommissionPaidInvoiceIds):
// offsets advance by the rows actually received, the exact count is compared,
// an empty page before the count or hitting the page cap is a failure, never a
// silently truncated list. makePage(from, to) returns the query for one page.
// Returns { ok:true, rows } | { ok:false, error, missingTable? }.
async function pagedReadAll(label, makePage, opts) {
  const PAGE = 1000;
  const MAX_PAGES = 200;
  const checkMissing = !!(opts && opts.checkMissing);
  try {
    const rows = [];
    let offset = 0;
    let total = null;
    for (let page = 0; page < MAX_PAGES; page++) {
      const { data, error, count } = await makePage(offset, offset + PAGE - 1);
      if (error) {
        if (checkMissing && ((opts && opts.isMissing) || isMissingCorrectionsTable)(error)) return { ok: false, missingTable: true, error: (opts && opts.missingMsg) || CORR_MISSING_MSG };
        console.warn("[" + label + "] SELECT failed on page", page, ":", error.message);
        return { ok: false, error: error.message || "query failed" };
      }
      const list = data || [];
      if (typeof count === 'number') total = count;
      rows.push(...list);
      offset += list.length;
      if (total != null) {
        if (offset >= total) return { ok: true, rows };
        if (list.length === 0) {
          console.warn("[" + label + "] server returned an empty page before the exact count (" + offset + "/" + total + ")");
          return { ok: false, error: "rows could not be read completely (" + offset + " of " + total + ")" };
        }
      } else if (list.length < PAGE) {
        return { ok: true, rows };
      }
    }
    console.warn("[" + label + "] page safety cap reached (" + MAX_PAGES + " pages); refusing to return a possibly truncated list.");
    return { ok: false, error: "too many rows to read safely (page cap reached)" };
  } catch (e) {
    console.warn("[" + label + "] threw:", e?.message || e);
    return { ok: false, error: e?.message || String(e) };
  }
}

/**
 * Every rep_commission_payments row (or one invoice's / one rep's / a list of
 * invoices'), paged, with the columns the correction math needs. Unlike
 * listRepCommissionPayments it REPORTS failure and is never capped at ~1000 rows.
 * invoiceIds: one .in(invoice_id, ids) query per chunk of 100 ids (URL length).
 * Returns { ok:true, rows } | { ok:false, error }.
 */
export async function listCommissionPaymentsPaged({ invoiceId, repId, invoiceIds } = {}) {
  const readOnce = (ids) => pagedReadAll("listCommissionPaymentsPaged", (from, to) => {
    let q = supabase.from('rep_commission_payments').select(CORR_PAYMENT_COLS, { count: 'exact' });
    if (invoiceId) q = q.eq('invoice_id', String(invoiceId));
    if (repId) q = q.eq('rep_id', String(repId));
    if (ids) q = q.in('invoice_id', ids);
    return q.order('id', { ascending: true }).range(from, to);
  });
  if (Array.isArray(invoiceIds)) {
    const all = Array.from(new Set(invoiceIds.map((x) => String(x)).filter(Boolean)));
    if (all.length === 0) return { ok: true, rows: [] };
    const rows = [];
    for (let i = 0; i < all.length; i += 100) {
      const r = await readOnce(all.slice(i, i + 100));
      if (!r.ok) return r;
      rows.push(...r.rows);
    }
    return { ok: true, rows };
  }
  return readOnce(null);
}

// Explicit column list for the REP-side reader: no created_by / created_by_email
// (admin identity) and no settled_by / settled_notes (internal waive notes).
// RLS is row-level only, so this narrows what the portal fetches, not what a rep
// could request by calling the API directly (see the PR limitations).
const CORR_REP_COLS = 'id,client_id,rep_id,rep_name,invoice_id,invoice_ref,paid_batch_id,paid_amount,paid_rate,kind,old_total,new_total,old_commission,new_commission,delta,reason,source,status,settled_batch_id,settled_at,settled_method,created_at,updated_at';

/**
 * Corrections, paged. Admin sees all; a rep sees only their own through RLS.
 * Optional filters: repId, invoiceId. repSafe:true (rep portal) selects the
 * explicit rep-safe column list instead of *. Returns { ok:true, rows } (rows is
 * [] with missingTable:true when 0031 has not been run) | { ok:false, error }.
 *
 * invoiceIds / batchIds (the Undo guard, which must NOT filter by rep id): the
 * union of two small .in reads, corrections whose invoice_id is in invoiceIds
 * and corrections whose settled_batch_id is in batchIds (chunks of 100), merged
 * and deduped by id. Any failing chunk fails the whole read.
 */
export async function listCorrections({ repId, invoiceId, repSafe, invoiceIds, batchIds } = {}) {
  if (Array.isArray(invoiceIds) || Array.isArray(batchIds)) {
    const uniq = (a) => Array.from(new Set((a || []).map((x) => String(x)).filter(Boolean)));
    const inv = uniq(invoiceIds); const bat = uniq(batchIds);
    const byId = new Map();
    const reads = [];
    for (let i = 0; i < inv.length; i += 100) reads.push(['invoice_id', inv.slice(i, i + 100)]);
    for (let i = 0; i < bat.length; i += 100) reads.push(['settled_batch_id', bat.slice(i, i + 100)]);
    for (const [col, chunk] of reads) {
      const r = await pagedReadAll("listCorrections", (from, to) => {
        let q = supabase.from(CORR_TABLE).select(repSafe ? CORR_REP_COLS : '*', { count: 'exact' });
        q = q.in(col, chunk);
        return q.order('id', { ascending: true }).range(from, to);
      }, { checkMissing: true });
      if (!r.ok && r.missingTable) {
        console.warn("[listCorrections] table missing (migration 0031 not run); returning an empty list");
        return { ok: true, rows: [], missingTable: true };
      }
      if (!r.ok) return r;
      r.rows.forEach((c) => { if (c && c.id !== undefined && c.id !== null) byId.set(String(c.id), c); });
    }
    return { ok: true, rows: Array.from(byId.values()).sort((a, b) => Number(a.id) - Number(b.id)) };
  }
  const res = await pagedReadAll("listCorrections", (from, to) => {
    let q = supabase.from(CORR_TABLE).select(repSafe ? CORR_REP_COLS : '*', { count: 'exact' });
    if (repId) q = q.eq('rep_id', String(repId));
    if (invoiceId) q = q.eq('invoice_id', String(invoiceId));
    return q.order('id', { ascending: true }).range(from, to);
  }, { checkMissing: true });
  if (!res.ok && res.missingTable) {
    console.warn("[listCorrections] table missing (migration 0031 not run); returning an empty list");
    return { ok: true, rows: [], missingTable: true };
  }
  return res;
}

/**
 * Insert one correction; a repeated client_id is a no-op and the WINNING row is
 * returned (upsert ignoreDuplicates, then re-read). Throws on any failure.
 * Returns { row, inserted }.
 */
export async function insertCorrectionIdempotent(row) {
  if (!row || !row.client_id) throw new Error("A correction needs a client_id.");
  const payload = {};
  CORR_INSERT_COLS.forEach((k) => { if (row[k] !== undefined) payload[k] = row[k]; });
  payload.status = 'open';
  let res;
  try {
    res = await supabase.from(CORR_TABLE).upsert(payload, { onConflict: 'client_id', ignoreDuplicates: true }).select();
  } catch (e) {
    console.warn("[insertCorrectionIdempotent] threw for client_id", row.client_id, ":", e?.message || e);
    throw e;
  }
  if (res.error) {
    console.warn("[insertCorrectionIdempotent] INSERT failed for client_id", row.client_id, ":", res.error.message);
    throw new Error(isMissingCorrectionsTable(res.error) ? CORR_MISSING_MSG : (res.error.message || "insert failed"));
  }
  if (Array.isArray(res.data) && res.data.length > 0) return { row: res.data[0], inserted: true };
  let won;
  try {
    won = await supabase.from(CORR_TABLE).select('*').eq('client_id', row.client_id).limit(1);
  } catch (e) {
    console.warn("[insertCorrectionIdempotent] re-read threw for client_id", row.client_id, ":", e?.message || e);
    throw e;
  }
  if (won.error) {
    console.warn("[insertCorrectionIdempotent] re-read failed for client_id", row.client_id, ":", won.error.message);
    throw new Error(won.error.message || "re-read failed");
  }
  if (!won.data || won.data.length === 0) {
    console.warn("[insertCorrectionIdempotent] no row after a no-op insert for client_id", row.client_id);
    throw new Error("The correction could not be confirmed after saving. Please refresh and check.");
  }
  return { row: won.data[0], inserted: false };
}

/**
 * Release a claim: settled -> open for the given ids, only while they are still
 * settled under this batch (the DB guard also refuses when payment rows carry
 * the batch). Never throws. Returns { ok, ids, error? } with the ids released.
 */
export async function releaseCorrectionClaim(ids, batchId) {
  const list = Array.from(new Set((ids || []).filter((x) => x != null)));
  if (list.length === 0) return { ok: true, ids: [] };
  if (!batchId) return { ok: false, ids: [], error: "no batchId" };
  try {
    const { data, error } = await supabase.from(CORR_TABLE)
      .update({ status: 'open', settled_batch_id: null, settled_at: null, settled_method: null, settled_notes: null, settled_by: null })
      .in('id', list).eq('status', 'settled').eq('settled_batch_id', batchId).select('id');
    if (error) {
      console.warn("[releaseCorrectionClaim] UPDATE failed for batch", batchId, ":", error.message);
      return { ok: false, ids: [], error: error.message };
    }
    const done = (data || []).map((r) => r.id);
    const complete = done.length === list.length;
    if (!complete) console.warn("[releaseCorrectionClaim] released only", done.length, "of", list.length, "for batch", batchId);
    return { ok: complete, ids: done, error: complete ? undefined : "only " + done.length + " of " + list.length + " claims could be released" };
  } catch (e) {
    console.warn("[releaseCorrectionClaim] threw for batch", batchId, ":", e?.message || e);
    return { ok: false, ids: [], error: e?.message || String(e) };
  }
}

/**
 * Claim open corrections for a payout batch with ONE compare-and-set UPDATE
 * (status open -> settled WHERE id IN ids AND status = open, RETURNING). Every
 * id must come back; otherwise the ones this call claimed are released and this
 * throws (someone else settled or waived one). batchInfo: { batchId, method,
 * notes, by, at?, repId? }. Returns { ok:true, rows }.
 */
export async function claimCorrectionsForBatch(ids, batchInfo = {}) {
  const list = Array.from(new Set((ids || []).filter((x) => x != null)));
  if (list.length === 0) return { ok: true, rows: [] };
  const { batchId } = batchInfo;
  if (!batchId) throw new Error("A settlement batch id is required.");
  let res;
  try {
    let q = supabase.from(CORR_TABLE).update({
      status: 'settled',
      settled_batch_id: batchId,
      settled_at: batchInfo.at || new Date().toISOString(),
      settled_method: batchInfo.method || null,
      settled_notes: batchInfo.notes || null,
      settled_by: batchInfo.by || null,
    }).in('id', list).eq('status', 'open');
    if (batchInfo.repId) q = q.eq('rep_id', String(batchInfo.repId));
    res = await q.select();
  } catch (e) {
    console.warn("[claimCorrectionsForBatch] threw for batch", batchId, ":", e?.message || e);
    throw e;
  }
  if (res.error) {
    console.warn("[claimCorrectionsForBatch] UPDATE failed for batch", batchId, ":", res.error.message);
    throw new Error(isMissingCorrectionsTable(res.error) ? CORR_MISSING_MSG : (res.error.message || "claim failed"));
  }
  const claimed = res.data || [];
  const got = new Set(claimed.map((r) => String(r.id)));
  const missing = list.filter((id) => !got.has(String(id)));
  if (missing.length > 0) {
    console.warn("[claimCorrectionsForBatch] compare-and-set lost for ids", missing, "; releasing the", claimed.length, "claimed");
    const rel = await releaseCorrectionClaim(claimed.map((r) => r.id), batchId);
    if (!rel.ok) {
      throw new Error("Some corrections were already settled by someone else, and the corrections this attempt claimed could not all be released (" + (rel.error || "unknown") + "). Refresh the Commission tab and check the corrections before paying.");
    }
    throw new Error("Some corrections were already settled or waived by someone else. Nothing was changed: refresh the Commission tab and try again.");
  }
  return { ok: true, rows: claimed };
}

/**
 * Waive an OPEN correction with a mandatory note (for example a negative
 * correction owed by a rep who will never be paid again). Compare-and-set on
 * status = open; throws when the note is empty or the correction is no longer
 * open. Returns { ok:true, row }.
 */
export async function waiveCorrection(id, note, actor) {
  const text = String(note || "").trim();
  if (id == null) throw new Error("A correction id is required.");
  if (!text) throw new Error("A note is required to waive a correction.");
  let res;
  try {
    res = await supabase.from(CORR_TABLE)
      .update({ status: 'waived', settled_at: new Date().toISOString(), settled_notes: text, settled_by: actor || null })
      .eq('id', id).eq('status', 'open').select();
  } catch (e) {
    console.warn("[waiveCorrection] threw for id", id, ":", e?.message || e);
    throw e;
  }
  if (res.error) {
    console.warn("[waiveCorrection] UPDATE failed for id", id, ":", res.error.message);
    throw new Error(isMissingCorrectionsTable(res.error) ? CORR_MISSING_MSG : (res.error.message || "waive failed"));
  }
  if (!res.data || res.data.length === 0) {
    console.warn("[waiveCorrection] no open row matched for id", id);
    throw new Error("This correction is no longer open (already settled or waived). Refresh and check.");
  }
  return { ok: true, row: res.data[0] };
}

/**
 * Record the correction for one invoice that was adjusted or voided (call this
 * AFTER the invoice change itself succeeded, never before). Reads the invoice's
 * paid rows and corrections FRESH from Supabase, applies the pure formula and
 * inserts. A client_id ordinal collision (two tabs) recomputes against the
 * winner and retries. Never throws. Returns
 *   { ok:true, inserted: row }   a correction was recorded
 *   { ok:true, none:true, code } nothing to record (never paid / already accounted)
 *   { ok:false, refused:true, code, reason }  needs a human
 *   { ok:false, changed:true, freshDelta, expectedDelta } expectedDelta was given
 *       and the freshly computed delta differs by more than 0.005: NOTHING is
 *       inserted (the admin approved a different amount)
 *   { ok:false, error, stage, missingTable? } a read or write failed (logged)
 * admin: { id, email }. expectedDelta (optional): the delta the admin approved.
 */
export async function recordCorrectionForInvoice({ invoiceId, newTotal, isVoid, reason, source, admin, legacyPaid, expectedDelta } = {}) {
  const why = String(reason || "").trim();
  if (!invoiceId) return { ok: false, error: "no invoiceId", stage: "input" };
  if (!why) return { ok: false, error: "A reason is required for a commission correction.", stage: "input" };
  const MAX_ATTEMPTS = 5;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    const paid = await listCommissionPaymentsPaged({ invoiceId });
    if (!paid.ok) {
      console.warn("[recordCorrectionForInvoice] paid-row read failed for", invoiceId, ":", paid.error);
      return { ok: false, error: paid.error, stage: "read_payments" };
    }
    const existing = await listCorrections({ invoiceId });
    if (!existing.ok) {
      console.warn("[recordCorrectionForInvoice] corrections read failed for", invoiceId, ":", existing.error);
      return { ok: false, error: existing.error, stage: "read_corrections" };
    }
    if (existing.missingTable && paid.rows.length > 0) {
      return { ok: false, error: CORR_MISSING_MSG, stage: "read_corrections", missingTable: true };
    }
    const res = computeCorrection({ paidRows: paid.rows, corrections: existing.rows, newTotal, isVoid: !!isVoid, legacyPaid: !!legacyPaid });
    if (!res.ok) {
      console.warn("[recordCorrectionForInvoice] refused for", invoiceId, ":", res.code);
      return { ok: false, refused: true, code: res.code, reason: res.reason };
    }
    if (res.none) return { ok: true, none: true, code: res.code };
    if (expectedDelta !== undefined && expectedDelta !== null && Math.abs(Number(res.delta) - Number(expectedDelta)) > 0.005) {
      console.warn("[recordCorrectionForInvoice] fresh delta", res.delta, "differs from the approved", expectedDelta, "for", invoiceId, "; nothing inserted");
      return { ok: false, changed: true, freshDelta: res.delta, expectedDelta: Number(expectedDelta), fresh: { newTotal: res.new_total, isVoid: res.kind === "void" } };
    }
    try {
      const { row, inserted } = await insertCorrectionIdempotent({
        ...res, reason: why, source: source || null,
        created_by: admin && admin.id != null ? String(admin.id) : null,
        created_by_email: (admin && admin.email) || null,
      });
      if (inserted) return { ok: true, inserted: row };
      console.warn("[recordCorrectionForInvoice] client_id collision for", invoiceId, "attempt", attempt, "; recomputing against the winner");
    } catch (e) {
      console.warn("[recordCorrectionForInvoice] insert failed for", invoiceId, ":", e?.message || e);
      return { ok: false, error: e?.message || String(e), stage: "insert", missingTable: e?.message === CORR_MISSING_MSG };
    }
  }
  console.warn("[recordCorrectionForInvoice] gave up after", MAX_ATTEMPTS, "collisions for", invoiceId);
  return { ok: false, error: "The correction kept colliding with another admin's change. Refresh and try again.", stage: "insert" };
}

/**
 * Fresh read of an invoice's CURRENT state in portal_orders for the reconcile
 * scan (never the SHARED_INVOICES cache, which can lag). Newest row by
 * invoice_ref, void = any row cancelled (same rule as the order status read at
 * the Orders tab). Never throws. Returns
 *   { ok:true, found:false } | { ok:true, found:true, total, cancelled, status } | { ok:false, error }.
 */
export async function getInvoiceStateForCorrection(invoiceRef) {
  if (!invoiceRef) return { ok: false, error: "no invoiceRef" };
  try {
    const rows = await fetchOrderRowsForInvoice(String(invoiceRef));
    if (!rows || rows.length === 0) return { ok: true, found: false };
    const cancelled = rows.some((r) => r.status === 'cancelled');
    return { ok: true, found: true, total: Number(rows[0].total), cancelled, status: rows[0].status || null };
  } catch (e) {
    console.warn("[getInvoiceStateForCorrection] failed for", invoiceRef, ":", e?.message || e);
    return { ok: false, error: e?.message || String(e) };
  }
}

/**
 * Realtime subscription on rep_commission_corrections (RLS filters the stream:
 * a rep receives only their own rows). Per-call random channel name (#85
 * collision lesson). Returns an unsubscribe function.
 */
export function subscribeRepCommissionCorrections(onChange) {
  if (typeof onChange !== 'function') return () => {};
  let channel;
  try {
    const channelName = 'rep_commission_corrections_realtime_' + Math.random().toString(36).slice(2, 10);
    channel = supabase
      .channel(channelName)
      .on('postgres_changes',
          { event: '*', schema: 'public', table: CORR_TABLE },
          payload => {
            try { onChange(payload); }
            catch (e) { console.warn("[subscribeRepCommissionCorrections] handler threw:", e?.message || e); }
          })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribeRepCommissionCorrections] channel status:", status, "; realtime unavailable.");
        }
      });
  } catch (e) {
    console.warn("[subscribeRepCommissionCorrections] setup failed:", e?.message || e);
    return () => {};
  }
  return () => {
    try { if (channel) supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribeRepCommissionCorrections] teardown:", e?.message || e); }
  };
}

/**
 * STRICT read of the PENDING rep requests (adjustment + void) for one invoice.
 * Unlike listAdjustmentRequests / listInvoiceVoidRequests this NEVER falls back
 * to a localStorage cache and never swallows an error: the caller (admin
 * Delete / Adjust guard) must fail closed when the answer is not certain.
 * A missing request table (migration not run) counts as "no requests".
 * Returns { ok:true, adjust:[rows], void:[rows] } or { ok:false, error }.
 */
export async function listPendingRequestsForInvoiceStrict(invoiceId) {
  if (!invoiceId) return { ok: false, error: "no invoiceId" };
  try {
    const [a, v] = await Promise.all([
      supabase.from('invoice_adjustment_requests').select('id,invoice_id,status,rep_name').eq('invoice_id', String(invoiceId)).eq('status', 'pending'),
      supabase.from('invoice_void_requests').select('id,invoice_id,status,rep_name').eq('invoice_id', String(invoiceId)).eq('status', 'pending'),
    ]);
    const pick = (res, label) => {
      if (res.error) {
        if (isMissingTableError(res.error)) return [];
        console.warn("[listPendingRequestsForInvoiceStrict]", label, "read failed for", invoiceId, ":", res.error.message);
        throw new Error(res.error.message || (label + " read failed"));
      }
      return Array.isArray(res.data) ? res.data : [];
    };
    return { ok: true, adjust: pick(a, "adjustment requests"), void: pick(v, "void requests") };
  } catch (e) {
    console.warn("[listPendingRequestsForInvoiceStrict] failed for", invoiceId, ":", e?.message || e);
    return { ok: false, error: e?.message || String(e) };
  }
}

/**
 * Rep-side "something changed on portal_orders" SIGNAL. The callback only
 * learns that an event happened: callers must re-fetch (syncPortalOrders) and
 * NEVER apply the event row itself. Uses a per-call random channel name (PR #85
 * lesson: a fixed name is shared by every subscriber in the tab and a teardown
 * removes the other subscribers' channel), separate from the fixed name used by
 * subscribePortalOrders. Returns an unsubscribe function.
 */
export function subscribePortalOrdersSignal(onSignal) {
  if (typeof onSignal !== 'function') return () => {};
  const channelName = 'portal_orders_signal_' + Math.random().toString(36).slice(2, 10);
  let channel;
  try {
    channel = supabase
      .channel(channelName)
      .on('postgres_changes',
          { event: '*', schema: 'public', table: 'portal_orders' },
          () => {
            try { onSignal(); }
            catch (e) { console.warn("[subscribePortalOrdersSignal] handler threw:", e?.message || e); }
          })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribePortalOrdersSignal] channel status:", status, "- realtime unavailable; the 60s poll and focus resync still apply.");
        }
      });
  } catch (e) {
    console.warn("[subscribePortalOrdersSignal] setup failed:", e?.message || e);
    return () => {};
  }
  return () => {
    try { if (channel) supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribePortalOrdersSignal] teardown:", e?.message || e); }
  };
}

// ─── Rep remittances (migration 0033) ────────────────────────────────────────
// "The sales rep handed us the money for this invoice." Supabase is the ONLY
// store of the record: no helper below reads or writes localStorage. Every
// failure is logged with the function name. See src/repRemittances.js for the
// pure rules (validation, eligibility, amount).

const REMIT_TABLE = 'invoice_rep_remittances';
const REMIT_MISSING_MSG = "Rep payment tracking is not set up yet. Run migration 0033_invoice_rep_remittances.sql in the Supabase SQL editor, then try again.";
const REMIT_ALREADY_MSG = "Payment from the sales rep was already recorded for this invoice. Refresh to see it.";

function isMissingRemittanceTable(err) {
  if (!err) return false;
  const code = String(err.code || "");
  if (code === "42P01" || code === "PGRST205") return true;
  const msg = String(err.message || err.details || err || "").toLowerCase();
  return msg.includes(REMIT_TABLE) && (msg.includes("does not exist") || msg.includes("could not find the table") || msg.includes("schema cache"));
}

/**
 * Remittances, paged with an exact count. Admin sees all; a rep only their own
 * (RLS). Filters: invoiceIds (chunks of 100), repId, status.
 * Returns { ok:true, rows } | { ok:true, rows:[], missingTable:true } when 0033
 * has not been run | { ok:false, error }.
 */
export async function listRemittances({ invoiceIds, repId, status } = {}) {
  const readOnce = (ids) => pagedReadAll("listRemittances", (from, to) => {
    let q = supabase.from(REMIT_TABLE).select('*', { count: 'exact' });
    if (repId) q = q.eq('rep_id', String(repId));
    if (status) q = q.eq('status', String(status));
    if (ids) q = q.in('invoice_id', ids);
    return q.order('id', { ascending: true }).range(from, to);
  }, { checkMissing: true, isMissing: isMissingRemittanceTable, missingMsg: REMIT_MISSING_MSG });
  const finish = (res) => {
    if (!res.ok && res.missingTable) {
      console.warn("[listRemittances] table missing (migration 0033 not run); returning an empty list");
      return { ok: true, rows: [], missingTable: true };
    }
    return res;
  };
  if (Array.isArray(invoiceIds)) {
    const all = Array.from(new Set(invoiceIds.map((x) => String(x)).filter(Boolean)));
    if (all.length === 0) return { ok: true, rows: [] };
    const rows = [];
    for (let i = 0; i < all.length; i += 100) {
      const r = finish(await readOnce(all.slice(i, i + 100)));
      if (!r.ok || r.missingTable) return r;
      rows.push(...r.rows);
    }
    return { ok: true, rows };
  }
  return finish(await readOnce(null));
}

/**
 * The signed-in REP's own remittances for the rep portal. Explicit column list
 * (REP_REMIT_COLUMNS: no marked_by_id, note or reversed_* internals); RLS scopes
 * the rows to the rep anyway. Degrades silently when 0033 is missing.
 * Returns { ok:true, rows, missingTable? } | { ok:false, error }.
 */
export async function listMyRemittances(repId) {
  if (!repId) return { ok: true, rows: [] };
  const res = await pagedReadAll("listMyRemittances", (from, to) =>
    supabase.from(REMIT_TABLE).select(REP_REMIT_COLUMNS, { count: 'exact' })
      .eq('rep_id', String(repId)).order('id', { ascending: true }).range(from, to),
    { checkMissing: true, isMissing: isMissingRemittanceTable, missingMsg: REMIT_MISSING_MSG });
  if (!res.ok && res.missingTable) {
    console.warn("[listMyRemittances] table missing (migration 0033 not run); the rep sees no remittances");
    return { ok: true, rows: [], missingTable: true };
  }
  return res;
}

/**
 * Everything the admin invoice modal needs for ONE invoice, read fresh: the
 * newest portal_orders rows (amount + eligibility) and this invoice's remittance
 * history. Never throws. Returns
 *   { ok:true, remittance: active|null, history: rows, amount: number|null,
 *     eligibility: {ok, reason}, missingTable? } | { ok:false, error }.
 */
export async function loadRemittanceContext(invoiceId, invoice) {
  const id = String(invoiceId || "").trim();
  if (!id) return { ok: false, error: "no invoiceId" };
  let rows;
  try { rows = await fetchOrderRowsForInvoice(id); }
  catch (e) {
    console.warn("[loadRemittanceContext] order read failed for", id, ":", e?.message || e);
    return { ok: false, error: "The invoice could not be read from the database (" + (e?.message || e) + ")." };
  }
  const list = await listRemittances({ invoiceIds: [id] });
  if (!list.ok) return { ok: false, error: list.error };
  if (list.missingTable) return { ok: true, missingTable: true, remittance: null, history: [], amount: null, eligibility: { ok: false, reason: REMIT_MISSING_MSG } };
  const active = activeRemittanceFor(list.rows, id);
  const base = rows[0] || null;
  const eligibility = remittanceEligibility({
    invoice: { id, status: invoice && invoice.status, repCreated: isRepCreatedInvoice(invoice || { id }) || (!!base && base.source === 'rep_invoice') },
    freshOrderRows: rows,
    activeRemittance: active,
  });
  return { ok: true, remittance: active, history: list.rows, amount: remittanceAmount(rows), eligibility };
}

/**
 * STRICT read for guards (Adjust / Delete): the ACTIVE remittance of one
 * invoice. FAILS CLOSED: any read error is { ok:false, error } and the caller
 * must refuse; only a missing table (0033 not run) counts as "none".
 * Returns { ok:true, remittance: row|null, missingTable? } | { ok:false, error }.
 */
export async function getActiveRemittanceForInvoiceStrict(invoiceId) {
  if (!invoiceId) return { ok: false, error: "no invoiceId" };
  try {
    const { data, error } = await supabase.from(REMIT_TABLE).select('*')
      .eq('invoice_id', String(invoiceId)).eq('status', 'received');
    if (error) {
      if (isMissingRemittanceTable(error)) return { ok: true, remittance: null, missingTable: true };
      console.warn("[getActiveRemittanceForInvoiceStrict] read failed for", invoiceId, ":", error.message);
      return { ok: false, error: error.message || "remittance read failed" };
    }
    return { ok: true, remittance: activeRemittanceFor(Array.isArray(data) ? data : [], invoiceId) };
  } catch (e) {
    console.warn("[getActiveRemittanceForInvoiceStrict] threw for", invoiceId, ":", e?.message || e);
    return { ok: false, error: e?.message || String(e) };
  }
}

/**
 * STRICT read used by the Reverse action: is commission for this invoice already
 * marked Paid? Reads rep_commission_payments (paged reader) and the corrections
 * ledger for the invoice, fresh. FAILS CLOSED (a failed read is { ok:false }; the
 * caller must not reverse blind). Read-only: writes nothing.
 * Returns { ok:true, paid, rowCount, correctionCount } | { ok:false, error }.
 */
export async function getCommissionPaidStateForInvoice(invoiceId) {
  const id = String(invoiceId || "").trim();
  if (!id) return { ok: false, error: "no invoiceId" };
  try {
    const [paid, corr] = await Promise.all([
      listCommissionPaymentsPaged({ invoiceId: id }),
      listCorrections({ invoiceId: id }),
    ]);
    if (!paid.ok) { console.warn("[getCommissionPaidStateForInvoice] payments read failed for", id, ":", paid.error); return { ok: false, error: "the commission payout records could not be read: " + paid.error }; }
    if (!corr.ok) { console.warn("[getCommissionPaidStateForInvoice] corrections read failed for", id, ":", corr.error); return { ok: false, error: "the commission corrections could not be read: " + corr.error }; }
    const rowCount = (paid.rows || []).length;
    return { ok: true, paid: rowCount > 0, rowCount, correctionCount: (corr.rows || []).length };
  } catch (e) {
    console.warn("[getCommissionPaidStateForInvoice] threw for", id, ":", e?.message || e);
    return { ok: false, error: e?.message || String(e) };
  }
}

/**
 * Record "received from the sales rep" for ONE invoice. Order: validate input,
 * FRESH portal_orders rows (must exist, not cancelled), FRESH remittance read
 * (no active one), resolve the rep uuid (unambiguous or null + warning), amount
 * = the freshly read newest row total, one plain INSERT with a stable client_id.
 * The partial unique index makes a concurrent second insert fail (23505).
 * repIdHint (optional): the cached invoice's repId stamp, honoured only when it
 * is a live rep profile. admin: { id, email }. Never throws. Returns
 *   { ok:true, row, warnings:[] }
 *   { ok:false, alreadyReceived:true, remittance, error }
 *   { ok:false, error, stage, missingTable? }
 */
export async function createRemittance({ invoiceId, method, reference, receivedOn, note, admin, repIdHint } = {}) {
  const fail = (error, stage, extra) => {
    console.warn("[createRemittance] " + stage + " failed for", invoiceId, ":", error);
    return { ok: false, error, stage, ...(extra || {}) };
  };
  const invId = String(invoiceId || "").trim();
  if (!invId) return fail("no invoiceId", "input");
  const v = validateRemittanceInput({ method, reference, receivedOn, note });
  if (!v.ok) return fail(Object.values(v.errors)[0], "input", { errors: v.errors });

  let rows;
  try { rows = await fetchOrderRowsForInvoice(invId); }
  catch (e) { return fail("The invoice could not be read from the database (" + (e?.message || e) + "). Nothing was recorded.", "read_order"); }

  const existing = await listRemittances({ invoiceIds: [invId] });
  if (!existing.ok) return fail("The existing remittances could not be read (" + existing.error + "). Nothing was recorded.", "read_remittances");
  if (existing.missingTable) return fail(REMIT_MISSING_MSG, "read_remittances", { missingTable: true });
  const active = activeRemittanceFor(existing.rows, invId);
  if (active) {
    console.warn("[createRemittance] invoice", invId, "already has an active remittance", active.id);
    return { ok: false, alreadyReceived: true, remittance: active, error: REMIT_ALREADY_MSG, stage: "already_received" };
  }

  const base = rows[0] || null;
  const elig = remittanceEligibility({
    invoice: { id: invId, repCreated: !!base && (base.source === 'rep_invoice' || isRepCreatedInvoice({ id: invId })) },
    freshOrderRows: rows,
    activeRemittance: null,
  });
  if (!elig.ok) return fail(elig.reason, "eligibility");
  const amount = remittanceAmount(rows);
  if (amount === null) return fail("The invoice total could not be read from the database. Nothing was recorded.", "amount");

  const warnings = [];
  let repId = null;
  let repName = String(base.rep || "").trim() || null;
  try {
    const profiles = await getAllProfiles();
    const who = resolveRepIdentity({ invoice: repIdHint ? { repId: repIdHint } : {}, dbRow: base, profiles });
    if (who.ok) { repId = who.rep.id; repName = repName || who.rep.name || null; }
    else {
      warnings.push("The rep could not be identified (" + who.reason + "), so the rep will not see this in their own account.");
      console.warn("[createRemittance] rep unresolved for", invId, ":", who.code);
    }
  } catch (e) {
    warnings.push("The rep accounts could not be read, so the rep will not see this in their own account.");
    console.warn("[createRemittance] profiles read failed for", invId, ":", e?.message || e);
  }

  const ordinal = existing.rows.length + 1;
  const payload = {
    client_id: "remit|" + invId + "|n" + ordinal,
    invoice_id: invId,
    invoice_db_id: base.id ?? null,
    rep_id: repId,
    rep_name: repName,
    customer: base.customer || null,
    amount,
    method: v.value.method,
    reference: v.value.reference || null,
    received_on: v.value.receivedOn,
    note: v.value.note || null,
    marked_by: (admin && admin.email) || null,
    marked_by_id: admin && admin.id != null ? String(admin.id) : null,
  };
  let res;
  try { res = await supabase.from(REMIT_TABLE).insert(payload).select().single(); }
  catch (e) { return fail(e?.message || String(e), "insert"); }
  if (res.error) {
    if (isMissingRemittanceTable(res.error)) return fail(REMIT_MISSING_MSG, "insert", { missingTable: true });
    if (String(res.error.code || "") === "23505") {
      const again = await getActiveRemittanceForInvoiceStrict(invId);
      if (again.ok && again.remittance) {
        console.warn("[createRemittance] 23505 for", invId, "; the winning active remittance is", again.remittance.id);
        return { ok: false, alreadyReceived: true, remittance: again.remittance, error: REMIT_ALREADY_MSG, stage: "already_received" };
      }
      return fail("Another admin recorded a payment for this invoice at the same moment. Refresh and check.", "insert");
    }
    return fail(res.error.message || "insert failed", "insert");
  }
  if (!res.data) return fail("The remittance could not be confirmed after saving. Please refresh and check.", "insert");
  return { ok: true, row: res.data, warnings };
}

/**
 * Reverse a mistaken remittance: ONE compare-and-set UPDATE (status received ->
 * reversed WHERE id = ? AND status = received, RETURNING). Nothing is deleted.
 * reason is required. admin: { id, email }. Never throws. Returns
 *   { ok:true, row } | { ok:false, alreadyReversed:true, row, error } |
 *   { ok:false, error, stage, missingTable? }
 */
export async function reverseRemittance(id, { reason, admin } = {}) {
  const fail = (error, stage, extra) => {
    console.warn("[reverseRemittance] " + stage + " failed for id", id, ":", error);
    return { ok: false, error, stage, ...(extra || {}) };
  };
  const why = String(reason || "").trim();
  if (id === undefined || id === null || id === "") return fail("A remittance id is required.", "input");
  if (!why) return fail("A reason is required to reverse a payment received from the rep.", "input");
  let res;
  try {
    res = await supabase.from(REMIT_TABLE)
      .update({
        status: 'reversed',
        reversed_reason: why,
        reversed_by: (admin && (admin.email || (admin.id != null ? String(admin.id) : ""))) || null,
        reversed_at: new Date().toISOString(),
      })
      .eq('id', id).eq('status', 'received').select();
  } catch (e) { return fail(e?.message || String(e), "update"); }
  if (res.error) {
    if (isMissingRemittanceTable(res.error)) return fail(REMIT_MISSING_MSG, "update", { missingTable: true });
    return fail(res.error.message || "reverse failed", "update");
  }
  if (Array.isArray(res.data) && res.data.length > 0) return { ok: true, row: res.data[0] };
  let cur;
  try { cur = await supabase.from(REMIT_TABLE).select('*').eq('id', id).limit(1); }
  catch (e) { return fail(e?.message || String(e), "reread"); }
  if (cur.error) return fail(cur.error.message || "re-read failed", "reread");
  const row = (cur.data || [])[0];
  if (!row) return fail("This remittance no longer exists. Refresh and check.", "not_found");
  if (row.status === 'reversed') {
    console.warn("[reverseRemittance] id", id, "was already reversed");
    return { ok: false, alreadyReversed: true, row, error: "This payment was already reversed. Refresh to see it.", stage: "already_reversed" };
  }
  return fail("The remittance could not be reversed (status is " + row.status + "). Refresh and check.", "update");
}

/**
 * Realtime subscription on invoice_rep_remittances (RLS filters the stream: a
 * rep receives only their own rows). Per-call random channel name (#85 lesson).
 * The handler only learns that something changed: re-fetch, never apply the
 * event row. Returns an unsubscribe function.
 */
export function subscribeRemittances(handler) {
  if (typeof handler !== 'function') return () => {};
  let channel;
  try {
    const channelName = 'invoice_rep_remittances_realtime_' + Math.random().toString(36).slice(2, 10);
    channel = supabase
      .channel(channelName)
      .on('postgres_changes',
          { event: '*', schema: 'public', table: REMIT_TABLE },
          payload => {
            try { handler(payload); }
            catch (e) { console.warn("[subscribeRemittances] handler threw:", e?.message || e); }
          })
      .subscribe(status => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn("[subscribeRemittances] channel status:", status, "; realtime unavailable.");
        }
      });
  } catch (e) {
    console.warn("[subscribeRemittances] setup failed:", e?.message || e);
    return () => {};
  }
  return () => {
    try { if (channel) supabase.removeChannel(channel); }
    catch (e) { console.warn("[subscribeRemittances] teardown:", e?.message || e); }
  };
}
